import { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import FactoryRoundedIcon from '@mui/icons-material/FactoryRounded';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import PauseCircleRoundedIcon from '@mui/icons-material/PauseCircleRounded';
import PlayCircleRoundedIcon from '@mui/icons-material/PlayCircleRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import ArchiveRoundedIcon from '@mui/icons-material/ArchiveRounded';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  getIndustrialCostProducts,
  getIndustrialCostProductionOperationSettingsConfig,
  getIndustrialCostProductionReportDetail,
  getIndustrialCostProductionReportFactoryControl,
  getIndustrialCostProductionReportFactorySummary,
  getIndustrialCostProductionPerformanceProducts,
  getIndustrialCostProductionReportReviewBoard,
  getIndustrialCostProductionReportReviewBoardSummary,
  listIndustrialCostProductionLinesConfig,
  listIndustrialCostProductionReportStopReasonsConfig,
  analyzeIndustrialCostProductionPerformance,
  validateIndustrialCostProductionReport,
  updateIndustrialCostProductionReportLine,
  updateIndustrialCostProductionReportStop,
  createIndustrialCostProductionReportStop,
  deleteIndustrialCostProductionReport,
  getIndustrialCostLineRecentHistory,
  exportIndustrialCostProductionReportLines,
  exportIndustrialCostProductionReportStops,
  archiveIndustrialCostPlantNotice,
  createIndustrialCostPlantNotice,
  listIndustrialCostPlantNotices,
  updateIndustrialCostPlantNotice,
} from '../services/industrialCostService';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { es } from 'date-fns/locale';
import { format, parseISO } from 'date-fns';
import IndustrialCostWeeklyActivityView from './IndustrialCostWeeklyActivityView';
import IndustrialCostProductionIntelligenceView from './IndustrialCostProductionIntelligenceView';
import type {
  CreatePlantNoticePayload,
  CreateProductionReportLinePayload,
  CreateProductionReportStopPayload,
  PlantNotice,
  PlantNoticePriority,
  Product,
  ProductionLineConfig,
  ProductionOperationSettings,
  ProductionReportDetail,
  ProductionReportFactoryControlItem,
  ProductionReportFactoryControlStatus,
  ProductionReportFactorySummaryLine,
  ProductionReportFactorySummaryTotals,
  ProductionReportPerformanceProductItem,
  ProductionReportLine,
  ProductionReportReviewBoardItem,
  ProductionReportReviewStatus,
  ProductionReportStop,
  ProductionReportStopReason,
} from '../types/industrialCost';

import { esNumberFormat } from '../utils/numberFormat';
type ControlLineEstimationResult = {
  enabled: boolean;
  estimatedDurationMinutes: number | null;
  estimatedEndTime: string;
  formulaLabel: string;
  missingInputs: string[];
  usesSetupTime: boolean;
};

type ControlLineTimingDeviation = {
  label: string;
  valueLabel: string;
  isOvertime: boolean;
};

type PerformancePeriodPreset = 'today' | 'week' | 'month' | 'year' | 'custom';

type PerformanceView = 'summary' | 'shifts' | 'operators' | 'products';

type ProductPerformanceSort =
  | 'reference'
  | 'activity'
  | 'quantity'
  | 'production'
  | 'deviation'
  | 'setup'
  | 'stops'
  | 'coverage';

type ProductPerformanceSortDirection = 'asc' | 'desc';

type ProductPerformanceDetailMode = 'orders' | 'lines';

type HistoryMode = 'review' | 'by_date';

type ReviewBoardFilterMode =
  | 'unvalidated'
  | 'all'
  | 'pending_revalidation'
  | 'validated'
  | 'exported';

type ControlTab = 'current' | 'weekly' | 'history' | 'performance' | 'intelligence' | 'export';

type ProductionReportExportKind = 'lines' | 'stops';

type IndustrialCostControlPermission =
  | 'factory_control:view'
  | 'production_history:view'
  | 'production_performance:view'
  | 'production_planning:manage';

type IndustrialCostProductionReportsControlViewProps = {
  permissions?: IndustrialCostControlPermission[];
};

type LineGroup = {
  key: string;
  label: string;
  configuredLine: ProductionLineConfig | null;
  items: ProductionReportFactoryControlItem[];
  representative: ProductionReportFactoryControlItem;
  current_status: ProductionReportFactoryControlStatus;
  status_label: string;
  has_configured_line_without_report: boolean;
  warnings: string[];
  totals: {
    reports_count: number;
    production_lines_count: number;
    closed_lines_count: number;
    stops_count: number;
    open_stops_count: number;
    quality_c_count: number;
    quality_nc_count: number;
    quality_na_count: number;
  };
};

type ProcessLineGroup = {
  key: string;
  label: string;
  groups: LineGroup[];
  firstDisplayOrder: number;
  tone: {
    accent: string;
    border: string;
    bg: string;
  };
  totals: {
    total: number;
    producing: number;
    stopped: number;
    idle: number;
    validated: number;
    exported: number;
    nc: number;
  };
};

type ProcessPerformanceGroup = {
  key: string;
  label: string;
  tone: {
    accent: string;
    border: string;
    bg: string;
  };
  firstDisplayOrder: number;
  lines: ProductionReportFactorySummaryLine[];
  totals: {
    reports_count: number;
    closed_orders_count: number;

    total_occupied_duration_minutes: number;
    real_duration_minutes: number;
    setup_duration_minutes: number;
    production_duration_minutes: number;

    estimated_duration_minutes: number;
    estimated_setup_duration_minutes: number;
    estimated_production_duration_minutes: number;

    setup_deviation_minutes: number;
    production_deviation_minutes: number;

    estimated_orders_count: number;
    orders_without_estimation_count: number;
    deviation_minutes: number;
    deviation_pct: number | null;
    within_tolerance_count: number;
    within_tolerance_pct: number | null;
    stops_count: number;
    stop_duration_minutes: number;
    average_stop_duration_minutes: number | null;
    stops_per_order: number | null;
    quality_nc_count: number;
    quality_c_count: number;
  };
};

type ReviewLineForm = {
  manufacturing_order_number: string;
  free_product_ref: string;
  customer_number: string;
  customer_order_number: string;
  quantity_value: string;
  actual_quantity_value: string;
  speed_value: string;
  start_time: string;
  end_time: string;
  notes: string;
  extra_fields: Record<string, string>;
};

type ReviewStopForm = {
  start_time: string;
  end_time: string;
  reason_id: string;
  notes: string;
};

function buildReviewLineFormFromLine(line: ProductionReportLine): ReviewLineForm {
  const extraFields: Record<string, string> = {};
  if (line.extra_fields && typeof line.extra_fields === 'object') {
    Object.entries(line.extra_fields).forEach(([k, v]) => {
      extraFields[k] = v !== null && v !== undefined ? String(v) : '';
    });
  }
  return {
    manufacturing_order_number: line.manufacturing_order_number ?? '',
    free_product_ref: line.free_product_ref ?? '',
    customer_number: line.customer_number ?? '',
    customer_order_number: line.customer_order_number ?? '',
    quantity_value:
      line.quantity_value !== null && line.quantity_value !== undefined
        ? String(line.quantity_value)
        : '',
    actual_quantity_value:
      line.actual_quantity_value !== null && line.actual_quantity_value !== undefined
        ? String(line.actual_quantity_value)
        : '',
    speed_value:
      line.speed_value !== null && line.speed_value !== undefined ? String(line.speed_value) : '',
    start_time: line.start_time ?? '',
    end_time: line.end_time ?? '',
    notes: line.notes ?? '',
    extra_fields: extraFields,
  };
}

function buildReviewStopFormFromStop(stop: ProductionReportStop): ReviewStopForm {
  return {
    start_time: stop.start_time ?? '',
    end_time: stop.end_time ?? '',
    reason_id: stop.reason_id ?? '',
    notes: stop.notes ?? '',
  };
}

function buildEmptyReviewStopForm(): ReviewStopForm {
  return { start_time: '', end_time: '', reason_id: '', notes: '' };
}

function calculateDurationMinutesFromTimes(
  startTime: string | null | undefined,
  endTime: string | null | undefined,
): number | null {
  const start = timeToMinutes(startTime);
  const end = timeToMinutes(endTime);
  if (start === null || end === null) return null;
  if (end >= start) return end - start;
  return end + 24 * 60 - start;
}

type ReviewBoardDialogProps = {
  item: ProductionReportReviewBoardItem | null;
  detail: ProductionReportDetail | null | undefined;
  productById: Map<string, Product>;
  operationSettings: ProductionOperationSettings | null | undefined;
  loading: boolean;
  validationPending?: boolean;
  timelineValidationIssue?: TimelineValidationIssue | null;
  onValidate?: () => void;
  onDetailRefresh?: () => void;
  onReportDeleted?: () => void;
  onClose: () => void;
};

type TimelineValidationEventSummary = {
  event_type: 'line' | 'stop';
  event_id: string;
  label: string;
  start_time: string | null;
  end_time: string | null;
};

type TimelineValidationGap = {
  start_time: string;
  end_time: string;
  position: 'start_of_shift' | 'between_events' | 'end_of_shift';
  previous_event: TimelineValidationEventSummary | null;
  next_event: TimelineValidationEventSummary | null;
  allowed_actions: string[];
};

type TimelineValidationOverlap = {
  start_time: string;
  end_time: string;
  previous_event: TimelineValidationEventSummary | null;
  next_event: TimelineValidationEventSummary | null;
  allowed_actions: string[];
};

type TimelineValidationIssue = {
  code: 'timeline_validation_failed';
  message: string;
  shift_window: {
    start_time: string;
    end_time: string;
  };
  incomplete_events: TimelineValidationEventSummary[];
  gaps: TimelineValidationGap[];
  overlaps: TimelineValidationOverlap[];
};

function getTodayIsoDate() {
  return new Date().toISOString().slice(0, 10);
}

function toIsoDate(value: Date) {
  return value.toISOString().slice(0, 10);
}

function getStartOfWeek(value: Date) {
  const date = new Date(value);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;

  date.setDate(date.getDate() + diff);
  return date;
}

function getEndOfWeek(value: Date) {
  const date = getStartOfWeek(value);
  date.setDate(date.getDate() + 6);
  return date;
}

function getPerformancePeriodRange(preset: PerformancePeriodPreset) {
  const today = new Date();

  if (preset === 'week') {
    return {
      dateFrom: toIsoDate(getStartOfWeek(today)),
      dateTo: toIsoDate(getEndOfWeek(today)),
    };
  }

  if (preset === 'month') {
    return {
      dateFrom: toIsoDate(new Date(today.getFullYear(), today.getMonth(), 1)),
      dateTo: toIsoDate(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
    };
  }

  if (preset === 'year') {
    return {
      dateFrom: toIsoDate(new Date(today.getFullYear(), 0, 1)),
      dateTo: toIsoDate(new Date(today.getFullYear(), 11, 31)),
    };
  }

  return {
    dateFrom: getTodayIsoDate(),
    dateTo: getTodayIsoDate(),
  };
}

function getShiftLabel(value: string | null | undefined) {
  if (value === 'morning') return 'Mañana';
  if (value === 'afternoon') return 'Tarde';
  if (value === 'night') return 'Noche';
  return '—';
}

function getReviewStatusLabel(value: ProductionReportReviewStatus) {
  if (value === 'pending_administrative_action') return 'Pendientes de acción';
  if (value === 'pending_revalidation') return 'Pendientes de revalidación';
  if (value === 'validated') return 'Validados';
  if (value === 'exported') return 'Exportados';
  return 'Todos';
}

function getReviewStatusTone(value: ProductionReportReviewStatus) {
  if (value === 'pending_revalidation') {
    return {
      border: 'rgba(248,113,113,0.28)',
      bg: 'rgba(127,29,29,0.16)',
      color: '#fecaca',
    };
  }

  if (value === 'pending_administrative_action') {
    return {
      border: 'rgba(251,191,36,0.28)',
      bg: 'rgba(120,53,15,0.16)',
      color: '#fde68a',
    };
  }

  if (value === 'validated') {
    return {
      border: 'rgba(34,197,94,0.24)',
      bg: 'rgba(20,83,45,0.16)',
      color: '#bbf7d0',
    };
  }

  if (value === 'exported') {
    return {
      border: 'rgba(168,85,247,0.24)',
      bg: 'rgba(88,28,135,0.16)',
      color: '#ddd6fe',
    };
  }

  return {
    border: 'rgba(148,163,184,0.18)',
    bg: 'rgba(15,23,42,0.56)',
    color: '#e2e8f0',
  };
}

function getReportStatusLabel(value: string | null | undefined) {
  if (value === 'draft') return 'Borrador';
  if (value === 'validated') return 'Validado';
  if (value === 'exported') return 'Exportado';
  return '—';
}

function getErrorMessage(error: unknown) {
  if (typeof error === 'object' && error !== null) {
    const maybeError = error as {
      response?: { data?: { detail?: string } };
      message?: string;
    };

    if (maybeError.response?.data?.detail) return maybeError.response.data.detail;
    if (maybeError.message) return maybeError.message;
  }

  return 'Se produjo un error inesperado.';
}

function getTimelineValidationIssue(error: unknown): TimelineValidationIssue | null {
  if (typeof error !== 'object' || error === null) return null;

  const maybeError = error as {
    response?: {
      data?: {
        detail?: unknown;
      };
    };
  };

  const detail = maybeError.response?.data?.detail;

  if (!detail || typeof detail !== 'object') return null;

  const maybeIssue = detail as TimelineValidationIssue;

  if (maybeIssue.code !== 'timeline_validation_failed') {
    return null;
  }

  return maybeIssue;
}

function getTimelineGapActionLabel(action: string) {
  if (action === 'create_stop') return 'Registrar como parada';
  if (action === 'extend_previous_end') return 'Extender actividad anterior';
  if (action === 'move_next_start') return 'Cambiar hora de inicio siguiente';
  return 'Editar manualmente';
}

function getTimelineOverlapActionLabel(action: string) {
  if (action === 'adjust_previous_end') return 'Ajustar fin del anterior';
  if (action === 'adjust_next_start') return 'Ajustar inicio del siguiente';
  return 'Editar manualmente';
}

function formatElapsedMinutes(value: number | null | undefined) {
  if (value === null || value === undefined) return '—';

  const hours = Math.floor(value / 60);
  const minutes = value % 60;

  if (hours <= 0) return `${minutes} min`;

  return `${hours} h ${String(minutes).padStart(2, '0')} min`;
}

function getCurrentTimeValue() {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
}

function normalizeTime(value: string | null | undefined) {
  if (!value) return '';

  const [hour = '', minute = ''] = String(value).split(':');

  if (!hour || !minute) return '';

  return `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
}

function timeToMinutes(value: string | null | undefined) {
  const normalized = normalizeTime(value);

  if (!normalized) return null;

  const [hour, minute] = normalized.split(':').map(Number);

  if (Number.isNaN(hour) || Number.isNaN(minute)) return null;

  return hour * 60 + minute;
}

function calculateElapsedMinutesFromStart(startTime: string | null | undefined) {
  const startMinutes = timeToMinutes(startTime);
  const nowMinutes = timeToMinutes(getCurrentTimeValue());

  if (startMinutes === null || nowMinutes === null) return null;

  if (nowMinutes >= startMinutes) {
    return nowMinutes - startMinutes;
  }

  return nowMinutes + 24 * 60 - startMinutes;
}

function parsePositiveNumber(value: string | number | null | undefined) {
  if (value === null || value === undefined || value === '') return null;

  const parsedValue = Number(value);

  if (!Number.isFinite(parsedValue) || parsedValue <= 0) return null;

  return parsedValue;
}

function formatEstimatedMinutes(totalMinutes: number | null | undefined) {
  if (totalMinutes === null || totalMinutes === undefined) return '—';

  const roundedMinutes = Math.round(totalMinutes);
  const hours = Math.floor(roundedMinutes / 60);
  const minutes = roundedMinutes % 60;

  if (hours <= 0) return `${minutes} min`;

  return `${hours} h ${String(minutes).padStart(2, '0')} min`;
}

function formatSignedMinutes(totalMinutes: number | null | undefined) {
  if (totalMinutes === null || totalMinutes === undefined) return '—';

  if (totalMinutes === 0) return '0 min';

  const sign = totalMinutes > 0 ? '+' : '-';
  return `${sign}${formatEstimatedMinutes(Math.abs(totalMinutes))}`;
}

function formatPct(value: number | null | undefined) {
  if (value === null || value === undefined) return '—';

  return `${value.toFixed(1).replace('.0', '')}%`;
}

function formatDecimal(value: number | null | undefined) {
  if (value === null || value === undefined) return '—';

  return value.toFixed(2).replace(/\.?0+$/, '');
}

const TITLE_ONLY_TOOLTIP_CARD_SX = {
  pointerEvents: 'none',
  '& > :first-child': {
    pointerEvents: 'auto',
    cursor: 'help',
    width: 'fit-content',
  },
};

function parseIsoDate(value: string) {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function addDays(value: Date, days: number) {
  const date = new Date(value);
  date.setDate(date.getDate() + days);
  return date;
}

function getDaysBetweenInclusive(dateFrom: string, dateTo: string) {
  const from = parseIsoDate(dateFrom);
  const to = parseIsoDate(dateTo);
  const diffMs = to.getTime() - from.getTime();

  return Math.max(1, Math.round(diffMs / (24 * 60 * 60 * 1000)) + 1);
}

function getPreviousPeriodRange(dateFrom: string, dateTo: string) {
  const days = getDaysBetweenInclusive(dateFrom, dateTo);
  const currentFrom = parseIsoDate(dateFrom);
  const previousTo = addDays(currentFrom, -1);
  const previousFrom = addDays(previousTo, -(days - 1));

  return {
    dateFrom: toIsoDate(previousFrom),
    dateTo: toIsoDate(previousTo),
  };
}

function formatComparisonValue(
  currentValue: number | null | undefined,
  previousValue: number | null | undefined,
  formatter: (value: number | null | undefined) => string,
) {
  if (previousValue === null || previousValue === undefined) {
    return 'Sin periodo anterior';
  }

  const current = currentValue ?? 0;
  const delta = current - previousValue;

  if (delta === 0) {
    return `Igual que periodo anterior · ${formatter(previousValue)}`;
  }

  const sign = delta > 0 ? '+' : '-';

  return `${sign}${formatter(Math.abs(delta))} vs anterior · antes ${formatter(previousValue)}`;
}

function addMinutesToTime(time: string, minutesToAdd: number) {
  const startMinutes = timeToMinutes(time);

  if (startMinutes === null) return '';

  const totalMinutes = (startMinutes + minutesToAdd) % (24 * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

function getLineExtraFieldValue(
  extraFields: Record<string, unknown> | null | undefined,
  fieldKey: string,
) {
  const rawValue = extraFields?.[fieldKey];

  if (rawValue === null || rawValue === undefined) return '';

  return String(rawValue);
}

const PENDING_QUANTITY_EXTRA_FIELD_KEY = '__pending_quantity_value';

function getControlLinePendingQuantity(
  line: NonNullable<ProductionReportFactoryControlItem['active_line']>,
) {
  return parsePositiveNumber(
    getLineExtraFieldValue(line.extra_fields, PENDING_QUANTITY_EXTRA_FIELD_KEY),
  );
}

function calculateCurrentTimingDeviation(
  elapsedMinutes: number | null,
  estimatedDurationMinutes: number | null,
): ControlLineTimingDeviation | null {
  if (elapsedMinutes === null || estimatedDurationMinutes === null) return null;

  const roundedEstimatedMinutes = Math.round(estimatedDurationMinutes);
  const deltaMinutes = elapsedMinutes - roundedEstimatedMinutes;

  if (deltaMinutes > 0) {
    return {
      label: 'Tiempo extra consumido',
      valueLabel: `+${formatEstimatedMinutes(deltaMinutes)}`,
      isOvertime: true,
    };
  }

  return {
    label: 'Tiempo restante',
    valueLabel: formatEstimatedMinutes(Math.abs(deltaMinutes)),
    isOvertime: false,
  };
}

function calculateControlLineEstimation(
  line: NonNullable<ProductionReportFactoryControlItem['active_line']>,
  operationSettings: ProductionOperationSettings | null | undefined,
): ControlLineEstimationResult {
  const estimationConfig = operationSettings?.estimation_config;
  const mode =
    estimationConfig?.mode ??
    (estimationConfig?.enable_estimated_duration ? 'quantity_by_speed' : 'none');

  if (!estimationConfig || mode === 'none') {
    return {
      enabled: false,
      estimatedDurationMinutes: null,
      estimatedEndTime: '',
      formulaLabel: '',
      missingInputs: [],
      usesSetupTime: false,
    };
  }

  const missingInputs: string[] = [];
  const pendingQuantity = getControlLinePendingQuantity(line);
  const estimationQuantity =
    pendingQuantity ?? parsePositiveNumber(line.quantity_value);
  const quantityLabel =
    pendingQuantity !== null ? 'cantidad pendiente' : 'cantidad prevista';
  const speed = parsePositiveNumber(line.speed_value);

  if (estimationQuantity === null) {
    missingInputs.push(quantityLabel);
  }

  if (speed === null) {
    missingInputs.push('velocidad');
  }

  let multiplier = 1;
  let multiplierLabel = '';

  if (mode === 'quantity_by_speed_and_multiplier') {
    const multiplierFieldKey = estimationConfig.multiplier_field_key;
    const multiplierField = operationSettings?.additional_fields_config?.find(
      (field) => field.key === multiplierFieldKey,
    );

    multiplierLabel = multiplierField?.label?.trim() || 'multiplicador';

    if (!multiplierFieldKey) {
      missingInputs.push('campo multiplicador');
    } else {
      const multiplierValue = parsePositiveNumber(
        getLineExtraFieldValue(line.extra_fields, multiplierFieldKey),
      );

      if (multiplierValue === null) {
        missingInputs.push(multiplierLabel);
      } else {
        multiplier = multiplierValue;
      }
    }
  }

  const formulaLabel =
    mode === 'quantity_by_speed_and_multiplier'
      ? `${quantityLabel} / (velocidad × ${multiplierLabel || 'multiplicador'})`
      : `${quantityLabel} / velocidad`;

  if (missingInputs.length > 0 || estimationQuantity === null || speed === null) {
    return {
      enabled: true,
      estimatedDurationMinutes: null,
      estimatedEndTime: '',
      formulaLabel,
      missingInputs,
      usesSetupTime: Boolean(estimationConfig.include_setup_time),
    };
  }

  const denominator = speed * multiplier;

  if (denominator <= 0) {
    return {
      enabled: true,
      estimatedDurationMinutes: null,
      estimatedEndTime: '',
      formulaLabel,
      missingInputs: ['velocidad válida'],
      usesSetupTime: Boolean(estimationConfig.include_setup_time),
    };
  }

  const productionDurationMinutes =
    estimationConfig.speed_time_unit === 'hour'
      ? (estimationQuantity / denominator) * 60
      : estimationQuantity / denominator;

  const setupTimeMinutes =
    estimationConfig.include_setup_time
      ? operationSettings?.production_params?.setup_time_minutes ?? 0
      : 0;

  const estimatedDurationMinutes = productionDurationMinutes + setupTimeMinutes;
  const estimatedEndTime = line.start_time
    ? addMinutesToTime(line.start_time, Math.round(estimatedDurationMinutes))
    : '';

  return {
    enabled: true,
    estimatedDurationMinutes,
    estimatedEndTime,
    formulaLabel,
    missingInputs: line.start_time ? [] : ['hora inicio'],
    usesSetupTime: Boolean(estimationConfig.include_setup_time),
  };
}


function normalizeLineKey(value: string | null | undefined) {
  return (value || 'Sin línea')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

function getStatusPriority(status: ProductionReportFactoryControlStatus) {
  if (status === 'stopped') return 0;
  if (status === 'producing') return 1;
  if (status === 'idle') return 2;
  if (status === 'validated') return 3;
  if (status === 'exported') return 4;
  return 99;
}

function getStatusLabel(status: ProductionReportFactoryControlStatus) {
  if (status === 'stopped') return 'Parada';
  if (status === 'producing') return 'Produciendo';
  if (status === 'idle') return 'Sin actividad';
  if (status === 'validated') return 'Sin actividad';
  if (status === 'exported') return 'Exportado';
  return 'Sin actividad';
}

function getStatusChipColor(
  status: ProductionReportFactoryControlStatus,
  hasConfiguredLineWithoutReport = false,
) {
  if (hasConfiguredLineWithoutReport) return 'default';
  if (status === 'stopped') return 'error';
  if (status === 'producing') return 'success';
  if (status === 'idle') return 'warning';
  if (status === 'validated') return 'info';
  if (status === 'exported') return 'secondary';

  return 'default';
}

function getStatusCardTone(
  status: ProductionReportFactoryControlStatus,
  hasConfiguredLineWithoutReport = false,
) {
  if (hasConfiguredLineWithoutReport) {
    return {
      border: 'rgba(148,163,184,0.18)',
      bg: 'rgba(15,23,42,0.56)',
      icon: <FactoryRoundedIcon />,
    };
  }

  if (status === 'stopped') {
    return {
      border: 'rgba(248,113,113,0.42)',
      bg: 'rgba(127,29,29,0.22)',
      icon: <PauseCircleRoundedIcon />,
    };
  }

  if (status === 'producing') {
    return {
      border: 'rgba(34,197,94,0.34)',
      bg: 'rgba(20,83,45,0.20)',
      icon: <PlayCircleRoundedIcon />,
    };
  }

  if (status === 'idle') {
    return {
      border: 'rgba(251,191,36,0.34)',
      bg: 'rgba(120,53,15,0.16)',
      icon: <FactoryRoundedIcon />,
    };
  }

  return {
    border: 'rgba(148,163,184,0.18)',
    bg: 'rgba(15,23,42,0.56)',
    icon: <FactoryRoundedIcon />,
  };
}

function getCurrentActivityItem(group: LineGroup) {
  return (
    group.items.find((item) => item.current_status === 'stopped') ??
    group.items.find((item) => item.current_status === 'producing') ??
    group.items.find((item) => item.current_status === 'idle') ??
    group.representative
  );
}

function findConfiguredLineForControlItem(
  item: ProductionReportFactoryControlItem,
  configuredLines: ProductionLineConfig[],
): ProductionLineConfig | null {
  if (item.production_line_config_id) {
    return (
      configuredLines.find((line) => line.id === item.production_line_config_id) ?? null
    );
  }

  const normalizedLineOrCenter = normalizeLineKey(item.line_or_center);

  if (!normalizedLineOrCenter) return null;

  return (
    configuredLines.find(
      (line) =>
        normalizeLineKey(line.name) === normalizedLineOrCenter ||
        normalizeLineKey(line.code) === normalizedLineOrCenter,
    ) ?? null
  );
}


function buildEmptyControlItemFromLine(line: ProductionLineConfig): ProductionReportFactoryControlItem {
  return {
    report_id: '',
    report_date: '',
    template_id: null,
    template_name: null,
    production_line_config_id: line.id,
    shift: '',
    line_or_center: line.name,
    operator_number: '',
    report_status: 'draft',
    current_status: 'idle',
    status_label: 'Sin parte',
    active_line: null,
    active_stop: null,
    totals: {
      production_lines_count: 0,
      closed_lines_count: 0,
      stops_count: 0,
      open_stops_count: 0,
      quality_c_count: 0,
      quality_nc_count: 0,
      quality_na_count: 0,
    },
    warnings: [],
  };
}

function buildLineGroups(
  items: ProductionReportFactoryControlItem[],
  configuredLines: ProductionLineConfig[],
): LineGroup[] {
  const grouped = new Map<string, ProductionReportFactoryControlItem[]>();

  items.forEach((item) => {
    const matchedConfiguredLine = findConfiguredLineForControlItem(item, configuredLines);
    const key = matchedConfiguredLine
      ? `config:${matchedConfiguredLine.id}`
      : `text:${normalizeLineKey(item.line_or_center)}`;

    const existing = grouped.get(key) ?? [];
    grouped.set(key, [...existing, item]);
  });

  const activeConfiguredLines = [...configuredLines]
    .filter((line) => line.is_active)
    .sort((a, b) => a.display_order - b.display_order);

  const configuredGroups = activeConfiguredLines.map((line) => {
    const key = `config:${line.id}`;
    const groupItems = grouped.get(key) ?? [];
    const hasConfiguredLineWithoutReport = groupItems.length === 0;
    const effectiveItems =
      groupItems.length > 0 ? groupItems : [buildEmptyControlItemFromLine(line)];

    const sortedItems = [...effectiveItems].sort(
      (a, b) => getStatusPriority(a.current_status) - getStatusPriority(b.current_status),
    );
    const representative = sortedItems[0];

    const status = representative.current_status;
    const warnings = Array.from(
      new Set(effectiveItems.flatMap((item) => item.warnings ?? [])),
    );

    return {
      key,
      label: line.name || representative.line_or_center || 'Línea sin nombre',
      configuredLine: line,
      items: effectiveItems,
      representative,
      current_status: status,
      status_label: hasConfiguredLineWithoutReport ? 'Sin parte' : getStatusLabel(status),
      has_configured_line_without_report: hasConfiguredLineWithoutReport,
      warnings,
      totals: {
        reports_count: groupItems.length,
        production_lines_count: groupItems.reduce(
          (sum, item) => sum + item.totals.production_lines_count,
          0,
        ),
        closed_lines_count: groupItems.reduce(
          (sum, item) => sum + item.totals.closed_lines_count,
          0,
        ),
        stops_count: groupItems.reduce(
          (sum, item) => sum + item.totals.stops_count,
          0,
        ),
        open_stops_count: groupItems.reduce(
          (sum, item) => sum + item.totals.open_stops_count,
          0,
        ),
        quality_c_count: groupItems.reduce(
          (sum, item) => sum + item.totals.quality_c_count,
          0,
        ),
        quality_nc_count: groupItems.reduce(
          (sum, item) => sum + item.totals.quality_nc_count,
          0,
        ),
        quality_na_count: groupItems.reduce(
          (sum, item) => sum + item.totals.quality_na_count,
          0,
        ),
      },
    };
  });

  const configuredKeys = new Set(activeConfiguredLines.map((line) => `config:${line.id}`));

  const unconfiguredGroups = Array.from(grouped.entries())
    .filter(([key]) => !configuredKeys.has(key))
    .map(([key, groupItems]) => {
      const sortedItems = [...groupItems].sort(
        (a, b) => getStatusPriority(a.current_status) - getStatusPriority(b.current_status),
      );
      const representative = sortedItems[0];

      const status = representative.current_status;
      const warnings = Array.from(
        new Set(groupItems.flatMap((item) => item.warnings ?? [])),
      );

      return {
        key,
        label: representative.line_or_center || 'Línea sin nombre',
        configuredLine: null,
        items: groupItems,
        representative,
        current_status: status,
        status_label: getStatusLabel(status),
        has_configured_line_without_report: false,
        warnings,
        totals: {
          reports_count: groupItems.length,
          production_lines_count: groupItems.reduce(
            (sum, item) => sum + item.totals.production_lines_count,
            0,
          ),
          closed_lines_count: groupItems.reduce(
            (sum, item) => sum + item.totals.closed_lines_count,
            0,
          ),
          stops_count: groupItems.reduce(
            (sum, item) => sum + item.totals.stops_count,
            0,
          ),
          open_stops_count: groupItems.reduce(
            (sum, item) => sum + item.totals.open_stops_count,
            0,
          ),
          quality_c_count: groupItems.reduce(
            (sum, item) => sum + item.totals.quality_c_count,
            0,
          ),
          quality_nc_count: groupItems.reduce(
            (sum, item) => sum + item.totals.quality_nc_count,
            0,
          ),
          quality_na_count: groupItems.reduce(
            (sum, item) => sum + item.totals.quality_na_count,
            0,
          ),
        },
      };
    })
    .sort(
      (a, b) =>
        getStatusPriority(a.current_status) - getStatusPriority(b.current_status) ||
        a.label.localeCompare(b.label),
    );

  return [...configuredGroups, ...unconfiguredGroups];
}

function countLineGroups(groups: LineGroup[]) {
  return groups.reduce(
    (acc, group) => {
      acc.total += 1;
      acc[group.current_status] += 1;
      acc.nc += group.totals.quality_nc_count;
      return acc;
    },
    {
      total: 0,
      producing: 0,
      stopped: 0,
      idle: 0,
      validated: 0,
      exported: 0,
      nc: 0,
    },
  );
}

const PROCESS_GROUP_TONES = [
  {
    accent: '#38bdf8',
    border: 'rgba(56,189,248,0.32)',
    bg: 'rgba(8,47,73,0.18)',
  },
  {
    accent: '#a78bfa',
    border: 'rgba(167,139,250,0.30)',
    bg: 'rgba(76,29,149,0.16)',
  },
  {
    accent: '#34d399',
    border: 'rgba(52,211,153,0.28)',
    bg: 'rgba(6,78,59,0.14)',
  },
  {
    accent: '#fbbf24',
    border: 'rgba(251,191,36,0.28)',
    bg: 'rgba(120,53,15,0.13)',
  },
  {
    accent: '#fb7185',
    border: 'rgba(251,113,133,0.28)',
    bg: 'rgba(136,19,55,0.13)',
  },
];

function getProcessGroupTone(index: number) {
  return PROCESS_GROUP_TONES[index % PROCESS_GROUP_TONES.length];
}

function normalizeProcessGroupKey(value: string | null | undefined) {
  return (value || 'Sin proceso asignado')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

function buildProcessLineGroups(groups: LineGroup[]): ProcessLineGroup[] {
  const grouped = new Map<
    string,
    {
      key: string;
      label: string;
      groups: LineGroup[];
      firstDisplayOrder: number;
    }
  >();

  groups.forEach((group, index) => {
    const processLabel = group.configuredLine?.process_group?.trim() || 'Sin proceso asignado';
    const processKey = normalizeProcessGroupKey(processLabel);
    const displayOrder = group.configuredLine?.display_order ?? index + 10000;
    const existing = grouped.get(processKey);

    if (existing) {
      existing.groups.push(group);
      existing.firstDisplayOrder = Math.min(existing.firstDisplayOrder, displayOrder);
    } else {
      grouped.set(processKey, {
        key: processKey,
        label: processLabel,
        groups: [group],
        firstDisplayOrder: displayOrder,
      });
    }
  });

  return Array.from(grouped.values())
    .sort(
      (a, b) =>
        a.firstDisplayOrder - b.firstDisplayOrder ||
        a.label.localeCompare(b.label),
    )
    .map((group, index) => ({
      ...group,
      tone: getProcessGroupTone(index),
      totals: countLineGroups(group.groups),
    }));
}

function findConfiguredLineForPerformanceLine(
  line: ProductionReportFactorySummaryLine,
  configuredLines: ProductionLineConfig[],
): ProductionLineConfig | null {
  if (line.production_line_config_id) {
    return (
      configuredLines.find(
        (configuredLine) => configuredLine.id === line.production_line_config_id,
      ) ?? null
    );
  }

  const normalizedLineOrCenter = normalizeLineKey(line.line_or_center);

  if (!normalizedLineOrCenter) return null;

  return (
    configuredLines.find(
      (configuredLine) =>
        normalizeLineKey(configuredLine.name) === normalizedLineOrCenter ||
        normalizeLineKey(configuredLine.code) === normalizedLineOrCenter,
    ) ?? null
  );
}

function buildEmptyProcessPerformanceTotals(): ProcessPerformanceGroup['totals'] {
  return {
    reports_count: 0,
    closed_orders_count: 0,

    total_occupied_duration_minutes: 0,
    real_duration_minutes: 0,
    setup_duration_minutes: 0,
    production_duration_minutes: 0,

    estimated_duration_minutes: 0,
    estimated_setup_duration_minutes: 0,
    estimated_production_duration_minutes: 0,

    setup_deviation_minutes: 0,
    production_deviation_minutes: 0,

    estimated_orders_count: 0,
    orders_without_estimation_count: 0,
    deviation_minutes: 0,
    deviation_pct: null,
    within_tolerance_count: 0,
    within_tolerance_pct: null,
    stops_count: 0,
    stop_duration_minutes: 0,
    average_stop_duration_minutes: null,
    stops_per_order: null,
    quality_nc_count: 0,
    quality_c_count: 0,
  };
}

function finalizeProcessPerformanceTotals(
  totals: ProcessPerformanceGroup['totals'],
): ProcessPerformanceGroup['totals'] {
  return {
    ...totals,
    deviation_pct:
      totals.estimated_duration_minutes > 0
        ? (totals.deviation_minutes / totals.estimated_duration_minutes) * 100
        : null,
    within_tolerance_pct:
      totals.estimated_orders_count > 0
        ? (totals.within_tolerance_count / totals.estimated_orders_count) * 100
        : null,
    average_stop_duration_minutes:
      totals.stops_count > 0 ? totals.stop_duration_minutes / totals.stops_count : null,
    stops_per_order:
      totals.closed_orders_count > 0 ? totals.stops_count / totals.closed_orders_count : null,
  };
}

function buildProcessPerformanceGroups(
  lines: ProductionReportFactorySummaryLine[],
  configuredLines: ProductionLineConfig[],
): ProcessPerformanceGroup[] {
  const grouped = new Map<
    string,
    {
      key: string;
      label: string;
      lines: ProductionReportFactorySummaryLine[];
      firstDisplayOrder: number;
      totals: ProcessPerformanceGroup['totals'];
    }
  >();

  lines.forEach((line, index) => {
    const configuredLine = findConfiguredLineForPerformanceLine(line, configuredLines);
    const processLabel = configuredLine?.process_group?.trim() || 'Sin proceso asignado';
    const processKey = normalizeProcessGroupKey(processLabel);
    const displayOrder = configuredLine?.display_order ?? index + 10000;

    const existing = grouped.get(processKey);
    const target =
      existing ??
      {
        key: processKey,
        label: processLabel,
        lines: [],
        firstDisplayOrder: displayOrder,
        totals: buildEmptyProcessPerformanceTotals(),
      };

    target.lines.push(line);
    target.firstDisplayOrder = Math.min(target.firstDisplayOrder, displayOrder);
    target.totals.reports_count += line.reports_count ?? 0;
    target.totals.closed_orders_count += line.closed_orders_count ?? 0;

    target.totals.total_occupied_duration_minutes +=
      line.total_occupied_duration_minutes ?? line.real_duration_minutes ?? 0;
    target.totals.real_duration_minutes += line.real_duration_minutes ?? 0;
    target.totals.setup_duration_minutes += line.setup_duration_minutes ?? 0;
    target.totals.production_duration_minutes += line.production_duration_minutes ?? 0;

    target.totals.estimated_duration_minutes += line.estimated_duration_minutes ?? 0;
    target.totals.estimated_setup_duration_minutes +=
      line.estimated_setup_duration_minutes ?? 0;
    target.totals.estimated_production_duration_minutes +=
      line.estimated_production_duration_minutes ?? 0;

    target.totals.setup_deviation_minutes += line.setup_deviation_minutes ?? 0;
    target.totals.production_deviation_minutes += line.production_deviation_minutes ?? 0;

    target.totals.estimated_orders_count += line.estimated_orders_count ?? 0;
    target.totals.orders_without_estimation_count += line.orders_without_estimation_count ?? 0;
    target.totals.deviation_minutes += line.deviation_minutes ?? 0;
    target.totals.within_tolerance_count += line.within_tolerance_count ?? 0;
    target.totals.stops_count += line.stops_count ?? 0;
    target.totals.stop_duration_minutes += line.stop_duration_minutes ?? 0;
    target.totals.quality_nc_count += line.quality_nc_count ?? 0;
    target.totals.quality_c_count += line.quality_c_count ?? 0;

    grouped.set(processKey, target);
  });

  return Array.from(grouped.values())
    .sort(
      (a, b) =>
        a.firstDisplayOrder - b.firstDisplayOrder ||
        a.label.localeCompare(b.label),
    )
    .map((group, index) => ({
      ...group,
      tone: getProcessGroupTone(index),
      totals: finalizeProcessPerformanceTotals(group.totals),
    }));
}

function getLineProductLabel(
  line: ProductionReportDetail['lines'][number],
  productById: Map<string, Product>,
) {
  if (line.product_id) {
    const product = productById.get(line.product_id);

    if (product) {
      if (product.code && product.name) return `${product.code} · ${product.name}`;
      if (product.code) return product.code;
      if (product.name) return product.name;
    }
  }

  if (line.free_product_ref) return line.free_product_ref;

  if (line.customer_order_number) return `Pedido ${line.customer_order_number}`;
  if (line.manufacturing_order_number) return `Orden ${line.manufacturing_order_number}`;

  return 'Producto sin referencia';
}

function getStopLabel(stop: ProductionReportDetail['stops'][number]) {
  return stop.reason_label || 'Parada';
}

type ReviewPrecheckIssue = {
  severity: 'error' | 'warning';
  title: string;
  description: string;
  lineId?: string;
  stopId?: string;
  idlePeriodId?: string;
  prevLineId?: string;
  prevStopId?: string;
  prevIdlePeriodId?: string;
  nextLineId?: string;
  nextStopId?: string;
  nextIdlePeriodId?: string;
  actions?: string[];
  gapStart?: string;
  gapEnd?: string;
};

function parseClockTimeToMinutes(value: string | null | undefined) {
  if (!value) return null;

  const [hoursRaw, minutesRaw] = value.split(':');
  const hours = Number(hoursRaw);
  const minutes = Number(minutesRaw);

  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return null;
  }

  return hours * 60 + minutes;
}

function getShiftWindowInMinutes(shift: string | null | undefined) {
  if (shift === 'morning') {
    return {
      start: 6 * 60,
      end: 14 * 60,
      startLabel: '06:00',
      endLabel: '14:00',
    };
  }

  if (shift === 'afternoon') {
    return {
      start: 14 * 60,
      end: 22 * 60,
      startLabel: '14:00',
      endLabel: '22:00',
    };
  }

  if (shift === 'night') {
    return {
      start: 22 * 60,
      end: 30 * 60,
      startLabel: '22:00',
      endLabel: '06:00',
    };
  }

  return null;
}

function normalizeShiftTimeInMinutes(
  value: string | null | undefined,
  shift: string | null | undefined,
) {
  const minutes = parseClockTimeToMinutes(value);

  if (minutes === null) {
    return null;
  }

  if (shift === 'night' && minutes <= 6 * 60) {
    return minutes + 24 * 60;
  }

  return minutes;
}

function formatMinutesAsClock(value: number) {
  const normalized = ((value % (24 * 60)) + 24 * 60) % (24 * 60);
  const hours = Math.floor(normalized / 60);
  const minutes = normalized % 60;

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

function FactoryLineCard({
  group,
  clockTick,
  operationSettings,
  onOpenDetail,
}: {
  group: LineGroup;
  clockTick: number;
  operationSettings: ProductionOperationSettings | null | undefined;
  onOpenDetail: (group: LineGroup) => void;
}) {
  const tone = getStatusCardTone(
    group.current_status,
    group.has_configured_line_without_report,
  );
  const currentItem = getCurrentActivityItem(group);

  const liveActiveLineElapsedMinutes = useMemo(
    () => calculateElapsedMinutesFromStart(currentItem.active_line?.start_time),
    [currentItem.active_line?.start_time, clockTick],
  );

  const liveActiveStopElapsedMinutes = useMemo(
    () => calculateElapsedMinutesFromStart(currentItem.active_stop?.start_time),
    [currentItem.active_stop?.start_time, clockTick],
  );

  const activeLineEstimation = useMemo(
    () =>
      currentItem.active_line
        ? calculateControlLineEstimation(currentItem.active_line, operationSettings)
        : null,
    [currentItem.active_line, operationSettings],
  );

  const activeLineTimingDeviation = useMemo(
    () =>
      activeLineEstimation?.enabled
        ? calculateCurrentTimingDeviation(
            liveActiveLineElapsedMinutes,
            activeLineEstimation.estimatedDurationMinutes,
          )
        : null,
    [
      activeLineEstimation?.enabled,
      activeLineEstimation?.estimatedDurationMinutes,
      liveActiveLineElapsedMinutes,
    ],
  );

  const isProducingWithActiveLine =
    group.current_status === 'producing' && Boolean(currentItem.active_line);

  const isCompactInactiveLine =
    !isProducingWithActiveLine &&
    (group.has_configured_line_without_report || group.current_status === 'idle');

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        border: `1px solid ${tone.border}`,
        bgcolor: tone.bg,
        p: 2,
        minHeight: 300,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack spacing={2} sx={{ height: '100%', flex: 1 }}>
        <Stack direction="row" justifyContent="space-between" spacing={1.5}>
          <Stack spacing={0.4}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box sx={{ color: '#38bdf8', display: 'flex' }}>{tone.icon}</Box>
              <Typography sx={{ color: '#f8fafc', fontWeight: 950, fontSize: '1.16rem' }}>
                {group.label}
              </Typography>
            </Stack>

            {!isProducingWithActiveLine && group.has_configured_line_without_report ? (
              <Typography sx={{ color: '#94a3b8', fontSize: '0.88rem' }}>
                {group.configuredLine?.code || 'Línea configurada'}
              </Typography>
            ) : null}
          </Stack>

          <Chip
            size="small"
            label={group.status_label}
            color={getStatusChipColor(
              group.current_status,
              group.has_configured_line_without_report,
            )}
            sx={{ fontWeight: 900 }}
          />
        </Stack>

        {group.has_configured_line_without_report ? (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2.5,
              border: '1px dashed rgba(148,163,184,0.18)',
              bgcolor: 'rgba(15,23,42,0.20)',
              px: 1.35,
              py: 1.05,
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={1}
            >
              <Stack spacing={0.25}>
                <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                  Sin parte
                </Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.82rem' }}>
                  Línea configurada sin parte.
                </Typography>
              </Stack>

              <Typography
                sx={{
                  color: '#cbd5e1',
                  fontSize: '0.8rem',
                  fontWeight: 850,
                  textAlign: { xs: 'left', sm: 'right' },
                  whiteSpace: 'nowrap',
                }}
              >
                Órdenes {group.totals.closed_lines_count}/{group.totals.production_lines_count} · Paradas{' '}
                {group.totals.stops_count} · C {group.totals.quality_c_count} · NC{' '}
                {group.totals.quality_nc_count}
              </Typography>
            </Stack>
          </Paper>
        ) : group.current_status === 'stopped' && currentItem.active_stop ? (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid rgba(248,113,113,0.30)',
              bgcolor: 'rgba(127,29,29,0.20)',
              px: 1.5,
              py: 1.4,
            }}
          >
            <Stack spacing={0.45}>
              <Typography sx={{ color: '#fecaca', fontWeight: 950 }}>
                Línea parada
              </Typography>
              <Typography sx={{ color: '#f8fafc', fontWeight: 900, fontSize: '1.05rem' }}>
                {currentItem.active_stop.reason_label}
              </Typography>
              <Typography sx={{ color: '#fca5a5', fontSize: '0.9rem' }}>
                Desde {currentItem.active_stop.start_time} · {formatElapsedMinutes(liveActiveStopElapsedMinutes)}
              </Typography>
            </Stack>
          </Paper>
        ) : group.current_status === 'producing' && currentItem.active_line ? (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid rgba(34,197,94,0.26)',
              bgcolor: 'rgba(20,83,45,0.16)',
              px: 1.5,
              py: 1.35,
            }}
          >
            <Stack spacing={1.05}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) minmax(0, auto)',
                  gap: 1,
                  alignItems: 'start',
                }}
              >
                <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                  <Typography sx={{ color: '#bbf7d0', fontWeight: 950 }}>
                    Fabricando ahora
                  </Typography>

                  <Typography
                    sx={{
                      color: '#f8fafc',
                      fontWeight: 950,
                      fontSize: '1.02rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={currentItem.active_line.product_label}
                  >
                    {currentItem.active_line.product_label}
                  </Typography>
                </Stack>

                <Stack spacing={0.25} sx={{ minWidth: 0, textAlign: 'right' }}>
                  <Typography
                    sx={{
                      color: '#cbd5e1',
                      fontWeight: 850,
                      fontSize: '0.84rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: 136,
                    }}
                    title={currentItem.active_line.manufacturing_order_number || '—'}
                  >
                    {currentItem.active_line.manufacturing_order_number || '—'}
                  </Typography>

                  <Typography sx={{ color: '#94a3b8', fontSize: '0.84rem', fontWeight: 800 }}>
                    Inicio:{' '}
                    {currentItem.active_line.start_time
                      ? String(currentItem.active_line.start_time).slice(0, 5)
                      : '—'}
                  </Typography>
                </Stack>
              </Box>

              {activeLineEstimation?.enabled ? (
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 2.5,
                    border: '1px solid rgba(59,130,246,0.22)',
                    bgcolor: 'rgba(15,23,42,0.24)',
                    px: 1.25,
                    py: 1,
                  }}
                >
                  <Stack spacing={0.85}>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          sm: 'minmax(0, 1fr) minmax(0, 0.85fr)',
                        },
                        gap: 1,
                        alignItems: 'end',
                      }}
                    >
                      <Box>
                        <Typography sx={{ color: '#93c5fd', fontSize: '0.74rem', fontWeight: 900 }}>
                          Hora teórica de fin
                        </Typography>
                        <Typography
                          sx={{
                            color: '#f8fafc',
                            fontSize: '1.65rem',
                            fontWeight: 950,
                            lineHeight: 1.02,
                          }}
                        >
                          {operationSettings?.estimation_config?.enable_estimated_end_time
                            ? activeLineEstimation.estimatedEndTime || '—'
                            : '—'}
                        </Typography>
                      </Box>

                      <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                        <Typography sx={{ color: '#64748b', fontSize: '0.72rem', fontWeight: 850 }}>
                          Tiempo tramo actual
                        </Typography>
                        <Typography
                          sx={{
                            color: '#e2e8f0',
                            fontSize: '1.05rem',
                            fontWeight: 950,
                            lineHeight: 1.15,
                          }}
                        >
                          {formatElapsedMinutes(liveActiveLineElapsedMinutes)}
                        </Typography>
                      </Box>
                    </Box>

                    {activeLineTimingDeviation ? (
                      <Paper
                        elevation={0}
                        sx={{
                          borderRadius: 2,
                          border: activeLineTimingDeviation.isOvertime
                            ? '1px solid rgba(248,113,113,0.34)'
                            : '1px solid rgba(34,197,94,0.24)',
                          bgcolor: activeLineTimingDeviation.isOvertime
                            ? 'rgba(127,29,29,0.2)'
                            : 'rgba(20,83,45,0.18)',
                          px: 1,
                          py: 0.8,
                        }}
                      >
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          spacing={1}
                          alignItems="center"
                        >
                          <Typography
                            sx={{
                              color: activeLineTimingDeviation.isOvertime ? '#fecaca' : '#bbf7d0',
                              fontSize: '0.74rem',
                              fontWeight: 850,
                            }}
                          >
                            {activeLineTimingDeviation.label}
                          </Typography>
                          <Typography
                            sx={{
                              color: activeLineTimingDeviation.isOvertime ? '#fca5a5' : '#f8fafc',
                              fontSize: '1.18rem',
                              fontWeight: 950,
                              lineHeight: 1,
                            }}
                          >
                            {activeLineTimingDeviation.valueLabel}
                          </Typography>
                        </Stack>
                      </Paper>
                    ) : null}
                  </Stack>
                </Paper>
              ) : (
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 2.5,
                    border: '1px solid rgba(148,163,184,0.14)',
                    bgcolor: 'rgba(15,23,42,0.20)',
                    px: 1.25,
                    py: 1,
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    spacing={1}
                    alignItems="center"
                  >
                    <Typography sx={{ color: '#64748b', fontSize: '0.72rem', fontWeight: 850 }}>
                      Tiempo tramo actual
                    </Typography>
                    <Typography sx={{ color: '#e2e8f0', fontSize: '1.05rem', fontWeight: 950 }}>
                      {formatElapsedMinutes(liveActiveLineElapsedMinutes)}
                    </Typography>
                  </Stack>
                </Paper>
              )}
            </Stack>
          </Paper>
        ) : (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2.5,
              border: '1px dashed rgba(148,163,184,0.18)',
              bgcolor: 'rgba(15,23,42,0.20)',
              px: 1.35,
              py: 1.05,
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={1}
            >
              <Stack spacing={0.25}>
                <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                  Sin actividad actual
                </Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.82rem' }}>
                  No hay orden activa ni parada abierta.
                </Typography>
              </Stack>

              <Typography
                sx={{
                  color: '#cbd5e1',
                  fontSize: '0.8rem',
                  fontWeight: 850,
                  textAlign: { xs: 'left', sm: 'right' },
                  whiteSpace: 'nowrap',
                }}
              >
                Órdenes {group.totals.closed_lines_count}/{group.totals.production_lines_count} · Paradas{' '}
                {group.totals.stops_count} · C {group.totals.quality_c_count} · NC{' '}
                {group.totals.quality_nc_count}
              </Typography>
            </Stack>
          </Paper>
        )}

        {!isProducingWithActiveLine && !isCompactInactiveLine ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: 1,
            }}
          >
          <Box>
            <Typography sx={{ color: '#64748b', fontSize: '0.72rem', fontWeight: 800 }}>
              Órdenes
            </Typography>
            <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
              {group.totals.closed_lines_count}/{group.totals.production_lines_count}
            </Typography>
          </Box>

          <Box>
            <Typography sx={{ color: '#64748b', fontSize: '0.72rem', fontWeight: 800 }}>
              Paradas
            </Typography>
            <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
              {group.totals.stops_count}
            </Typography>
          </Box>

          <Box>
            <Typography sx={{ color: '#64748b', fontSize: '0.72rem', fontWeight: 800 }}>
              Calidad
            </Typography>
            <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
              C {group.totals.quality_c_count} · NC {group.totals.quality_nc_count}
            </Typography>
          </Box>
        </Box>
        ) : null}

        {!isProducingWithActiveLine && !isCompactInactiveLine && group.warnings.length > 0 && (
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {group.warnings.slice(0, 3).map((warning) => (
              <Chip
                key={warning}
                size="small"
                icon={<ReportProblemRoundedIcon />}
                label={warning}
                color={warning.includes('NC') ? 'error' : 'warning'}
                variant="outlined"
              />
            ))}
          </Stack>
        )}

        <Button
          fullWidth
          variant="outlined"
          startIcon={<VisibilityRoundedIcon />}
          disabled={group.has_configured_line_without_report}
          onClick={() => onOpenDetail(group)}
          sx={{ mt: 'auto' }}
        >
          Ver detalle
        </Button>
      </Stack>
    </Paper>
  );
}

function LineDetailDialog({
  group,
  details,
  productById,
  loading,
  onClose,
}: {
  group: LineGroup | null;
  details: ProductionReportDetail[];
  productById: Map<string, Product>;
  loading: boolean;
  onClose: () => void;
}) {
  if (!group) return null;

  // Filtrar partes de hoy y ayer únicamente
  const todayStr = new Date().toISOString().slice(0, 10);
  const yesterdayStr = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  const recentDetails = details.filter(
    (d) => d.report.report_date === todayStr || d.report.report_date === yesterdayStr,
  );

  const allLines = recentDetails.flatMap((detail) =>
    detail.lines.map((line) => ({
      ...line,
      report: detail.report,
    })),
  );

  const allStops = recentDetails.flatMap((detail) =>
    detail.stops.map((stop) => ({
      ...stop,
      report: detail.report,
    })),
  );

  const allIdlePeriods = recentDetails.flatMap((detail) =>
    (detail.idle_periods ?? []).map((idlePeriod) => ({
      ...idlePeriod,
      report: detail.report,
    })),
  );

  const parseQuantityValue = (value: unknown) => {
    const parsedValue = Number(value);

    return Number.isFinite(parsedValue) ? parsedValue : null;
  };

  const shiftDisplayOrder: Record<string, number> = {
    morning: 1,
    afternoon: 2,
    night: 3,
  };

  const quantityDisplayByLineId = new Map<
    string,
    {
      actualQuantityInSegment: number | null;
      accumulatedActualQuantity: number;
      totalOrderQuantity: number | null;
    }
  >();

  const linesByManufacturingOrderNumber = new Map<string, typeof allLines>();

  allLines.forEach((line) => {
    const normalizedManufacturingOrderNumber = String(
      line.manufacturing_order_number || '',
    )
      .trim()
      .toLowerCase();

    const groupKey =
      normalizedManufacturingOrderNumber || `line:${line.id}`;

    const existingLines = linesByManufacturingOrderNumber.get(groupKey) ?? [];

    linesByManufacturingOrderNumber.set(groupKey, [
      ...existingLines,
      line,
    ]);
  });

  linesByManufacturingOrderNumber.forEach((lines) => {
    const orderedLines = [...lines].sort((a, b) => {
      const reportDateDelta = String(a.report.report_date || '').localeCompare(
        String(b.report.report_date || ''),
      );

      if (reportDateDelta !== 0) return reportDateDelta;

      const shiftDelta =
        (shiftDisplayOrder[a.report.shift] ?? 99) -
        (shiftDisplayOrder[b.report.shift] ?? 99);

      if (shiftDelta !== 0) return shiftDelta;

      const startTimeDelta = String(a.start_time || '').localeCompare(
        String(b.start_time || ''),
      );

      if (startTimeDelta !== 0) return startTimeDelta;

      return String(a.created_at || '').localeCompare(
        String(b.created_at || ''),
      );
    });

    const totalOrderQuantity = orderedLines.reduce<number | null>(
      (currentTotal, line) => {
        const storedQuantity = parseQuantityValue(line.quantity_value);
        const pendingQuantity = parseQuantityValue(
          line.extra_fields?.__pending_quantity_value,
        );

        const candidateTotal = Math.max(
          storedQuantity ?? 0,
          pendingQuantity ?? 0,
        );

        if (candidateTotal <= 0) {
          return currentTotal;
        }

        return currentTotal === null
          ? candidateTotal
          : Math.max(currentTotal, candidateTotal);
      },
      null,
    );

    let accumulatedActualQuantity = 0;

    orderedLines.forEach((line) => {
      const actualQuantityInSegment = parseQuantityValue(
        line.actual_quantity_value,
      );

      if (actualQuantityInSegment !== null) {
        accumulatedActualQuantity += actualQuantityInSegment;
      }

      quantityDisplayByLineId.set(line.id, {
        actualQuantityInSegment,
        accumulatedActualQuantity,
        totalOrderQuantity,
      });
    });
  });

  const qualityC = recentDetails.reduce(
    (sum, detail) => sum + detail.quality_results.filter((item) => item.result === 'C').length,
    0,
  );
  const qualityNC = recentDetails.reduce(
    (sum, detail) => sum + detail.quality_results.filter((item) => item.result === 'NC').length,
    0,
  );
  const qualityNA = recentDetails.reduce(
    (sum, detail) => sum + detail.quality_results.filter((item) => item.result === 'NA').length,
    0,
  );

  return (
    <Dialog open={Boolean(group)} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>
        <Stack spacing={0.5}>
          <Typography sx={{ fontWeight: 950 }}>
            Detalle de línea · {group.label}
          </Typography>
          <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
            Solo lectura. Actividad, partes, órdenes, paradas y calidad del filtro seleccionado.
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <LinearProgress />
        ) : (
          <Stack spacing={2.5}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'repeat(5, minmax(0, 1fr))',
                },
                gap: 1.25,
              }}
            >
              {[
                { label: 'Partes', value: details.length },
                { label: 'Órdenes', value: allLines.length },
                { label: 'Paradas', value: allStops.length },
                { label: 'Sin orden', value: allIdlePeriods.length },
                { label: 'Calidad', value: `C ${qualityC} · NC ${qualityNC} · Saltados ${qualityNA}` },
              ].map((item) => (
                <Paper
                  key={item.label}
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid rgba(148,163,184,0.16)',
                    bgcolor: 'rgba(15,23,42,0.48)',
                    px: 1.5,
                    py: 1.25,
                  }}
                >
                  <Typography sx={{ color: '#64748b', fontSize: '0.76rem', fontWeight: 850 }}>
                    {item.label}
                  </Typography>
                  <Typography sx={{ color: '#f8fafc', fontWeight: 950 }}>
                    {item.value}
                  </Typography>
                </Paper>
              ))}
            </Box>

            {recentDetails.length === 0 ? (
              <Alert severity="info" variant="outlined">
                No hay actividad en las últimas 48 horas para esta línea.
              </Alert>
            ) : (
              <Stack spacing={2.5}>
                {recentDetails.map((detail) => {
                  // Mezclar órdenes y paradas y ordenar por start_time desc (más reciente primero)
                  type EventItem =
                    | { kind: 'line'; data: typeof detail.lines[0] }
                    | { kind: 'stop'; data: typeof detail.stops[0] }
                    | { kind: 'idle'; data: NonNullable<typeof detail.idle_periods>[number] };

                  const events: EventItem[] = [
                    ...detail.lines.map((l) => ({ kind: 'line' as const, data: l })),
                    ...detail.stops.map((s) => ({ kind: 'stop' as const, data: s })),
                    ...(detail.idle_periods ?? []).map((idlePeriod) => ({
                      kind: 'idle' as const,
                      data: idlePeriod,
                    })),
                  ].sort((a, b) => {
                    const aStart = normalizeShiftTimeInMinutes(
                      a.data.start_time,
                      detail.report.shift,
                    );
                    const bStart = normalizeShiftTimeInMinutes(
                      b.data.start_time,
                      detail.report.shift,
                    );

                    return (bStart ?? -1) - (aStart ?? -1);
                  });

                  return (
                    <Stack key={detail.report.id} spacing={1}>
                      {/* Cabecera del parte */}
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Stack spacing={0.15}>
                          <Typography sx={{ color: '#f8fafc', fontWeight: 850, fontSize: '0.95rem' }}>
                            {detail.report.report_date} · {getShiftLabel(detail.report.shift)} · Operario {detail.report.operator_number}
                          </Typography>
                          <Typography sx={{ color: '#64748b', fontSize: '0.8rem' }}>
                            {detail.lines.length} órdenes · {detail.stops.length} paradas ·{' '}
                            {(detail.idle_periods ?? []).length} sin orden
                          </Typography>
                        </Stack>
                        <Chip
                          size="small"
                          label={detail.report.status}
                          variant="outlined"
                          color={detail.report.status === 'validated' ? 'success' : 'default'}
                        />
                      </Stack>

                      {/* Lista unificada de eventos */}
                      {events.length === 0 ? (
                        <Typography sx={{ color: '#64748b', fontSize: '0.82rem', pl: 0.5 }}>
                          Sin eventos registrados.
                        </Typography>
                      ) : (
                        <Stack spacing={0.75}>
                          {events.map((event) => {
                            if (event.kind === 'line') {
                              const line = event.data;
                              const isOpen = !line.end_time;
                              return (
                                <Paper
                                  key={line.id}
                                  elevation={0}
                                  sx={{
                                    borderRadius: 2,
                                    border: isOpen
                                      ? '1px solid rgba(251,146,60,0.4)'
                                      : '1px solid rgba(34,197,94,0.2)',
                                    bgcolor: isOpen
                                      ? 'rgba(124,45,18,0.18)'
                                      : 'rgba(20,83,45,0.14)',
                                    px: 1.5,
                                    py: 1,
                                  }}
                                >
                                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                                    <Stack spacing={0.2}>
                                      <Stack direction="row" spacing={0.75} alignItems="center">
                                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: isOpen ? '#fb923c' : '#4ade80', flexShrink: 0 }} />
                                        <Typography sx={{ color: '#f8fafc', fontWeight: 800, fontSize: '0.88rem' }}>
                                          {getLineProductLabel(line, productById)}
                                        </Typography>
                                      </Stack>
                                      <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem', pl: 2 }}>
                                        OF {line.manufacturing_order_number || '—'} · {line.start_time || '—'} – {line.end_time || '…'} · Turno: {formatDecimal(quantityDisplayByLineId.get(line.id)?.actualQuantityInSegment)} {line.quantity_unit || ''} · OF: {formatDecimal(quantityDisplayByLineId.get(line.id)?.accumulatedActualQuantity)}/{formatDecimal(quantityDisplayByLineId.get(line.id)?.totalOrderQuantity)} {line.quantity_unit || ''}
                                      </Typography>
                                    </Stack>
                                    <Chip
                                      size="small"
                                      label={isOpen ? 'Activa' : `${line.duration_minutes ?? '—'} min`}
                                      sx={{
                                        bgcolor: isOpen ? 'rgba(251,146,60,0.15)' : 'rgba(34,197,94,0.1)',
                                        color: isOpen ? '#fb923c' : '#4ade80',
                                        border: isOpen ? '1px solid rgba(251,146,60,0.3)' : '1px solid rgba(34,197,94,0.2)',
                                        fontWeight: 700,
                                        flexShrink: 0,
                                      }}
                                    />
                                  </Stack>
                                </Paper>
                              );
                            }

                            if (event.kind === 'idle') {
                              const idlePeriod = event.data;
                              return (
                                <Paper
                                  key={idlePeriod.id}
                                  elevation={0}
                                  sx={{
                                    borderRadius: 2,
                                    border: '1px dashed rgba(148,163,184,0.28)',
                                    bgcolor: 'rgba(30,41,59,0.18)',
                                    px: 1.5,
                                    py: 1,
                                  }}
                                >
                                  <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    spacing={1}
                                  >
                                    <Stack spacing={0.2}>
                                      <Stack direction="row" spacing={0.75} alignItems="center">
                                        <Box
                                          sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: '50%',
                                            bgcolor: '#94a3b8',
                                            flexShrink: 0,
                                          }}
                                        />
                                        <Typography
                                          sx={{
                                            color: '#cbd5e1',
                                            fontWeight: 800,
                                            fontSize: '0.88rem',
                                          }}
                                        >
                                          Sin orden planificada
                                        </Typography>
                                      </Stack>
                                      <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem', pl: 2 }}>
                                        {idlePeriod.start_time || '—'} – {idlePeriod.end_time || '—'}
                                        {idlePeriod.notes ? ` · ${idlePeriod.notes}` : ''}
                                      </Typography>
                                    </Stack>

                                    <Chip
                                      size="small"
                                      label={`${idlePeriod.duration_minutes ?? '—'} min`}
                                      sx={{
                                        bgcolor: 'rgba(148,163,184,0.10)',
                                        color: '#cbd5e1',
                                        border: '1px solid rgba(148,163,184,0.20)',
                                        fontWeight: 700,
                                        flexShrink: 0,
                                      }}
                                    />
                                  </Stack>
                                </Paper>
                              );
                            }

                            const stop = event.data;
                            const isOpen = !stop.end_time;
                            return (
                              <Paper
                                key={stop.id}
                                elevation={0}
                                sx={{
                                  borderRadius: 2,
                                  border: '1px solid rgba(239,68,68,0.25)',
                                  bgcolor: 'rgba(127,29,29,0.15)',
                                  px: 1.5,
                                  py: 1,
                                }}
                              >
                                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                                  <Stack spacing={0.2}>
                                    <Stack direction="row" spacing={0.75} alignItems="center">
                                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#f87171', flexShrink: 0 }} />
                                      <Typography sx={{ color: '#fca5a5', fontWeight: 800, fontSize: '0.88rem' }}>
                                        {getStopLabel(stop)}
                                      </Typography>
                                    </Stack>
                                    <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem', pl: 2 }}>
                                      {stop.start_time || '—'} – {stop.end_time || '…'}{stop.notes ? ` · ${stop.notes}` : ''}
                                    </Typography>
                                  </Stack>
                                  <Chip
                                    size="small"
                                    label={isOpen ? 'Activa' : `${stop.duration_minutes ?? '—'} min`}
                                    sx={{
                                      bgcolor: 'rgba(239,68,68,0.12)',
                                      color: '#f87171',
                                      border: '1px solid rgba(239,68,68,0.25)',
                                      fontWeight: 700,
                                      flexShrink: 0,
                                    }}
                                  />
                                </Stack>
                              </Paper>
                            );
                          })}
                        </Stack>
                      )}

                      <Divider sx={{ borderColor: 'rgba(148,163,184,0.1)', mt: 0.5 }} />
                    </Stack>
                  );
                })}
              </Stack>
            )}

            <Divider sx={{ borderColor: 'rgba(148,163,184,0.18)' }} />

            <Stack spacing={1.25}>
              <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                Paradas
              </Typography>

              {allStops.length === 0 ? (
                <Alert severity="info" variant="outlined">
                  No hay paradas registradas para esta línea.
                </Alert>
              ) : (
                allStops.map((stop) => (
                  <Paper
                    key={stop.id}
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: '1px solid rgba(148,163,184,0.14)',
                      bgcolor: 'rgba(2,6,23,0.24)',
                      px: 1.5,
                      py: 1.2,
                    }}
                  >
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      justifyContent="space-between"
                      spacing={1}
                    >
                      <Stack spacing={0.25}>
                        <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
                          {getStopLabel(stop)}
                        </Typography>
                        <Typography sx={{ color: '#94a3b8', fontSize: '0.88rem' }}>
                          {stop.start_time || '—'} - {stop.end_time || '—'} · {stop.notes || 'Sin observaciones'}
                        </Typography>
                      </Stack>

                      <Chip
                        size="small"
                        label={stop.duration_minutes === null ? 'Abierta' : `${stop.duration_minutes} min`}
                        color={stop.end_time ? 'default' : 'error'}
                        variant="outlined"
                      />
                    </Stack>
                  </Paper>
                ))
              )}
            </Stack>
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

function ReviewBoardDialog({
  item,
  detail,
  productById,
  operationSettings,
  loading,
  validationPending = false,
  timelineValidationIssue = null,
  onValidate = () => {},
  onDetailRefresh = () => {},
  onReportDeleted = () => {},
  onClose,
}: ReviewBoardDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const allLines = detail?.lines ?? [];
  const allStops = detail?.stops ?? [];
  const allIdlePeriods = detail?.idle_periods ?? [];
  const allQualityResults = detail?.quality_results ?? [];
  const tone = item ? getReviewStatusTone(item.review_status) : null;

  const qualitySummary = {
    total: allQualityResults.length,
    conforming: allQualityResults.filter((result) => result.result === 'C').length,
    nonConforming: allQualityResults.filter((result) => result.result === 'NC').length,
    skipped: allQualityResults.filter((result) => result.result === 'NA').length,
  };

  type ReviewDisplayTimelineEvent =
    | {
        kind: 'line';
        id: string;
        title: string;
        subtitle: string;
        start_time: string | null;
        end_time: string | null;
        duration_minutes: number | null;
      }
    | {
        kind: 'stop';
        id: string;
        title: string;
        subtitle: string;
        start_time: string | null;
        end_time: string | null;
        duration_minutes: number | null;
      }
    | {
        kind: 'idle';
        id: string;
        title: string;
        subtitle: string;
        start_time: string | null;
        end_time: string | null;
        duration_minutes: number | null;
      };

  const reviewTimelineEvents: ReviewDisplayTimelineEvent[] = [
    ...allLines.map((line) => ({
      kind: 'line' as const,
      id: line.id,
      title: getLineProductLabel(line, productById),
      subtitle: [
        line.manufacturing_order_number ? `OF ${line.manufacturing_order_number}` : null,
        line.free_product_ref ? `Ref. ${line.free_product_ref}` : null,
        line.actual_quantity_value !== null && line.actual_quantity_value !== undefined
          ? `Cantidad real ${line.actual_quantity_value}`
          : null,
        line.quantity_value !== null && line.quantity_value !== undefined
          ? `Prevista ${line.quantity_value}`
          : null,
        line.speed_value !== null && line.speed_value !== undefined
          ? `Velocidad ${line.speed_value}`
          : null,
      ]
        .filter(Boolean)
        .join(' · '),
      start_time: line.start_time,
      end_time: line.end_time,
      duration_minutes: line.duration_minutes,
    })),
    ...allStops.map((stop) => ({
      kind: 'stop' as const,
      id: stop.id,
      title: getStopLabel(stop),
      subtitle: stop.notes || 'Parada registrada',
      start_time: stop.start_time,
      end_time: stop.end_time,
      duration_minutes: stop.duration_minutes,
    })),
    ...allIdlePeriods.map((idlePeriod) => ({
      kind: 'idle' as const,
      id: idlePeriod.id,
      title: 'Sin orden planificada',
      subtitle: idlePeriod.notes || 'Hueco de turno cubierto para validación',
      start_time: idlePeriod.start_time,
      end_time: idlePeriod.end_time,
      duration_minutes: idlePeriod.duration_minutes,
    })),
  ].sort((a, b) => {
    const aStart = normalizeShiftTimeInMinutes(a.start_time, item?.shift ?? null);
    const bStart = normalizeShiftTimeInMinutes(b.start_time, item?.shift ?? null);

    return (aStart ?? 0) - (bStart ?? 0);
  });

  // --- Estado de edición inline ---
  const [editingLine, setEditingLine] = useState<ProductionReportLine | null>(null);
  const [lineForm, setLineForm] = useState<ReviewLineForm>({
    manufacturing_order_number: '',
    free_product_ref: '',
    customer_number: '',
    customer_order_number: '',
    quantity_value: '',
    actual_quantity_value: '',
    speed_value: '',
    start_time: '',
    end_time: '',
    notes: '',
    extra_fields: {},
  });
  const [editingStop, setEditingStop] = useState<ProductionReportStop | null>(null);
  const [stopForm, setStopForm] = useState<ReviewStopForm>(buildEmptyReviewStopForm());
  const [creatingStop, setCreatingStop] = useState(false);
  const [newStopForm, setNewStopForm] = useState<ReviewStopForm>(buildEmptyReviewStopForm());

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const deleteReportMutation = useMutation({
    mutationFn: () => {
      if (!item?.report_id) throw new Error('Sin parte seleccionado.');
      return deleteIndustrialCostProductionReport(item.report_id);
    },
    onSuccess: () => {
      enqueueSnackbar('Parte eliminado correctamente.', { variant: 'success' });
      setConfirmDeleteOpen(false);
      onClose();
      onReportDeleted();
    },
    onError: (error: unknown) => {
      const msg =
        typeof error === 'object' && error !== null
          ? ((error as { response?: { data?: { detail?: string } }; message?: string })
              .response?.data?.detail ??
            (error as { message?: string }).message ??
            'Error al eliminar el parte.')
          : 'Error al eliminar el parte.';
      enqueueSnackbar(msg, { variant: 'error' });
    },
  });

  const stopReasonsQuery = useQuery({
    queryKey: ['industrial-cost-production-report-stop-reasons-config'],
    queryFn: listIndustrialCostProductionReportStopReasonsConfig,
    enabled: Boolean(item),
  });
  const stopReasons: ProductionReportStopReason[] = stopReasonsQuery.data ?? [];

  const openEditLine = (line: ProductionReportLine, overrides?: Partial<ReviewLineForm>) => {
    setEditingLine(line);
    setLineForm({ ...buildReviewLineFormFromLine(line), ...overrides });
  };

  const openEditStop = (stop: ProductionReportStop, overrides?: Partial<ReviewStopForm>) => {
    setEditingStop(stop);
    setStopForm({ ...buildReviewStopFormFromStop(stop), ...overrides });
  };

  const openCreateStop = (prefill?: Partial<ReviewStopForm>) => {
    setNewStopForm({ ...buildEmptyReviewStopForm(), ...prefill });
    setCreatingStop(true);
  };

  const updateLineMutation = useMutation({
    mutationFn: (vars: { line: ProductionReportLine; form: ReviewLineForm }) => {
      if (!item?.report_id) throw new Error('Sin parte seleccionado.');
      const additionalFields = operationSettings?.additional_fields_config ?? [];
      const extraFields: Record<string, unknown> = {
        ...(vars.line.extra_fields ?? {}),
      };
      additionalFields
        .filter((f) => f.is_active && f.visible)
        .forEach((f) => {
          const raw = String(vars.form.extra_fields[f.key] ?? '').trim();
          if (raw === '') {
            extraFields[f.key] = null;
          } else if (f.field_type === 'number') {
            const parsed = Number(raw);
            extraFields[f.key] = Number.isNaN(parsed) ? null : parsed;
          } else {
            extraFields[f.key] = raw;
          }
        });
      const payload: CreateProductionReportLinePayload = {
        line_order: vars.line.line_order,
        product_id: vars.line.product_id,
        free_product_ref: vars.form.free_product_ref.trim() || null,
        customer_number: vars.form.customer_number.trim() || null,
        customer_order_number: vars.form.customer_order_number.trim() || null,
        manufacturing_order_number: vars.form.manufacturing_order_number.trim() || null,
        quantity_value: vars.form.quantity_value.trim() ? Number(vars.form.quantity_value) : null,
        actual_quantity_value: vars.form.actual_quantity_value.trim()
          ? Number(vars.form.actual_quantity_value)
          : null,
        continuation_status: vars.line.continuation_status,
        continued_from_line_id: vars.line.continued_from_line_id,
        quantity_unit: vars.line.quantity_unit,
        speed_value: vars.form.speed_value.trim() ? Number(vars.form.speed_value) : null,
        start_time: vars.form.start_time || null,
        end_time: vars.form.end_time || null,
        duration_minutes: calculateDurationMinutesFromTimes(
          vars.form.start_time,
          vars.form.end_time,
        ),
        notes: vars.form.notes.trim() || null,
        extra_fields: extraFields,
      };
      return updateIndustrialCostProductionReportLine(item.report_id, vars.line.id, payload);
    },
    onSuccess: () => {
      enqueueSnackbar('Orden actualizada correctamente.', { variant: 'success' });
      setEditingLine(null);
      onDetailRefresh();
    },
    onError: (error: unknown) => {
      const msg =
        typeof error === 'object' && error !== null
          ? ((error as { response?: { data?: { detail?: string } }; message?: string })
              .response?.data?.detail ??
            (error as { message?: string }).message ??
            'Error al actualizar la orden.')
          : 'Error al actualizar la orden.';
      enqueueSnackbar(msg, { variant: 'error' });
    },
  });

  const updateStopMutation = useMutation({
    mutationFn: (vars: { stop: ProductionReportStop; form: ReviewStopForm }) => {
      if (!item?.report_id) throw new Error('Sin parte seleccionado.');
      const payload: CreateProductionReportStopPayload = {
        line_id: vars.stop.line_id,
        start_time: vars.form.start_time,
        end_time: vars.form.end_time || null,
        duration_minutes: calculateDurationMinutesFromTimes(
          vars.form.start_time,
          vars.form.end_time,
        ),
        reason_id: vars.form.reason_id || null,
        notes: vars.form.notes.trim() || null,
      };
      return updateIndustrialCostProductionReportStop(item.report_id, vars.stop.id, payload);
    },
    onSuccess: () => {
      enqueueSnackbar('Parada actualizada correctamente.', { variant: 'success' });
      setEditingStop(null);
      onDetailRefresh();
    },
    onError: (error: unknown) => {
      const msg =
        typeof error === 'object' && error !== null
          ? ((error as { response?: { data?: { detail?: string } }; message?: string })
              .response?.data?.detail ??
            (error as { message?: string }).message ??
            'Error al actualizar la parada.')
          : 'Error al actualizar la parada.';
      enqueueSnackbar(msg, { variant: 'error' });
    },
  });

  const createStopMutation = useMutation({
    mutationFn: (form: ReviewStopForm) => {
      if (!item?.report_id) throw new Error('Sin parte seleccionado.');
      const payload: CreateProductionReportStopPayload = {
        line_id: null,
        start_time: form.start_time,
        end_time: form.end_time || null,
        duration_minutes: calculateDurationMinutesFromTimes(form.start_time, form.end_time),
        reason_id: form.reason_id || null,
        notes: form.notes.trim() || null,
      };
      return createIndustrialCostProductionReportStop(item.report_id, payload);
    },
    onSuccess: () => {
      enqueueSnackbar('Parada registrada correctamente.', { variant: 'success' });
      setCreatingStop(false);
      setNewStopForm(buildEmptyReviewStopForm());
      onDetailRefresh();
    },
    onError: (error: unknown) => {
      const msg =
        typeof error === 'object' && error !== null
          ? ((error as { response?: { data?: { detail?: string } }; message?: string })
              .response?.data?.detail ??
            (error as { message?: string }).message ??
            'Error al registrar la parada.')
          : 'Error al registrar la parada.';
      enqueueSnackbar(msg, { variant: 'error' });
    },
  });

  const complementaryWarnings = useMemo(() => {
    if (!detail) {
      return [];
    }

    const warnings: Array<{
      title: string;
      description: string;
      lineId?: string;
    }> = [];

    if (allLines.length === 0) {
      warnings.push({
        title: 'No hay órdenes registradas',
        description: 'Este parte no tiene líneas de producción registradas.',
      });
    }

    allLines.forEach((line) => {
      if (line.actual_quantity_value === null || line.actual_quantity_value === undefined) {
        warnings.push({
          title: `Cantidad real pendiente · OF ${line.manufacturing_order_number || '—'}`,
          description: 'La orden no tiene cantidad real informada en el cierre.',
          lineId: line.id,
        });
      }
    });

    return warnings;
  }, [allLines, detail]);

  const precheckIssues = useMemo<ReviewPrecheckIssue[]>(() => {
    if (!item || !detail) {
      return [];
    }

    const issues: ReviewPrecheckIssue[] = [];
    const shiftWindow = getShiftWindowInMinutes(item.shift);

    allLines.forEach((line) => {
      if (!line.end_time) {
        issues.push({
          severity: 'error',
          title: `Orden abierta · OF ${line.manufacturing_order_number || '—'}`,
          description: 'La orden sigue sin hora de fin.',
          lineId: line.id,
          actions: ['edit_line_times'],
        });
      }

      const start = normalizeShiftTimeInMinutes(line.start_time, item.shift);
      const end = normalizeShiftTimeInMinutes(line.end_time, item.shift);

      if (start !== null && end !== null && end < start) {
        issues.push({
          severity: 'error',
          title: `Tramo incoherente · OF ${line.manufacturing_order_number || '—'}`,
          description: `La hora fin (${line.end_time}) es anterior a la hora inicio (${line.start_time}).`,
          lineId: line.id,
          actions: ['edit_line_times'],
        });
      }
    });

    allStops.forEach((stop) => {
      if (!stop.end_time) {
        issues.push({
          severity: 'error',
          title: `Parada abierta · ${getStopLabel(stop)}`,
          description: 'La parada sigue sin hora de fin.',
          stopId: stop.id,
          actions: ['edit_stop_times'],
        });
      }

      const start = normalizeShiftTimeInMinutes(stop.start_time, item.shift);
      const end = normalizeShiftTimeInMinutes(stop.end_time, item.shift);

      if (start !== null && end !== null && end < start) {
        issues.push({
          severity: 'error',
          title: `Parada con horario incoherente · ${getStopLabel(stop)}`,
          description: `La hora fin (${stop.end_time}) es anterior a la hora inicio (${stop.start_time}).`,
          stopId: stop.id,
          actions: ['edit_stop_times'],
        });
      }
    });

    type ReviewTimelineEvent =
      | { id: string; type: 'line'; label: string; start: number; end: number }
      | { id: string; type: 'stop'; label: string; start: number; end: number }
      | { id: string; type: 'idle'; label: string; start: number; end: number };

    const lineEvents: ReviewTimelineEvent[] = allLines.flatMap((line) => {
      const start = normalizeShiftTimeInMinutes(line.start_time, item.shift);
      const end = normalizeShiftTimeInMinutes(line.end_time, item.shift);

      if (start === null || end === null) {
        return [];
      }

      return [
        {
          id: line.id,
          type: 'line' as const,
          label: `OF ${line.manufacturing_order_number || '—'}`,
          start,
          end,
        },
      ];
    });

    const stopEvents: ReviewTimelineEvent[] = allStops.flatMap((stop) => {
      const start = normalizeShiftTimeInMinutes(stop.start_time, item.shift);
      const end = normalizeShiftTimeInMinutes(stop.end_time, item.shift);

      if (start === null || end === null) {
        return [];
      }

      return [
        {
          id: stop.id,
          type: 'stop' as const,
          label: getStopLabel(stop),
          start,
          end,
        },
      ];
    });

    const idleEvents: ReviewTimelineEvent[] = allIdlePeriods.flatMap((idlePeriod) => {
      const start = normalizeShiftTimeInMinutes(idlePeriod.start_time, item.shift);
      const end = normalizeShiftTimeInMinutes(idlePeriod.end_time, item.shift);

      if (start === null || end === null) {
        return [];
      }

      return [
        {
          id: idlePeriod.id,
          type: 'idle' as const,
          label: 'Sin orden planificada',
          start,
          end,
        },
      ];
    });

    const completeEvents: ReviewTimelineEvent[] = [
      ...lineEvents,
      ...stopEvents,
      ...idleEvents,
    ].sort((a, b) => a.start - b.start);

    if (shiftWindow && completeEvents.length > 0) {
      const firstEvent = completeEvents[0];
      const lastEvent = completeEvents[completeEvents.length - 1];

      if (firstEvent.start > shiftWindow.start) {
        issues.push({
          severity: 'warning',
          title: 'Hueco al inicio del turno',
          description: `No hay registro entre ${shiftWindow.startLabel} y ${formatMinutesAsClock(firstEvent.start)}.`,
          actions: ['create_stop', 'edit_next_start'],
          gapStart: shiftWindow.startLabel,
          gapEnd: formatMinutesAsClock(firstEvent.start),
          nextLineId: firstEvent.type === 'line' ? firstEvent.id : undefined,
          nextStopId: firstEvent.type === 'stop' ? firstEvent.id : undefined,
          nextIdlePeriodId: firstEvent.type === 'idle' ? firstEvent.id : undefined,
        });
      }

      if (lastEvent.end < shiftWindow.end) {
        issues.push({
          severity: 'warning',
          title: 'Hueco al final del turno',
          description: `No hay registro entre ${formatMinutesAsClock(lastEvent.end)} y ${shiftWindow.endLabel}.`,
          actions: ['create_stop', 'edit_previous_end'],
          gapStart: formatMinutesAsClock(lastEvent.end),
          gapEnd: shiftWindow.endLabel,
          prevLineId: lastEvent.type === 'line' ? lastEvent.id : undefined,
          prevStopId: lastEvent.type === 'stop' ? lastEvent.id : undefined,
          prevIdlePeriodId: lastEvent.type === 'idle' ? lastEvent.id : undefined,
        });
      }
    }

    for (let index = 1; index < completeEvents.length; index += 1) {
      const previousEvent = completeEvents[index - 1];
      const currentEvent = completeEvents[index];

      if (currentEvent.start > previousEvent.end) {
        issues.push({
          severity: 'warning',
          title: 'Hueco entre eventos',
          description: `Hay un vacío entre ${formatMinutesAsClock(previousEvent.end)} y ${formatMinutesAsClock(currentEvent.start)}.`,
          actions: ['create_stop'],
          gapStart: formatMinutesAsClock(previousEvent.end),
          gapEnd: formatMinutesAsClock(currentEvent.start),
          prevLineId: previousEvent.type === 'line' ? previousEvent.id : undefined,
          prevStopId: previousEvent.type === 'stop' ? previousEvent.id : undefined,
          prevIdlePeriodId: previousEvent.type === 'idle' ? previousEvent.id : undefined,
          nextLineId: currentEvent.type === 'line' ? currentEvent.id : undefined,
          nextStopId: currentEvent.type === 'stop' ? currentEvent.id : undefined,
          nextIdlePeriodId: currentEvent.type === 'idle' ? currentEvent.id : undefined,
        });
      }

      if (currentEvent.start < previousEvent.end) {
        issues.push({
          severity: 'error',
          title: 'Solape entre eventos',
          description: `${previousEvent.label} y ${currentEvent.label} se solapan entre ${formatMinutesAsClock(currentEvent.start)} y ${formatMinutesAsClock(previousEvent.end)}.`,
          actions: ['edit_previous_end', 'edit_next_start'],
          prevLineId: previousEvent.type === 'line' ? previousEvent.id : undefined,
          prevStopId: previousEvent.type === 'stop' ? previousEvent.id : undefined,
          prevIdlePeriodId: previousEvent.type === 'idle' ? previousEvent.id : undefined,
          nextLineId: currentEvent.type === 'line' ? currentEvent.id : undefined,
          nextStopId: currentEvent.type === 'stop' ? currentEvent.id : undefined,
          nextIdlePeriodId: currentEvent.type === 'idle' ? currentEvent.id : undefined,
        });
      }
    }

    return issues;
  }, [allIdlePeriods, allLines, allStops, detail, item]);

  return (
    <Dialog
      open={Boolean(item)}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      PaperProps={{
        sx: {
          borderRadius: 4,
          bgcolor: '#020617',
          border: '1px solid rgba(148,163,184,0.16)',
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: '1px solid rgba(148,163,184,0.14)',
          px: { xs: 2.5, md: 3 },
          py: 2,
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          spacing={1.5}
        >
          <Stack spacing={0.45}>
            <Typography sx={{ color: '#f8fafc', fontWeight: 950, fontSize: '1.1rem' }}>
              Revisión administrativa del parte
            </Typography>
            <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
              {item
                ? `${item.report_date} · ${getShiftLabel(item.shift)} · ${item.line_or_center || 'Sin línea'}`
                : 'Sin parte seleccionado'}
            </Typography>
          </Stack>

          {item && tone ? (
            <Chip
              label={getReviewStatusLabel(item.review_status)}
              variant="outlined"
              sx={{
                alignSelf: { xs: 'flex-start', md: 'center' },
                color: tone.color,
                borderColor: tone.border,
                bgcolor: tone.bg,
                fontWeight: 900,
              }}
            />
          ) : null}
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 2.5, md: 3 }, py: 2.5 }}>
        {loading ? (
          <Stack spacing={2}>
            <LinearProgress />
            <Typography sx={{ color: '#94a3b8' }}>
              Cargando información completa del parte...
            </Typography>
          </Stack>
        ) : !item ? (
          <Alert severity="info" variant="outlined">
            No hay parte seleccionado para revisar.
          </Alert>
        ) : !detail ? (
          <Alert severity="error" variant="outlined">
            No se pudo cargar el detalle del parte.
          </Alert>
        ) : (
          <Stack spacing={2.25}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'repeat(4, minmax(0, 1fr))',
                },
                gap: 1.25,
              }}
            >
              {[
                {
                  label: 'Fecha',
                  value: item.report_date,
                },
                {
                  label: 'Turno',
                  value: getShiftLabel(item.shift),
                },
                {
                  label: 'Operario',
                  value: item.operator_number || '—',
                },
                {
                  label: 'Estado parte',
                  value: getReportStatusLabel(item.report_status),
                },
              ].map((summary) => (
                <Paper
                  key={summary.label}
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid rgba(148,163,184,0.14)',
                    bgcolor: 'rgba(15,23,42,0.46)',
                    px: 1.5,
                    py: 1.35,
                  }}
                >
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.74rem', fontWeight: 850 }}>
                    {summary.label}
                  </Typography>
                  <Typography sx={{ color: '#f8fafc', fontSize: '1rem', fontWeight: 900 }}>
                    {summary.value}
                  </Typography>
                </Paper>
              ))}
            </Box>

            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid rgba(148,163,184,0.14)',
                bgcolor: 'rgba(15,23,42,0.46)',
                p: 2,
              }}
            >
              <Stack spacing={1}>
                <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                  Motivo administrativo
                </Typography>
                <Typography sx={{ color: '#cbd5e1', fontSize: '0.92rem' }}>
                  {item.review_reasons.length > 0
                    ? item.review_reasons.join(' · ')
                    : 'Sin observaciones administrativas'}
                </Typography>
              </Stack>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: timelineValidationIssue
                  ? '1px solid rgba(248,113,113,0.28)'
                  : '1px solid rgba(148,163,184,0.18)',
                bgcolor: timelineValidationIssue
                  ? 'rgba(127,29,29,0.14)'
                  : 'rgba(15,23,42,0.32)',
                p: 2,
              }}
            >
              <Stack spacing={1.25}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  justifyContent="space-between"
                  spacing={1}
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                >
                  <Stack spacing={0.35}>
                    <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                      Estado de validación del parte
                    </Typography>
                    <Typography sx={{ color: '#94a3b8', fontSize: '0.88rem' }}>
                      {timelineValidationIssue
                        ? 'El backend ha detectado incidencias reales que deben resolverse antes de validar.'
                        : 'Todavía no se ha intentado validar o revalidar este parte desde revisión.'}
                    </Typography>
                  </Stack>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    {timelineValidationIssue ? (
                      <Chip
                        label="Incidencias detectadas"
                        variant="outlined"
                        sx={{
                          color: '#fecaca',
                          borderColor: 'rgba(248,113,113,0.28)',
                          bgcolor: 'rgba(127,29,29,0.18)',
                          fontWeight: 900,
                        }}
                      />
                    ) : (
                      <Chip
                        label="Pendiente de comprobar"
                        variant="outlined"
                        sx={{
                          color: '#cbd5e1',
                          borderColor: 'rgba(148,163,184,0.24)',
                          bgcolor: 'rgba(51,65,85,0.18)',
                          fontWeight: 900,
                        }}
                      />
                    )}

                    <Button
                      variant="contained"
                      onClick={onValidate}
                      disabled={validationPending}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 900,
                      }}
                    >
                      {validationPending
                        ? 'Comprobando...'
                        : item.review_status === 'pending_revalidation'
                          ? 'Revalidar'
                          : 'Validar'}
                    </Button>
                  </Stack>
                </Stack>

                {timelineValidationIssue ? (
                  <Stack spacing={1.5}>
                    <Alert severity="warning" variant="outlined">
                      {timelineValidationIssue.message}
                    </Alert>

                    <Paper
                      elevation={0}
                      sx={{
                        borderRadius: 3,
                        border: '1px solid rgba(148,163,184,0.18)',
                        bgcolor: 'rgba(2,6,23,0.28)',
                        px: 2,
                        py: 1.5,
                      }}
                    >
                      <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
                        Ventana del turno
                      </Typography>
                      <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                        {timelineValidationIssue.shift_window.start_time} -{' '}
                        {timelineValidationIssue.shift_window.end_time}
                      </Typography>
                    </Paper>

                    {(timelineValidationIssue.incomplete_events ?? []).length > 0 ? (
                      <Stack spacing={1.1}>
                        <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
                          Eventos sin cerrar
                        </Typography>

                        {timelineValidationIssue.incomplete_events.map((event) => (
                          <Paper
                            key={`${event.event_type}-${event.event_id}`}
                            elevation={0}
                            sx={{
                              borderRadius: 2.5,
                              border: '1px solid rgba(248,113,113,0.22)',
                              bgcolor: 'rgba(127,29,29,0.14)',
                              px: 2,
                              py: 1.35,
                            }}
                          >
                            <Stack spacing={0.55}>
                              <Typography sx={{ color: '#f8fafc', fontWeight: 800 }}>
                                {event.label}
                              </Typography>
                              <Typography sx={{ color: '#fecaca', fontSize: '0.9rem' }}>
                                Inicio {event.start_time || '—'} · Fin {event.end_time || '—'}
                              </Typography>
                              <Typography sx={{ color: '#94a3b8', fontSize: '0.82rem' }}>
                                En el siguiente paso conectaremos aquí la misma resolución guiada que ya usa operario.
                              </Typography>
                            </Stack>
                          </Paper>
                        ))}
                      </Stack>
                    ) : null}

                    {(timelineValidationIssue.gaps ?? []).length > 0 ? (
                      <Stack spacing={1.1}>
                        <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
                          Huecos sin registrar
                        </Typography>

                        {timelineValidationIssue.gaps.map((gap, index) => (
                          <Paper
                            key={`${gap.start_time}-${gap.end_time}-${index}`}
                            elevation={0}
                            sx={{
                              borderRadius: 2.5,
                              border: '1px solid rgba(245,158,11,0.22)',
                              bgcolor: 'rgba(120,53,15,0.16)',
                              px: 2,
                              py: 1.35,
                            }}
                          >
                            <Stack spacing={0.55}>
                              <Typography sx={{ color: '#f8fafc', fontWeight: 800 }}>
                                {gap.start_time} - {gap.end_time}
                              </Typography>
                              <Typography sx={{ color: '#fde68a', fontSize: '0.9rem' }}>
                                {gap.position === 'start_of_shift'
                                  ? 'Hueco al inicio del turno.'
                                  : gap.position === 'end_of_shift'
                                    ? 'Hueco al final del turno.'
                                    : 'Hueco entre actividades.'}
                              </Typography>
                              <Typography sx={{ color: '#cbd5e1', fontSize: '0.88rem' }}>
                                {gap.previous_event
                                  ? `Anterior: ${gap.previous_event.label} (${gap.previous_event.start_time || '—'} - ${gap.previous_event.end_time || '—'})`
                                  : 'Anterior: —'}
                              </Typography>
                              <Typography sx={{ color: '#cbd5e1', fontSize: '0.88rem' }}>
                                {gap.next_event
                                  ? `Siguiente: ${gap.next_event.label} (${gap.next_event.start_time || '—'} - ${gap.next_event.end_time || '—'})`
                                  : 'Siguiente: —'}
                              </Typography>
                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {gap.allowed_actions.map((action) => (
                                  <Chip
                                    key={action}
                                    label={getTimelineGapActionLabel(action)}
                                    variant="outlined"
                                    sx={{
                                      color: '#fde68a',
                                      borderColor: 'rgba(245,158,11,0.22)',
                                      bgcolor: 'rgba(120,53,15,0.18)',
                                      fontWeight: 800,
                                    }}
                                  />
                                ))}
                              </Stack>
                            </Stack>
                          </Paper>
                        ))}
                      </Stack>
                    ) : null}

                    {(timelineValidationIssue.overlaps ?? []).length > 0 ? (
                      <Stack spacing={1.1}>
                        <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
                          Solapes a revisar
                        </Typography>

                        {timelineValidationIssue.overlaps.map((overlap, index) => (
                          <Paper
                            key={`${overlap.start_time}-${overlap.end_time}-${index}`}
                            elevation={0}
                            sx={{
                              borderRadius: 2.5,
                              border: '1px solid rgba(244,114,182,0.22)',
                              bgcolor: 'rgba(131,24,67,0.14)',
                              px: 2,
                              py: 1.35,
                            }}
                          >
                            <Stack spacing={0.55}>
                              <Typography sx={{ color: '#f8fafc', fontWeight: 800 }}>
                                {overlap.start_time} - {overlap.end_time}
                              </Typography>
                              <Typography sx={{ color: '#fbcfe8', fontSize: '0.88rem' }}>
                                {overlap.previous_event
                                  ? `Anterior: ${overlap.previous_event.label} (${overlap.previous_event.start_time || '—'} - ${overlap.previous_event.end_time || '—'})`
                                  : 'Anterior: —'}
                              </Typography>
                              <Typography sx={{ color: '#fbcfe8', fontSize: '0.88rem' }}>
                                {overlap.next_event
                                  ? `Siguiente: ${overlap.next_event.label} (${overlap.next_event.start_time || '—'} - ${overlap.next_event.end_time || '—'})`
                                  : 'Siguiente: —'}
                              </Typography>
                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {overlap.allowed_actions.map((action) => (
                                  <Chip
                                    key={action}
                                    label={getTimelineOverlapActionLabel(action)}
                                    variant="outlined"
                                    sx={{
                                      color: '#fbcfe8',
                                      borderColor: 'rgba(244,114,182,0.22)',
                                      bgcolor: 'rgba(131,24,67,0.18)',
                                      fontWeight: 800,
                                    }}
                                  />
                                ))}
                              </Stack>
                            </Stack>
                          </Paper>
                        ))}
                      </Stack>
                    ) : null}
                  </Stack>
                ) : precheckIssues.length > 0 ? (
                  <Stack spacing={1}>
                    <Alert severity="warning" variant="outlined">
                      Se han detectado incidencias previas. Resuélvelas antes de validar.
                    </Alert>

                    {precheckIssues.map((issue, index) => {
                      const lineMatch = issue.lineId
                        ? allLines.find((l) => l.id === issue.lineId) ?? null
                        : null;
                      const stopMatch = issue.stopId
                        ? allStops.find((s) => s.id === issue.stopId) ?? null
                        : null;

                      return (
                        <Paper
                          key={`${issue.title}-${index}`}
                          elevation={0}
                          sx={{
                            borderRadius: 2.5,
                            border:
                              issue.severity === 'error'
                                ? '1px solid rgba(248,113,113,0.28)'
                                : '1px solid rgba(245,158,11,0.22)',
                            bgcolor:
                              issue.severity === 'error'
                                ? 'rgba(127,29,29,0.14)'
                                : 'rgba(120,53,15,0.12)',
                            px: 2,
                            py: 1.35,
                          }}
                        >
                          <Stack spacing={0.75}>
                            <Typography
                              sx={{
                                color: issue.severity === 'error' ? '#fecaca' : '#fde68a',
                                fontWeight: 900,
                                fontSize: '0.9rem',
                              }}
                            >
                              {issue.title}
                            </Typography>
                            <Typography sx={{ color: '#cbd5e1', fontSize: '0.86rem' }}>
                              {issue.description}
                            </Typography>

                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                              {/* Orden abierta o tramo incoherente: editar tiempos */}
                              {issue.actions?.includes('edit_line_times') && lineMatch ? (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  sx={{ borderRadius: 1.5, textTransform: 'none', fontSize: '0.8rem' }}
                                  onClick={() => openEditLine(lineMatch)}
                                >
                                  Editar tiempos de la orden
                                </Button>
                              ) : null}

                              {/* Cantidad real pendiente */}
                              {issue.actions?.includes('complete_actual_quantity') && lineMatch ? (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  sx={{ borderRadius: 1.5, textTransform: 'none', fontSize: '0.8rem' }}
                                  onClick={() =>
                                    openEditLine(lineMatch, { actual_quantity_value: '' })
                                  }
                                >
                                  Completar cantidad real
                                </Button>
                              ) : null}

                              {/* Parada abierta o incoherente: editar tiempos */}
                              {issue.actions?.includes('edit_stop_times') && stopMatch ? (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  sx={{ borderRadius: 1.5, textTransform: 'none', fontSize: '0.8rem' }}
                                  onClick={() => openEditStop(stopMatch)}
                                >
                                  Editar tiempos de la parada
                                </Button>
                              ) : null}

                              {/* Solape: editar fin de la anterior */}
                              {issue.actions?.includes('edit_previous_end') ? (() => {
                                const prevLine = issue.prevLineId
                                  ? allLines.find((l) => l.id === issue.prevLineId) ?? null
                                  : null;
                                const prevStop = issue.prevStopId
                                  ? allStops.find((s) => s.id === issue.prevStopId) ?? null
                                  : null;
                                if (prevLine) {
                                  return (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      sx={{ borderRadius: 1.5, textTransform: 'none', fontSize: '0.8rem' }}
                                      onClick={() => openEditLine(prevLine)}
                                    >
                                      Editar fin de la anterior
                                    </Button>
                                  );
                                }
                                if (prevStop) {
                                  return (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      sx={{ borderRadius: 1.5, textTransform: 'none', fontSize: '0.8rem' }}
                                      onClick={() => openEditStop(prevStop)}
                                    >
                                      Editar fin de la anterior
                                    </Button>
                                  );
                                }
                                return null;
                              })() : null}

                              {/* Solape: editar inicio de la siguiente */}
                              {issue.actions?.includes('edit_next_start') ? (() => {
                                const nextLine = issue.nextLineId
                                  ? allLines.find((l) => l.id === issue.nextLineId) ?? null
                                  : null;
                                const nextStop = issue.nextStopId
                                  ? allStops.find((s) => s.id === issue.nextStopId) ?? null
                                  : null;
                                if (nextLine) {
                                  return (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      sx={{ borderRadius: 1.5, textTransform: 'none', fontSize: '0.8rem' }}
                                      onClick={() => openEditLine(nextLine)}
                                    >
                                      Editar inicio de la siguiente
                                    </Button>
                                  );
                                }
                                if (nextStop) {
                                  return (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      sx={{ borderRadius: 1.5, textTransform: 'none', fontSize: '0.8rem' }}
                                      onClick={() => openEditStop(nextStop)}
                                    >
                                      Editar inicio de la siguiente
                                    </Button>
                                  );
                                }
                                return null;
                              })() : null}

                              {/* Hueco: registrar parada */}
                              {issue.actions?.includes('create_stop') ? (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  sx={{ borderRadius: 1.5, textTransform: 'none', fontSize: '0.8rem' }}
                                  onClick={() =>
                                    openCreateStop(
                                      issue.gapStart && issue.gapEnd
                                        ? { start_time: issue.gapStart, end_time: issue.gapEnd }
                                        : undefined,
                                    )
                                  }
                                >
                                  Registrar parada
                                </Button>
                              ) : null}
                            </Stack>
                          </Stack>
                        </Paper>
                      );
                    })}
                  </Stack>
                ) : (
                  <Alert severity="info" variant="outlined">
                    No se han detectado incidencias previas visibles en esta revisión. Pulsa {item.review_status === 'pending_revalidation' ? 'Revalidar' : 'Validar'} para la comprobación final real.
                  </Alert>
                )}

                {complementaryWarnings.length > 0 ? (
                  <Stack spacing={1}>
                    <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
                      Advertencias complementarias
                    </Typography>

                    {complementaryWarnings.map((warning, index) => {
                      const warningLine = warning.lineId
                        ? allLines.find((l) => l.id === warning.lineId) ?? null
                        : null;
                      return (
                        <Paper
                          key={`${warning.title}-${index}`}
                          elevation={0}
                          sx={{
                            borderRadius: 2.5,
                            border: '1px solid rgba(245,158,11,0.22)',
                            bgcolor: 'rgba(120,53,15,0.12)',
                            px: 2,
                            py: 1.35,
                          }}
                        >
                          <Stack spacing={0.75}>
                            <Typography sx={{ color: '#fde68a', fontWeight: 900, fontSize: '0.9rem' }}>
                              {warning.title}
                            </Typography>
                            <Typography sx={{ color: '#cbd5e1', fontSize: '0.86rem' }}>
                              {warning.description}
                            </Typography>
                            {warningLine ? (
                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  sx={{ borderRadius: 1.5, textTransform: 'none', fontSize: '0.8rem' }}
                                  onClick={() =>
                                    openEditLine(warningLine, { actual_quantity_value: '' })
                                  }
                                >
                                  Completar cantidad real
                                </Button>
                              </Stack>
                            ) : null}
                          </Stack>
                        </Paper>
                      );
                    })}
                  </Stack>
                ) : null}
              </Stack>
            </Paper>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  lg: 'minmax(0, 1.8fr) minmax(320px, 0.9fr)',
                },
                gap: 2,
                alignItems: 'start',
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(56,189,248,0.18)',
                  bgcolor: 'rgba(15,23,42,0.42)',
                  p: 1.75,
                }}
              >
                <Stack spacing={1.5}>
                  <Stack spacing={0.25}>
                    <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                      Registro horario del parte
                    </Typography>
                    <Typography sx={{ color: '#94a3b8', fontSize: '0.86rem' }}>
                      Secuencia real del turno: órdenes, paradas y periodos sin orden planificada.
                    </Typography>
                  </Stack>

                  {reviewTimelineEvents.length === 0 ? (
                    <Alert severity="info" variant="outlined">
                      No hay eventos registrados en este parte.
                    </Alert>
                  ) : (
                    <Stack spacing={0.9}>
                      {reviewTimelineEvents.map((event) => {
                        const isLine = event.kind === 'line';
                        const isStop = event.kind === 'stop';
                        const isIdle = event.kind === 'idle';

                        return (
                          <Paper
                            key={`${event.kind}-${event.id}`}
                            elevation={0}
                            sx={{
                              borderRadius: 2.5,
                              border: isLine
                                ? '1px solid rgba(34,197,94,0.24)'
                                : isStop
                                  ? '1px solid rgba(248,113,113,0.24)'
                                  : '1px dashed rgba(148,163,184,0.30)',
                              bgcolor: isLine
                                ? 'rgba(20,83,45,0.12)'
                                : isStop
                                  ? 'rgba(127,29,29,0.12)'
                                  : 'rgba(30,41,59,0.18)',
                              px: 1.5,
                              py: 1.1,
                            }}
                          >
                            <Stack
                              direction={{ xs: 'column', sm: 'row' }}
                              justifyContent="space-between"
                              spacing={1}
                              alignItems={{ xs: 'flex-start', sm: 'center' }}
                            >
                              <Stack direction="row" spacing={1} alignItems="flex-start">
                                <Box
                                  sx={{
                                    width: 9,
                                    height: 9,
                                    borderRadius: '50%',
                                    bgcolor: isLine
                                      ? '#4ade80'
                                      : isStop
                                        ? '#f87171'
                                        : '#94a3b8',
                                    mt: 0.65,
                                    flexShrink: 0,
                                  }}
                                />

                                <Stack spacing={0.18}>
                                  <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                                    <Chip
                                      size="small"
                                      label={isLine ? 'Orden' : isStop ? 'Parada' : 'Sin orden'}
                                      sx={{
                                        height: 20,
                                        bgcolor: isLine
                                          ? 'rgba(34,197,94,0.14)'
                                          : isStop
                                            ? 'rgba(248,113,113,0.14)'
                                            : 'rgba(148,163,184,0.12)',
                                        color: isLine
                                          ? '#86efac'
                                          : isStop
                                            ? '#fecaca'
                                            : '#cbd5e1',
                                        border: isLine
                                          ? '1px solid rgba(34,197,94,0.22)'
                                          : isStop
                                            ? '1px solid rgba(248,113,113,0.22)'
                                            : '1px solid rgba(148,163,184,0.20)',
                                        fontWeight: 850,
                                        fontSize: '0.68rem',
                                      }}
                                    />

                                    <Typography
                                      sx={{
                                        color: isIdle ? '#cbd5e1' : '#f8fafc',
                                        fontWeight: 900,
                                        fontSize: '0.92rem',
                                      }}
                                    >
                                      {event.title}
                                    </Typography>
                                  </Stack>

                                  <Typography sx={{ color: '#94a3b8', fontSize: '0.82rem' }}>
                                    {event.start_time || '—'} – {event.end_time || 'Abierto'} ·{' '}
                                    {event.subtitle || 'Sin detalle'}
                                  </Typography>
                                </Stack>
                              </Stack>

                              <Chip
                                size="small"
                                label={
                                  event.duration_minutes === null ||
                                  event.duration_minutes === undefined
                                    ? 'Sin duración'
                                    : `${event.duration_minutes} min`
                                }
                                variant="outlined"
                                sx={{
                                  color: isLine
                                    ? '#86efac'
                                    : isStop
                                      ? '#fecaca'
                                      : '#cbd5e1',
                                  borderColor: isLine
                                    ? 'rgba(34,197,94,0.24)'
                                    : isStop
                                      ? 'rgba(248,113,113,0.24)'
                                      : 'rgba(148,163,184,0.24)',
                                  bgcolor: 'rgba(15,23,42,0.30)',
                                  fontWeight: 850,
                                  flexShrink: 0,
                                }}
                              />
                            </Stack>
                          </Paper>
                        );
                      })}
                    </Stack>
                  )}
                </Stack>
              </Paper>

              <Stack spacing={1.5}>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid rgba(148,163,184,0.16)',
                    bgcolor: 'rgba(15,23,42,0.42)',
                    p: 1.75,
                  }}
                >
                  <Stack spacing={1.25}>
                    <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                      Resumen administrativo
                    </Typography>

                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                        gap: 1,
                      }}
                    >
                      {[
                        { label: 'Órdenes', value: allLines.length },
                        { label: 'Paradas', value: allStops.length },
                        { label: 'Sin orden', value: allIdlePeriods.length },
                        { label: 'Controles', value: qualitySummary.total },
                      ].map((summaryItem) => (
                        <Paper
                          key={summaryItem.label}
                          elevation={0}
                          sx={{
                            borderRadius: 2,
                            border: '1px solid rgba(148,163,184,0.12)',
                            bgcolor: 'rgba(2,6,23,0.24)',
                            px: 1.25,
                            py: 1,
                          }}
                        >
                          <Typography sx={{ color: '#64748b', fontSize: '0.72rem', fontWeight: 850 }}>
                            {summaryItem.label}
                          </Typography>
                          <Typography sx={{ color: '#f8fafc', fontWeight: 950 }}>
                            {summaryItem.value}
                          </Typography>
                        </Paper>
                      ))}
                    </Box>
                  </Stack>
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: qualitySummary.nonConforming > 0
                      ? '1px solid rgba(248,113,113,0.28)'
                      : '1px solid rgba(34,197,94,0.22)',
                    bgcolor: qualitySummary.nonConforming > 0
                      ? 'rgba(127,29,29,0.12)'
                      : 'rgba(20,83,45,0.10)',
                    p: 1.75,
                  }}
                >
                  <Stack spacing={1}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      spacing={1}
                    >
                      <Stack spacing={0.2}>
                        <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                          Calidad
                        </Typography>
                        <Typography sx={{ color: '#94a3b8', fontSize: '0.82rem' }}>
                          Resumen de controles registrados en el parte.
                        </Typography>
                      </Stack>

                      <Chip
                        size="small"
                        label={
                          qualitySummary.nonConforming > 0
                            ? `${qualitySummary.nonConforming} NC`
                            : 'Sin NC'
                        }
                        color={qualitySummary.nonConforming > 0 ? 'error' : 'success'}
                        variant="outlined"
                        sx={{ fontWeight: 850 }}
                      />
                    </Stack>

                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                        gap: 0.75,
                      }}
                    >
                      {[
                        { label: 'C', value: qualitySummary.conforming, color: '#86efac' },
                        { label: 'NC', value: qualitySummary.nonConforming, color: '#fca5a5' },
                        { label: 'Saltados', value: qualitySummary.skipped, color: '#cbd5e1' },
                      ].map((qualityItem) => (
                        <Paper
                          key={qualityItem.label}
                          elevation={0}
                          sx={{
                            borderRadius: 2,
                            border: '1px solid rgba(148,163,184,0.12)',
                            bgcolor: 'rgba(2,6,23,0.24)',
                            px: 1,
                            py: 0.9,
                          }}
                        >
                          <Typography sx={{ color: '#64748b', fontSize: '0.68rem', fontWeight: 850 }}>
                            {qualityItem.label}
                          </Typography>
                          <Typography sx={{ color: qualityItem.color, fontWeight: 950 }}>
                            {qualityItem.value}
                          </Typography>
                        </Paper>
                      ))}
                    </Box>

                    <Typography sx={{ color: '#64748b', fontSize: '0.74rem' }}>
                      El detalle control a control queda fuera de esta revisión rápida para no saturar la vista.
                    </Typography>
                  </Stack>
                </Paper>
              </Stack>
            </Box>

            <Alert severity="info" variant="outlined">
              Esta revisión ya usa el mismo diagnóstico real de validación que el puesto operario.
              El siguiente paso será conectar aquí las mismas acciones guiadas para resolver huecos,
              solapes y cierres pendientes sin salir de este modal.
            </Alert>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2.5, md: 3 }, py: 2, justifyContent: 'space-between' }}>
        <Button
          color="error"
          variant="outlined"
          onClick={() => setConfirmDeleteOpen(true)}
          sx={{ textTransform: 'none' }}
        >
          Eliminar parte
        </Button>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>

      {/* Dialog de edición completa de línea */}
      <Dialog
        open={Boolean(editingLine)}
        onClose={() => setEditingLine(null)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 3, bgcolor: '#0f172a', border: '1px solid rgba(148,163,184,0.18)' } }}
      >
        <DialogTitle sx={{ color: '#f8fafc', fontWeight: 900 }}>
          Editar orden · OF {editingLine?.manufacturing_order_number || '—'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ pt: 1.5 }}>

            {/* Tiempos */}
            <Stack spacing={1}>
              <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Horario
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField
                  label="Hora inicio"
                  type="time"
                  fullWidth
                  value={lineForm.start_time}
                  onChange={(e) => setLineForm((f) => ({ ...f, start_time: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Hora fin"
                  type="time"
                  fullWidth
                  value={lineForm.end_time}
                  onChange={(e) => setLineForm((f) => ({ ...f, end_time: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>
            </Stack>

            {/* Identificación */}
            <Stack spacing={1}>
              <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Identificación
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField
                  label="Nº Orden de fabricación"
                  fullWidth
                  value={lineForm.manufacturing_order_number}
                  onChange={(e) =>
                    setLineForm((f) => ({ ...f, manufacturing_order_number: e.target.value }))
                  }
                />
                <TextField
                  label="Referencia producto"
                  fullWidth
                  value={lineForm.free_product_ref}
                  onChange={(e) =>
                    setLineForm((f) => ({ ...f, free_product_ref: e.target.value }))
                  }
                />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField
                  label="Cliente"
                  fullWidth
                  value={lineForm.customer_number}
                  onChange={(e) =>
                    setLineForm((f) => ({ ...f, customer_number: e.target.value }))
                  }
                />
                <TextField
                  label="Pedido cliente"
                  fullWidth
                  value={lineForm.customer_order_number}
                  onChange={(e) =>
                    setLineForm((f) => ({ ...f, customer_order_number: e.target.value }))
                  }
                />
              </Stack>
            </Stack>

            {/* Cantidades y velocidad */}
            <Stack spacing={1}>
              <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Producción
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField
                  label="Cantidad prevista"
                  type="number"
                  fullWidth
                  value={lineForm.quantity_value}
                  onChange={(e) =>
                    setLineForm((f) => ({ ...f, quantity_value: e.target.value }))
                  }
                  inputProps={{ min: 0, step: 'any' }}
                />
                <TextField
                  label="Cantidad real"
                  type="number"
                  fullWidth
                  value={lineForm.actual_quantity_value}
                  onChange={(e) =>
                    setLineForm((f) => ({ ...f, actual_quantity_value: e.target.value }))
                  }
                  inputProps={{ min: 0, step: 'any' }}
                />
                <TextField
                  label="Velocidad"
                  type="number"
                  fullWidth
                  value={lineForm.speed_value}
                  onChange={(e) =>
                    setLineForm((f) => ({ ...f, speed_value: e.target.value }))
                  }
                  inputProps={{ min: 0, step: 'any' }}
                />
              </Stack>
            </Stack>

            {/* Campos adicionales configurables */}
            {(() => {
              const activeAdditionalFields = (
                operationSettings?.additional_fields_config ?? []
              ).filter((f) => f.is_active && f.visible);
              if (activeAdditionalFields.length === 0) return null;
              return (
                <Stack spacing={1}>
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Campos adicionales
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={1.5}>
                    {activeAdditionalFields.map((field) => (
                      <TextField
                        key={field.key}
                        label={field.label}
                        type={field.field_type === 'number' ? 'number' : 'text'}
                        value={lineForm.extra_fields[field.key] ?? ''}
                        onChange={(e) =>
                          setLineForm((f) => ({
                            ...f,
                            extra_fields: { ...f.extra_fields, [field.key]: e.target.value },
                          }))
                        }
                        inputProps={field.field_type === 'number' ? { step: 'any' } : undefined}
                        sx={{ minWidth: 180, flex: '1 1 180px' }}
                      />
                    ))}
                  </Stack>
                </Stack>
              );
            })()}

            {/* Observaciones */}
            <TextField
              label="Observaciones"
              fullWidth
              multiline
              rows={2}
              value={lineForm.notes}
              onChange={(e) => setLineForm((f) => ({ ...f, notes: e.target.value }))}
            />

          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingLine(null)}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={updateLineMutation.isPending}
            onClick={() => {
              if (editingLine) {
                updateLineMutation.mutate({ line: editingLine, form: lineForm });
              }
            }}
          >
            {updateLineMutation.isPending ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de edición de parada */}
      <Dialog
        open={Boolean(editingStop)}
        onClose={() => setEditingStop(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3, bgcolor: '#0f172a', border: '1px solid rgba(148,163,184,0.18)' } }}
      >
        <DialogTitle sx={{ color: '#f8fafc', fontWeight: 900 }}>
          Editar parada · {editingStop ? getStopLabel(editingStop) : '—'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                label="Hora inicio"
                type="time"
                fullWidth
                value={stopForm.start_time}
                onChange={(e) => setStopForm((f) => ({ ...f, start_time: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Hora fin"
                type="time"
                fullWidth
                value={stopForm.end_time}
                onChange={(e) => setStopForm((f) => ({ ...f, end_time: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
            <TextField
              select
              label="Motivo"
              fullWidth
              value={stopForm.reason_id}
              onChange={(e) => setStopForm((f) => ({ ...f, reason_id: e.target.value }))}
            >
              <MenuItem value="">Sin motivo</MenuItem>
              {stopReasons.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Observaciones"
              fullWidth
              multiline
              rows={2}
              value={stopForm.notes}
              onChange={(e) => setStopForm((f) => ({ ...f, notes: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingStop(null)}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={updateStopMutation.isPending}
            onClick={() => {
              if (editingStop) {
                updateStopMutation.mutate({ stop: editingStop, form: stopForm });
              }
            }}
          >
            {updateStopMutation.isPending ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de nueva parada (para huecos) */}
      <Dialog
        open={creatingStop}
        onClose={() => setCreatingStop(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3, bgcolor: '#0f172a', border: '1px solid rgba(148,163,184,0.18)' } }}
      >
        <DialogTitle sx={{ color: '#f8fafc', fontWeight: 900 }}>
          Registrar parada
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                label="Hora inicio"
                type="time"
                fullWidth
                value={newStopForm.start_time}
                onChange={(e) => setNewStopForm((f) => ({ ...f, start_time: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Hora fin"
                type="time"
                fullWidth
                value={newStopForm.end_time}
                onChange={(e) => setNewStopForm((f) => ({ ...f, end_time: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
            <TextField
              select
              label="Motivo"
              fullWidth
              value={newStopForm.reason_id}
              onChange={(e) => setNewStopForm((f) => ({ ...f, reason_id: e.target.value }))}
            >
              <MenuItem value="">Sin motivo</MenuItem>
              {stopReasons.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Observaciones"
              fullWidth
              multiline
              rows={2}
              value={newStopForm.notes}
              onChange={(e) => setNewStopForm((f) => ({ ...f, notes: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreatingStop(false)}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={createStopMutation.isPending}
            onClick={() => createStopMutation.mutate(newStopForm)}
          >
            {createStopMutation.isPending ? 'Guardando...' : 'Registrar parada'}
          </Button>
        </DialogActions>
      </Dialog>
    {/* Dialog de confirmación de eliminación */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, bgcolor: '#0f172a', border: '1px solid rgba(248,113,113,0.3)' } }}
      >
        <DialogTitle sx={{ color: '#fca5a5', fontWeight: 900 }}>
          Eliminar parte permanentemente
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <Alert severity="error" variant="outlined">
              Esta acción es irreversible. Se eliminarán el parte y todos sus datos asociados: órdenes, paradas, calidad y auditoría.
            </Alert>
            <Typography sx={{ color: '#cbd5e1', fontSize: '0.9rem' }}>
              Parte: <strong>{item?.shift} · {item?.report_date} · {item?.line_or_center}</strong>
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Cancelar</Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteReportMutation.isPending}
            onClick={() => deleteReportMutation.mutate()}
          >
            {deleteReportMutation.isPending ? 'Eliminando...' : 'Eliminar definitivamente'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

type PlantNoticeForm = {
  title: string;
  message: string;
  priority: PlantNoticePriority;
  production_line_config_ids: string[];
};

function buildEmptyPlantNoticeForm(): PlantNoticeForm {
  return {
    title: '',
    message: '',
    priority: 'normal',
    production_line_config_ids: [],
  };
}

function formatPlantNoticeTimestamp(value: string | null | undefined) {
  if (!value) return '—';

  try {
    return format(parseISO(value), 'dd/MM/yyyy HH:mm');
  } catch {
    return value;
  }
}

function PlantNoticesDialog({
  open,
  activeNotices,
  activeLoading,
  activeError,
  configuredLines,
  onRefreshActive,
  onClose,
}: {
  open: boolean;
  activeNotices: PlantNotice[];
  activeLoading: boolean;
  activeError: boolean;
  configuredLines: ProductionLineConfig[];
  onRefreshActive: () => Promise<unknown>;
  onClose: () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();

  const [mode, setMode] = useState<'active' | 'archived'>('active');
  const [formOpen, setFormOpen] = useState(false);
  const [editingNotice, setEditingNotice] = useState<PlantNotice | null>(null);
  const [form, setForm] = useState<PlantNoticeForm>(
    buildEmptyPlantNoticeForm(),
  );
  const [expandedNoticeIds, setExpandedNoticeIds] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;

    setMode('active');
    setFormOpen(false);
    setEditingNotice(null);
    setForm(buildEmptyPlantNoticeForm());
    setExpandedNoticeIds([]);
  }, [open]);

  const archivedNoticesQuery = useQuery({
    queryKey: ['industrial-cost-plant-notices', 'archived'],
    queryFn: () =>
      listIndustrialCostPlantNotices({
        status_filter: 'archived',
      }),
    enabled: open && mode === 'archived',
  });

  const activeConfiguredLines = useMemo(
    () =>
      [...configuredLines]
        .filter((line) => line.is_active)
        .sort(
          (a, b) =>
            a.display_order - b.display_order ||
            a.name.localeCompare(b.name),
        ),
    [configuredLines],
  );

  const closeForm = () => {
    setFormOpen(false);
    setEditingNotice(null);
    setForm(buildEmptyPlantNoticeForm());
  };

  const openCreateForm = () => {
    setEditingNotice(null);
    setForm(buildEmptyPlantNoticeForm());
    setFormOpen(true);
  };

  const openEditForm = (notice: PlantNotice) => {
    setEditingNotice(notice);
    setForm({
      title: notice.title,
      message: notice.message,
      priority: notice.priority,
      production_line_config_ids: notice.targets.map(
        (target) => target.production_line_config_id,
      ),
    });
    setFormOpen(true);
  };

  const toggleNoticeHistory = (noticeId: string) => {
    setExpandedNoticeIds((current) =>
      current.includes(noticeId)
        ? current.filter((id) => id !== noticeId)
        : [...current, noticeId],
    );
  };


  const toggleProductionLine = (productionLineConfigId: string) => {
    setForm((current) => ({
      ...current,
      production_line_config_ids:
        current.production_line_config_ids.includes(
          productionLineConfigId,
        )
          ? current.production_line_config_ids.filter(
              (lineId) => lineId !== productionLineConfigId,
            )
          : [
              ...current.production_line_config_ids,
              productionLineConfigId,
            ],
    }));
  };

  const createNoticeMutation = useMutation({
    mutationFn: (payload: CreatePlantNoticePayload) =>
      createIndustrialCostPlantNotice(payload),
    onSuccess: async () => {
      enqueueSnackbar('Aviso publicado correctamente.', {
        variant: 'success',
      });
      closeForm();
      await onRefreshActive();
    },
    onError: (error: unknown) => {
      enqueueSnackbar(getErrorMessage(error), {
        variant: 'error',
      });
    },
  });

  const updateNoticeMutation = useMutation({
    mutationFn: ({
      noticeId,
      payload,
    }: {
      noticeId: string;
      payload: CreatePlantNoticePayload;
    }) => updateIndustrialCostPlantNotice(noticeId, payload),
    onSuccess: async () => {
      enqueueSnackbar('Aviso actualizado correctamente.', {
        variant: 'success',
      });
      closeForm();
      await onRefreshActive();
    },
    onError: (error: unknown) => {
      enqueueSnackbar(getErrorMessage(error), {
        variant: 'error',
      });
    },
  });

  const archiveNoticeMutation = useMutation({
    mutationFn: (noticeId: string) =>
      archiveIndustrialCostPlantNotice(noticeId),
    onSuccess: async () => {
      enqueueSnackbar('Aviso archivado correctamente.', {
        variant: 'success',
      });
      await onRefreshActive();
    },
    onError: (error: unknown) => {
      enqueueSnackbar(getErrorMessage(error), {
        variant: 'error',
      });
    },
  });

  const handleSave = () => {
    const normalizedTitle = form.title.trim();
    const normalizedMessage = form.message.trim();

    if (!normalizedTitle) {
      enqueueSnackbar('Introduce un título para el aviso.', {
        variant: 'warning',
      });
      return;
    }

    if (!normalizedMessage) {
      enqueueSnackbar('Introduce el contenido del aviso.', {
        variant: 'warning',
      });
      return;
    }

    if (form.production_line_config_ids.length === 0) {
      enqueueSnackbar('Selecciona al menos una línea.', {
        variant: 'warning',
      });
      return;
    }

    const payload: CreatePlantNoticePayload = {
      title: normalizedTitle,
      message: normalizedMessage,
      priority: form.priority,
      production_line_config_ids:
        form.production_line_config_ids,
    };

    if (editingNotice) {
      updateNoticeMutation.mutate({
        noticeId: editingNotice.id,
        payload,
      });
      return;
    }

    createNoticeMutation.mutate(payload);
  };

  const handleArchive = (notice: PlantNotice) => {
    if (
      !window.confirm(
        `¿Archivar el aviso "${notice.title}"? Dejará de mostrarse en fábrica.`,
      )
    ) {
      return;
    }

    archiveNoticeMutation.mutate(notice.id);
  };

  const notices =
    mode === 'active'
      ? activeNotices
      : archivedNoticesQuery.data ?? [];

  const listLoading =
    mode === 'active'
      ? activeLoading
      : archivedNoticesQuery.isLoading;

  const listError =
    mode === 'active'
      ? activeError
      : archivedNoticesQuery.isError;

  const saving =
    createNoticeMutation.isPending ||
    updateNoticeMutation.isPending;

  const registeredAcknowledgementsCount = activeNotices.reduce(
    (sum, notice) =>
      sum +
      notice.targets.reduce(
        (targetSum, target) =>
          targetSum + (target.acknowledgement_count ?? 0),
        0,
      ),
    0,
  );

  const expectedAcknowledgementsCount = activeNotices.reduce(
    (sum, notice) =>
      sum +
      notice.targets.reduce(
        (targetSum, target) =>
          targetSum +
          (target.expected_acknowledgement_count ?? 0),
        0,
      ),
    0,
  );

  const affectedLinesCount = new Set(
    activeNotices.flatMap((notice) =>
      notice.targets.map(
        (target) => target.production_line_config_id,
      ),
    ),
  ).size;

  const handleRetryList = () => {
    if (mode === 'active') {
      void onRefreshActive();
      return;
    }

    void archivedNoticesQuery.refetch();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'center' }}
          spacing={1.5}
        >
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
          >
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: 2.5,
                display: 'grid',
                placeItems: 'center',
                color: '#38bdf8',
                bgcolor: 'rgba(14,116,144,0.18)',
                border: '1px solid rgba(56,189,248,0.28)',
                flexShrink: 0,
              }}
            >
              <NotificationsActiveRoundedIcon />
            </Box>

            <Stack spacing={0.2}>
              <Typography
                sx={{
                  color: '#f8fafc',
                  fontWeight: 950,
                  fontSize: '1.08rem',
                }}
              >
                Pizarra de fábrica
              </Typography>

              <Typography
                sx={{
                  color: '#94a3b8',
                  fontSize: '0.86rem',
                }}
              >
                Publica avisos por línea y revisa sus confirmaciones.
              </Typography>
            </Stack>
          </Stack>

          {mode === 'active' ? (
            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={openCreateForm}
              sx={{
                borderRadius: 2.5,
                px: 1.75,
                fontWeight: 850,
              }}
            >
              Nuevo aviso
            </Button>
          ) : null}
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid rgba(148,163,184,0.14)',
              bgcolor: 'rgba(15,23,42,0.30)',
              p: 1,
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
            >
              <Button
                variant={mode === 'active' ? 'contained' : 'text'}
                onClick={() => setMode('active')}
                sx={{
                  borderRadius: 2,
                  fontWeight: 850,
                }}
              >
                Activos · {activeNotices.length}
              </Button>

              <Button
                variant={mode === 'archived' ? 'contained' : 'text'}
                onClick={() => setMode('archived')}
                sx={{
                  borderRadius: 2,
                  fontWeight: 850,
                }}
              >
                Histórico
              </Button>
            </Stack>
          </Paper>

          {mode === 'active' ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(3, minmax(0, 1fr))',
                },
                gap: 1,
              }}
            >
              {[
                {
                  label: 'Avisos activos',
                  value: activeNotices.length,
                },
                {
                  label: 'Lecturas',
                  value: `${registeredAcknowledgementsCount}/${expectedAcknowledgementsCount}`,
                },
                {
                  label: 'Líneas afectadas',
                  value: affectedLinesCount,
                },
              ].map((item) => (
                <Paper
                  key={item.label}
                  elevation={0}
                  sx={{
                    borderRadius: 2.5,
                    border: '1px solid rgba(148,163,184,0.14)',
                    bgcolor: 'rgba(15,23,42,0.36)',
                    px: 1.35,
                    py: 1.05,
                  }}
                >
                  <Typography
                    sx={{
                      color: '#64748b',
                      fontSize: '0.72rem',
                      fontWeight: 850,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                    }}
                  >
                    {item.label}
                  </Typography>

                  <Typography
                    sx={{
                      color: '#f8fafc',
                      fontSize: '1.35rem',
                      fontWeight: 950,
                      lineHeight: 1.1,
                      mt: 0.35,
                    }}
                  >
                    {item.value}
                  </Typography>
                </Paper>
              ))}
            </Box>
          ) : null}

          <Collapse in={formOpen}>
            <Paper
              elevation={0}
              sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 3,
                border: '1px solid rgba(56,189,248,0.28)',
                bgcolor: 'rgba(8,47,73,0.16)',
                p: { xs: 1.5, md: 2 },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 3,
                  bgcolor: '#38bdf8',
                },
              }}
            >
              <Stack spacing={1.75}>
                <Stack spacing={0.25}>
                  <Typography
                    sx={{
                      color: '#f8fafc',
                      fontWeight: 950,
                    }}
                  >
                    {editingNotice
                      ? 'Editar aviso'
                      : 'Publicar nuevo aviso'}
                  </Typography>

                  <Typography
                    sx={{
                      color: '#94a3b8',
                      fontSize: '0.82rem',
                    }}
                  >
                    El aviso aparecerá en los puestos con acceso a las
                    líneas seleccionadas.
                  </Typography>
                </Stack>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      md: 'minmax(0, 1fr) 220px',
                    },
                    gap: 1.25,
                  }}
                >
                  <TextField
                    size="small"
                    label="Título"
                    value={form.title}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        title: event.target.value,
                      }))
                    }
                    inputProps={{ maxLength: 200 }}
                    fullWidth
                  />

                  <TextField
                    select
                    size="small"
                    label="Prioridad"
                    value={form.priority}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        priority: event.target
                          .value as PlantNoticePriority,
                      }))
                    }
                    fullWidth
                  >
                    <MenuItem value="normal">Normal</MenuItem>
                    <MenuItem value="important">Importante</MenuItem>
                  </TextField>
                </Box>

                <TextField
                  size="small"
                  label="Mensaje"
                  value={form.message}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      message: event.target.value,
                    }))
                  }
                  inputProps={{ maxLength: 4000 }}
                  helperText={`${form.message.length}/4000 caracteres`}
                  multiline
                  minRows={4}
                  fullWidth
                />

                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 2.5,
                    border: '1px solid rgba(148,163,184,0.14)',
                    bgcolor: 'rgba(15,23,42,0.28)',
                    p: 1.25,
                  }}
                >
                  <Stack spacing={1}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      justifyContent="space-between"
                      alignItems={{ xs: 'stretch', sm: 'center' }}
                      spacing={0.75}
                    >
                      <Stack spacing={0.15}>
                        <Typography
                          sx={{
                            color: '#e2e8f0',
                            fontSize: '0.84rem',
                            fontWeight: 900,
                          }}
                        >
                          Líneas destinatarias
                        </Typography>

                        <Typography
                          sx={{
                            color: '#64748b',
                            fontSize: '0.76rem',
                          }}
                        >
                          Seleccionadas ·{' '}
                          {form.production_line_config_ids.length}
                        </Typography>
                      </Stack>

                      <Stack
                        direction="row"
                        spacing={0.5}
                        flexWrap="wrap"
                        useFlexGap
                      >
                        <Button
                          size="small"
                          variant="text"
                          onClick={() =>
                            setForm((current) => ({
                              ...current,
                              production_line_config_ids:
                                activeConfiguredLines.map(
                                  (line) => line.id,
                                ),
                            }))
                          }
                        >
                          Seleccionar todas
                        </Button>

                        <Button
                          size="small"
                          variant="text"
                          onClick={() =>
                            setForm((current) => ({
                              ...current,
                              production_line_config_ids: [],
                            }))
                          }
                        >
                          Limpiar
                        </Button>
                      </Stack>
                    </Stack>

                    {activeConfiguredLines.length === 0 ? (
                      <Alert severity="warning" variant="outlined">
                        No hay líneas activas disponibles.
                      </Alert>
                    ) : (
                      <Stack
                        direction="row"
                        spacing={0.75}
                        flexWrap="wrap"
                        useFlexGap
                      >
                        {activeConfiguredLines.map((line) => {
                          const selected =
                            form.production_line_config_ids.includes(
                              line.id,
                            );

                          return (
                            <Chip
                              key={line.id}
                              label={line.name || line.code}
                              clickable
                              color={
                                selected ? 'primary' : 'default'
                              }
                              variant={
                                selected ? 'filled' : 'outlined'
                              }
                              onClick={() =>
                                toggleProductionLine(line.id)
                              }
                              sx={{
                                borderRadius: 2,
                                fontWeight: selected ? 900 : 750,
                              }}
                            />
                          );
                        })}
                      </Stack>
                    )}
                  </Stack>
                </Paper>

                <Stack
                  direction="row"
                  spacing={1}
                  justifyContent="flex-end"
                >
                  <Button onClick={closeForm}>
                    Cancelar
                  </Button>

                  <Button
                    variant="contained"
                    disabled={saving}
                    onClick={handleSave}
                    sx={{
                      borderRadius: 2.5,
                      px: 1.75,
                      fontWeight: 850,
                    }}
                  >
                    {saving
                      ? 'Guardando...'
                      : editingNotice
                        ? 'Guardar cambios'
                        : 'Publicar aviso'}
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Collapse>


          {listError ? (
            <Alert
              severity="error"
              variant="outlined"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleRetryList}
                >
                  Reintentar
                </Button>
              }
            >
              No se pudieron cargar los avisos de fábrica.
            </Alert>
          ) : null}

          {listLoading ? (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2.5,
                border: '1px solid rgba(148,163,184,0.14)',
                bgcolor: 'rgba(15,23,42,0.26)',
                px: 1.25,
                py: 1,
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
              >
                <CircularProgress size={18} thickness={5} />

                <Typography
                  sx={{
                    color: '#94a3b8',
                    fontSize: '0.82rem',
                  }}
                >
                  Cargando avisos...
                </Typography>
              </Stack>
            </Paper>
          ) : null}

          {!listLoading && !listError && notices.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2.5,
                border: '1px dashed rgba(148,163,184,0.20)',
                bgcolor: 'rgba(15,23,42,0.22)',
                px: 1.5,
                py: 1.4,
              }}
            >
              <Stack spacing={0.25}>
                <Typography
                  sx={{
                    color: '#e2e8f0',
                    fontWeight: 850,
                  }}
                >
                  {mode === 'active'
                    ? 'No hay avisos activos'
                    : 'No hay avisos archivados'}
                </Typography>

                <Typography
                  sx={{
                    color: '#64748b',
                    fontSize: '0.82rem',
                  }}
                >
                  {mode === 'active'
                    ? 'Publica un aviso cuando necesites comunicar información a una o varias líneas.'
                    : 'Los avisos archivados aparecerán aquí para su consulta.'}
                </Typography>
              </Stack>
            </Paper>
          ) : null}

          <Stack spacing={1.25}>
            {notices.map((notice) => {
              const acknowledgementCount = notice.targets.reduce(
                (sum, target) =>
                  sum + (target.acknowledgement_count ?? 0),
                0,
              );

              const expectedAcknowledgementCount =
                notice.targets.reduce(
                  (sum, target) =>
                    sum +
                    (target.expected_acknowledgement_count ?? 0),
                  0,
                );

              const historyExpanded = expandedNoticeIds.includes(
                notice.id,
              );

              return (
                <Paper
                  key={notice.id}
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border:
                      notice.priority === 'important'
                        ? '1px solid rgba(251,191,36,0.34)'
                        : '1px solid rgba(148,163,184,0.18)',
                    bgcolor:
                      notice.priority === 'important'
                        ? 'rgba(120,53,15,0.16)'
                        : 'rgba(15,23,42,0.42)',
                    p: 1.75,
                  }}
                >
                  <Stack spacing={1.25}>
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      justifyContent="space-between"
                      spacing={1}
                    >
                      <Stack spacing={0.5}>
                        <Stack
                          direction="row"
                          spacing={0.75}
                          alignItems="center"
                          flexWrap="wrap"
                          useFlexGap
                        >
                          <Chip
                            size="small"
                            color={
                              notice.priority === 'important'
                                ? 'warning'
                                : 'default'
                            }
                            label={
                              notice.priority === 'important'
                                ? 'Importante'
                                : 'Normal'
                            }
                          />

                          <Typography
                            sx={{
                              color: '#f8fafc',
                              fontWeight: 950,
                            }}
                          >
                            {notice.title}
                          </Typography>
                        </Stack>

                        <Typography
                          sx={{
                            color: '#cbd5e1',
                            whiteSpace: 'pre-wrap',
                          }}
                        >
                          {notice.message}
                        </Typography>
                      </Stack>

                      {mode === 'active' ? (
                        <Stack
                          direction="row"
                          spacing={0.75}
                          alignItems="flex-start"
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditRoundedIcon />}
                            onClick={() => openEditForm(notice)}
                          >
                            Editar
                          </Button>

                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            startIcon={<ArchiveRoundedIcon />}
                            disabled={archiveNoticeMutation.isPending}
                            onClick={() => handleArchive(notice)}
                          >
                            Archivar
                          </Button>
                        </Stack>
                      ) : null}
                    </Stack>

                    <Divider />

                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      justifyContent="space-between"
                      spacing={0.75}
                    >
                      <Typography
                        sx={{
                          color: '#94a3b8',
                          fontSize: '0.82rem',
                        }}
                      >
                        Publicado {formatPlantNoticeTimestamp(
                          notice.published_at,
                        )}
                      </Typography>

                      <Typography
                        sx={{
                          color: '#cbd5e1',
                          fontSize: '0.82rem',
                          fontWeight: 850,
                        }}
                      >
                        Lecturas · {acknowledgementCount}/
                        {expectedAcknowledgementCount}
                      </Typography>
                    </Stack>
                    
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => toggleNoticeHistory(notice.id)}
                      sx={{
                        alignSelf: 'flex-start',
                        px: 0,
                        fontWeight: 850,
                      }}
                    >
                      {historyExpanded
                        ? 'Ocultar registro de lecturas'
                        : 'Ver registro de lecturas'}
                    </Button>

                    <Collapse in={historyExpanded}>
                      <Stack spacing={0.85}>
                        {notice.targets.map((target) => {
                          const acknowledgements =
                            target.acknowledgements ?? [];

                          const lineLabel =
                            target.production_line_name ||
                            target.production_line_code ||
                            'Línea';

                          return (
                            <Paper
                              key={target.production_line_config_id}
                              elevation={0}
                              sx={{
                                borderRadius: 2.5,
                                border:
                                  '1px solid rgba(148,163,184,0.14)',
                                bgcolor: 'rgba(15,23,42,0.30)',
                                px: 1.25,
                                py: 1,
                              }}
                            >
                              <Stack spacing={0.65}>
                                <Stack
                                  direction={{
                                    xs: 'column',
                                    sm: 'row',
                                  }}
                                  justifyContent="space-between"
                                  spacing={0.5}
                                >
                                  <Typography
                                    sx={{
                                      color: '#e2e8f0',
                                      fontSize: '0.84rem',
                                      fontWeight: 900,
                                    }}
                                  >
                                    {lineLabel}
                                  </Typography>

                                  <Typography
                                    sx={{
                                      color: '#94a3b8',
                                      fontSize: '0.78rem',
                                      fontWeight: 800,
                                    }}
                                  >
                                    {acknowledgements.length} lectura
                                    {acknowledgements.length === 1
                                      ? ''
                                      : 's'}
                                  </Typography>
                                </Stack>

                                {acknowledgements.length === 0 ? (
                                  <Typography
                                    sx={{
                                      color: '#64748b',
                                      fontSize: '0.8rem',
                                    }}
                                  >
                                    Sin confirmaciones registradas.
                                  </Typography>
                                ) : (
                                  <Stack spacing={0.4}>
                                    {acknowledgements.map(
                                      (acknowledgement, index) => (
                                        <Typography
                                          key={`${
                                            acknowledgement.report_id ||
                                            'legacy'
                                          }-${
                                            acknowledgement.acknowledged_at
                                          }-${index}`}
                                          sx={{
                                            color: '#cbd5e1',
                                            fontSize: '0.8rem',
                                          }}
                                        >
                                          {acknowledgement.report_date ||
                                            'Fecha no disponible'}{' '}
                                          ·{' '}
                                          {acknowledgement.shift
                                            ? getShiftLabel(
                                                acknowledgement.shift,
                                              )
                                            : 'Turno no disponible'}{' '}
                                          ·{' '}
                                          {acknowledgement.operator_number
                                            ? `Operario ${acknowledgement.operator_number}`
                                            : 'Operario no disponible'}{' '}
                                          · Confirmado{' '}
                                          {formatPlantNoticeTimestamp(
                                            acknowledgement.acknowledged_at,
                                          )}
                                        </Typography>
                                      ),
                                    )}
                                  </Stack>
                                )}
                              </Stack>
                            </Paper>
                          );
                        })}
                      </Stack>
                    </Collapse>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

type PerformanceBreakdownRow = ProductionReportFactorySummaryTotals & {
  key: string;
  label: string;
  helper: string;
};

function getCapacityPercentage(
  explicitValue: number | null | undefined,
  numerator: number,
  denominator: number,
) {
  if (
    explicitValue !== null &&
    explicitValue !== undefined &&
    Number.isFinite(explicitValue)
  ) {
    return explicitValue;
  }

  if (denominator <= 0) return null;

  return (numerator / denominator) * 100;
}

function clampProgressValue(value: number | null | undefined) {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return 0;
  }

  return Math.min(Math.max(value, 0), 100);
}

function PerformanceShiftCapacityTable({
  rows,
}: {
  rows: PerformanceBreakdownRow[];
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        border: '1px solid rgba(56,189,248,0.18)',
        bgcolor: 'rgba(8,47,73,0.18)',
        p: 2.5,
      }}
    >
      <Stack spacing={1.75}>
        <Stack spacing={0.3}>
          <Typography sx={{ color: '#f8fafc', fontWeight: 950 }}>
            Completitud de turno
          </Typography>
          <Typography sx={{ color: '#94a3b8', fontSize: '0.86rem' }}>
            La capacidad reportada se calcula solo sobre partes validados:
            número de partes validados × duración configurada del turno. No incluye borradores,
            partes reabiertos ni exportados.
          </Typography>
        </Stack>

        {rows.length === 0 ? (
          <Alert severity="info" variant="outlined">
            No hay turnos con capacidad reportada en el periodo seleccionado.
          </Alert>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                lg: 'repeat(3, minmax(0, 1fr))',
              },
              gap: 1.35,
            }}
          >
            {rows.map((row) => {
              const availableDuration = row.available_duration_minutes ?? 0;
              const reportsCount = row.reports_count ?? 0;
              const productionDuration = row.production_duration_minutes ?? 0;
              const setupDuration = row.setup_duration_minutes ?? 0;
              const stopDuration = row.stop_duration_minutes ?? 0;
              const idleDuration = row.idle_duration_minutes ?? 0;
              const accountedDuration =
                row.accounted_duration_minutes ??
                productionDuration + setupDuration + stopDuration + idleDuration;
              const unaccountedDuration =
                row.unaccounted_duration_minutes ??
                Math.max(availableDuration - accountedDuration, 0);

              const accountedPct = getCapacityPercentage(
                row.accounted_capacity_pct,
                accountedDuration,
                availableDuration,
              );
              const productionPct = getCapacityPercentage(
                row.production_capacity_pct,
                productionDuration,
                availableDuration,
              );
              const setupPct = getCapacityPercentage(
                row.setup_capacity_pct,
                setupDuration,
                availableDuration,
              );
              const stopPct = getCapacityPercentage(
                row.stop_capacity_pct,
                stopDuration,
                availableDuration,
              );
              const idlePct = getCapacityPercentage(
                row.idle_capacity_pct,
                idleDuration,
                availableDuration,
              );
              const unaccountedPct = getCapacityPercentage(
                row.unaccounted_capacity_pct,
                unaccountedDuration,
                availableDuration,
              );

              const averageProductionPerReport =
                reportsCount > 0 ? productionDuration / reportsCount : null;
              const averageAccountedPerReport =
                reportsCount > 0 ? accountedDuration / reportsCount : null;
              const averageCapacityPerReport =
                reportsCount > 0 ? availableDuration / reportsCount : null;

              return (
                <Paper
                  key={row.key}
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid rgba(148,163,184,0.16)',
                    bgcolor: 'rgba(15,23,42,0.58)',
                    p: 1.5,
                  }}
                >
                  <Stack spacing={1.35}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      spacing={1}
                    >
                      <Stack spacing={0.2}>
                        <Typography sx={{ color: '#f8fafc', fontWeight: 950 }}>
                          {row.label}
                        </Typography>
                        <Typography sx={{ color: '#64748b', fontSize: '0.74rem' }}>
                          {row.helper}
                        </Typography>
                      </Stack>

                      <Stack direction="row" spacing={0.6} flexWrap="wrap" justifyContent="flex-end">
                        <Chip
                          size="small"
                          label={`Fabricando ${formatPct(productionPct)}`}
                          variant="outlined"
                          sx={{
                            color:
                              productionPct !== null && productionPct >= 75
                                ? '#86efac'
                                : productionPct !== null && productionPct >= 60
                                  ? '#fde68a'
                                  : '#fca5a5',
                            borderColor: 'rgba(148,163,184,0.22)',
                            bgcolor: 'rgba(2,6,23,0.26)',
                            fontWeight: 900,
                          }}
                        />
                        <Chip
                          size="small"
                          label={`Registro ${formatPct(accountedPct)}`}
                          variant="outlined"
                          sx={{
                            color:
                              accountedPct !== null && accountedPct >= 95
                                ? '#86efac'
                                : accountedPct !== null && accountedPct >= 80
                                  ? '#fde68a'
                                  : '#fca5a5',
                            borderColor: 'rgba(148,163,184,0.22)',
                            bgcolor: 'rgba(2,6,23,0.26)',
                            fontWeight: 900,
                          }}
                        />
                      </Stack>
                    </Stack>

                    <Stack spacing={0.55}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        spacing={1}
                      >
                        <Typography sx={{ color: '#94a3b8', fontSize: '0.76rem' }}>
                          Registro cubierto
                        </Typography>
                        <Typography sx={{ color: '#e2e8f0', fontSize: '0.76rem', fontWeight: 850 }}>
                          {formatEstimatedMinutes(accountedDuration)} /{' '}
                          {formatEstimatedMinutes(availableDuration)}
                        </Typography>
                      </Stack>

                      <LinearProgress
                        variant="determinate"
                        value={clampProgressValue(accountedPct)}
                        sx={{
                          height: 8,
                          borderRadius: 999,
                          bgcolor: 'rgba(15,23,42,0.86)',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 999,
                          },
                        }}
                      />
                    </Stack>

                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                        gap: 0.85,
                      }}
                    >
                      {[
                        {
                          label: 'Media fabricando / parte',
                          value:
                            averageProductionPerReport !== null
                              ? formatEstimatedMinutes(averageProductionPerReport)
                              : '—',
                          helper: 'producción real media',
                        },
                        {
                          label: 'Media registrada / parte',
                          value:
                            averageAccountedPerReport !== null
                              ? formatEstimatedMinutes(averageAccountedPerReport)
                              : '—',
                          helper: 'fabricación + preparación + paradas + sin orden',
                        },
                        {
                          label: 'Capacidad media / parte',
                          value:
                            averageCapacityPerReport !== null
                              ? formatEstimatedMinutes(averageCapacityPerReport)
                              : '—',
                          helper: 'duración configurada del turno',
                        },
                      ].map((item) => (
                        <Paper
                          key={item.label}
                          elevation={0}
                          sx={{
                            borderRadius: 2.25,
                            border: '1px solid rgba(56,189,248,0.16)',
                            bgcolor: 'rgba(8,47,73,0.2)',
                            px: 1,
                            py: 0.85,
                          }}
                        >
                          <Typography
                            sx={{
                              color: '#7dd3fc',
                              fontSize: '0.66rem',
                              fontWeight: 900,
                              textTransform: 'uppercase',
                              letterSpacing: '0.03em',
                            }}
                          >
                            {item.label}
                          </Typography>
                          <Typography sx={{ color: '#f8fafc', fontWeight: 950 }}>
                            {item.value}
                          </Typography>
                          <Typography sx={{ color: '#94a3b8', fontSize: '0.68rem' }}>
                            {item.helper}
                          </Typography>
                        </Paper>
                      ))}
                    </Box>

                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                        gap: 0.85,
                      }}
                    >
                      {[
                        {
                          label: 'Fabricando',
                          value: productionDuration,
                          pct: productionPct,
                          helper: 'tiempo real productivo',
                          tone: '#86efac',
                        },
                        {
                          label: 'Preparación',
                          value: setupDuration,
                          pct: setupPct,
                          helper: 'setup antes de fabricar',
                          tone: '#fbbf24',
                        },
                        {
                          label: 'Paradas',
                          value: stopDuration,
                          pct: stopPct,
                          helper: 'paradas registradas',
                          tone: stopDuration > 0 ? '#fca5a5' : '#86efac',
                        },
                        {
                          label: 'Sin orden',
                          value: idleDuration,
                          pct: idlePct,
                          helper: 'huecos registrados sin orden',
                          tone: '#93c5fd',
                        },
                        {
                          label: 'Sin cubrir',
                          value: unaccountedDuration,
                          pct: unaccountedPct,
                          helper: 'capacidad sin registro; debería ser 0 en validados',
                          tone: unaccountedDuration > 0 ? '#fb7185' : '#86efac',
                        },
                        {
                          label: 'Capacidad reportada',
                          value: availableDuration,
                          pct: null,
                          helper: 'partes validados × duración turno',
                          tone: '#e2e8f0',
                        },
                      ].map((item) => (
                        <Paper
                          key={item.label}
                          elevation={0}
                          sx={{
                            borderRadius: 2.25,
                            border: '1px solid rgba(148,163,184,0.12)',
                            bgcolor: 'rgba(2,6,23,0.24)',
                            px: 1,
                            py: 0.85,
                          }}
                        >
                          <Typography
                            sx={{
                              color: '#64748b',
                              fontSize: '0.68rem',
                              fontWeight: 850,
                              textTransform: 'uppercase',
                              letterSpacing: '0.03em',
                            }}
                          >
                            {item.label}
                          </Typography>
                          <Typography sx={{ color: item.tone, fontWeight: 950 }}>
                            {formatEstimatedMinutes(item.value)}
                          </Typography>
                          <Typography sx={{ color: '#94a3b8', fontSize: '0.7rem' }}>
                            {item.pct !== null ? formatPct(item.pct) : item.helper}
                          </Typography>
                          {item.pct !== null ? (
                            <Typography sx={{ color: '#64748b', fontSize: '0.66rem' }}>
                              {item.helper}
                            </Typography>
                          ) : null}
                        </Paper>
                      ))}
                    </Box>
                  </Stack>
                </Paper>
              );
            })}
          </Box>
        )}
      </Stack>
    </Paper>
  );
}

function PerformanceBreakdownTable({
  title,
  description,
  rows,
  emptyMessage,
}: {
  title: string;
  description: string;
  rows: PerformanceBreakdownRow[];
  emptyMessage: string;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        border: '1px solid rgba(148,163,184,0.16)',
        bgcolor: 'rgba(15,23,42,0.58)',
        p: 2.5,
      }}
    >
      <Stack spacing={1.75}>
        <Stack spacing={0.3}>
          <Typography sx={{ color: '#f8fafc', fontWeight: 950 }}>
            {title}
          </Typography>
          <Typography sx={{ color: '#94a3b8', fontSize: '0.86rem' }}>
            {description}
          </Typography>
        </Stack>

        {rows.length === 0 ? (
          <Alert severity="info" variant="outlined">
            {emptyMessage}
          </Alert>
        ) : (
          <Box
            sx={{
              overflowX: 'auto',
              borderRadius: 2.5,
              border: '1px solid rgba(148,163,184,0.12)',
            }}
          >
            <Box sx={{ minWidth: 1120 }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns:
                    'minmax(170px, 1.35fr) minmax(165px, 1.2fr) minmax(110px, 0.8fr) minmax(130px, 0.9fr) minmax(175px, 1.2fr) minmax(135px, 0.9fr) minmax(125px, 0.85fr)',
                  gap: 1.25,
                  px: 1.5,
                  py: 1.1,
                  bgcolor: 'rgba(2,6,23,0.36)',
                  borderBottom: '1px solid rgba(148,163,184,0.12)',
                }}
              >
                {[
                  'DIMENSIÓN',
                  'PRODUCCIÓN REAL / TEÓRICA',
                  'DESVIACIÓN PROD.',
                  'PREPARACIÓN MEDIA',
                  'OPERATIVO VS PARADO',
                  'PARADAS',
                  'COBERTURA',
                ].map((label) => (
                  <Typography
                    key={label}
                    sx={{
                      color: '#93c5fd',
                      fontSize: '0.68rem',
                      fontWeight: 900,
                      letterSpacing: '0.03em',
                    }}
                  >
                    {label}
                  </Typography>
                ))}
              </Box>

              {rows.map((row, index) => {
                const productionReal = row.production_duration_minutes ?? 0;
                const productionEstimated =
                  row.estimated_production_duration_minutes ?? 0;
                const productionDeviation =
                  productionReal - productionEstimated;
                const productionDeviationPct =
                  productionEstimated > 0
                    ? (productionDeviation / productionEstimated) * 100
                    : null;

                const setupAverage =
                  row.closed_orders_count > 0
                    ? row.setup_duration_minutes / row.closed_orders_count
                    : null;

                const operative = row.real_duration_minutes ?? 0;
                const stopped = row.stop_duration_minutes ?? 0;
                const operativeAndStopped = operative + stopped;
                const operativePct =
                  operativeAndStopped > 0
                    ? (operative / operativeAndStopped) * 100
                    : null;

                const coveragePct =
                  row.closed_orders_count > 0
                    ? (row.estimated_orders_count / row.closed_orders_count) *
                      100
                    : null;

                return (
                  <Box
                    key={row.key}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns:
                        'minmax(170px, 1.35fr) minmax(165px, 1.2fr) minmax(110px, 0.8fr) minmax(130px, 0.9fr) minmax(175px, 1.2fr) minmax(135px, 0.9fr) minmax(125px, 0.85fr)',
                      gap: 1.25,
                      alignItems: 'center',
                      px: 1.5,
                      py: 1.35,
                      borderTop:
                        index === 0
                          ? 'none'
                          : '1px solid rgba(148,163,184,0.10)',
                      bgcolor:
                        productionDeviationPct !== null &&
                        productionDeviationPct > 10
                          ? 'rgba(127,29,29,0.10)'
                          : 'transparent',
                    }}
                  >
                    <Stack spacing={0.2}>
                      <Typography
                        sx={{
                          color: '#f8fafc',
                          fontWeight: 900,
                          fontSize: '0.92rem',
                        }}
                      >
                        {row.label}
                      </Typography>
                      <Typography
                        sx={{
                          color: '#64748b',
                          fontSize: '0.74rem',
                        }}
                      >
                        {row.helper}
                      </Typography>
                    </Stack>

                    <Stack spacing={0.2}>
                      <Typography
                        sx={{
                          color: '#f8fafc',
                          fontWeight: 900,
                          fontSize: '0.9rem',
                        }}
                      >
                        {formatEstimatedMinutes(productionReal)}{' '}
                        <Typography
                          component="span"
                          sx={{ color: '#64748b', fontSize: '0.76rem' }}
                        >
                          / {formatEstimatedMinutes(productionEstimated)}
                        </Typography>
                      </Typography>
                    </Stack>

                    <Stack spacing={0.2}>
                      <Typography
                        sx={{
                          color:
                            productionDeviationPct === null
                              ? '#94a3b8'
                              : productionDeviationPct <= 0
                                ? '#86efac'
                                : '#fca5a5',
                          fontWeight: 950,
                        }}
                      >
                        {productionDeviationPct === null
                          ? '—'
                          : formatPct(productionDeviationPct)}
                      </Typography>
                      <Typography sx={{ color: '#64748b', fontSize: '0.72rem' }}>
                        {productionDeviationPct === null
                          ? 'sin estándar'
                          : formatSignedMinutes(productionDeviation)}
                      </Typography>
                    </Stack>

                    <Stack spacing={0.2}>
                      <Typography
                        sx={{
                          color: '#fbbf24',
                          fontWeight: 900,
                        }}
                      >
                        {formatEstimatedMinutes(setupAverage)}
                      </Typography>
                      <Typography sx={{ color: '#64748b', fontSize: '0.72rem' }}>
                        total {formatEstimatedMinutes(row.setup_duration_minutes)}
                      </Typography>
                    </Stack>

                    <Stack spacing={0.2}>
                      <Typography
                        sx={{
                          color: '#f8fafc',
                          fontWeight: 900,
                          fontSize: '0.9rem',
                        }}
                      >
                        {formatEstimatedMinutes(operative)}{' '}
                        <Typography
                          component="span"
                          sx={{ color: '#fca5a5', fontSize: '0.76rem' }}
                        >
                          / {formatEstimatedMinutes(stopped)}
                        </Typography>
                      </Typography>
                      <Typography sx={{ color: '#64748b', fontSize: '0.72rem' }}>
                        {operativePct === null
                          ? 'sin actividad'
                          : `${formatPct(operativePct)} operativo`}
                      </Typography>
                    </Stack>

                    <Stack spacing={0.2}>
                      <Typography
                        sx={{
                          color: stopped > 0 ? '#fca5a5' : '#86efac',
                          fontWeight: 900,
                        }}
                      >
                        {formatEstimatedMinutes(stopped)}
                      </Typography>
                      <Typography sx={{ color: '#64748b', fontSize: '0.72rem' }}>
                        {row.stops_count} parada
                        {row.stops_count === 1 ? '' : 's'}
                      </Typography>
                    </Stack>

                    <Stack spacing={0.2}>
                      <Typography
                        sx={{
                          color:
                            coveragePct !== null && coveragePct >= 90
                              ? '#86efac'
                              : '#fbbf24',
                          fontWeight: 900,
                        }}
                      >
                        {formatPct(coveragePct)}
                      </Typography>
                      <Typography sx={{ color: '#64748b', fontSize: '0.72rem' }}>
                        {row.estimated_orders_count}/{row.closed_orders_count}{' '}
                        con estándar
                      </Typography>
                    </Stack>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Stack>
    </Paper>
  );
}

const PERFORMANCE_PRODUCT_QUANTITY_FORMATTER = esNumberFormat({
    maximumFractionDigits: 6,
  },
);

function formatPerformanceProductQuantityValue(value: number) {
  return PERFORMANCE_PRODUCT_QUANTITY_FORMATTER.format(value);
}

function formatPerformanceProductQuantities(
  quantities: ProductionReportPerformanceProductItem['actual_quantities'],
) {
  if (quantities.length === 0) return '—';

  return quantities
    .map(
      (item) =>
        `${formatPerformanceProductQuantityValue(item.value)} ${item.unit}`,
    )
    .join(' · ');
}

function ProductPerformanceTable({
  rows,
  expandedProductKey,
  sortField,
  sortDirection,
  quantitySortEnabled,
  onToggleProduct,
  onSortChange,
}: {
  rows: ProductionReportPerformanceProductItem[];
  expandedProductKey: string | null;
  sortField: ProductPerformanceSort;
  sortDirection: ProductPerformanceSortDirection;
  quantitySortEnabled: boolean;
  onToggleProduct: (productKey: string) => void;
  onSortChange: (field: ProductPerformanceSort) => void;
}) {
  const [detailMode, setDetailMode] =
    useState<ProductPerformanceDetailMode>('orders');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    setPage(0);
  }, [rows, rowsPerPage]);

  const totalPages = Math.max(
    1,
    Math.ceil(rows.length / rowsPerPage),
  );

  const visibleRows = rows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const firstVisibleRow =
    rows.length > 0 ? page * rowsPerPage + 1 : 0;

  const lastVisibleRow = Math.min(
    (page + 1) * rowsPerPage,
    rows.length,
  );

  const getSortIndicator = (field: ProductPerformanceSort) => {
    if (sortField !== field) return '↕';

    return sortDirection === 'desc' ? '↓' : '↑';
  };

  const headers: Array<{
    label: string;
    field: ProductPerformanceSort;
    disabled?: boolean;
    tooltip?: string;
  }> = [
    {
      label: 'PRODUCTO / REFERENCIA',
      field: 'reference',
    },
    {
      label: 'ACTIVIDAD',
      field: 'activity',
    },
    {
      label: 'CANTIDAD REAL',
      field: 'quantity',
      disabled: !quantitySortEnabled,
      tooltip: quantitySortEnabled
        ? undefined
        : 'No se puede ordenar la cantidad cuando existen unidades diferentes.',
    },
    {
      label: 'PRODUCCIÓN REAL / TEÓRICA',
      field: 'production',
    },
    {
      label: 'DESVIACIÓN PROD.',
      field: 'deviation',
    },
    {
      label: 'PREPARACIÓN MEDIA',
      field: 'setup',
    },
    {
      label: 'PARADAS ATRIBUIBLES',
      field: 'stops',
    },
    {
      label: 'COBERTURA',
      field: 'coverage',
    },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        border: '1px solid rgba(148,163,184,0.16)',
        bgcolor: 'rgba(15,23,42,0.58)',
        p: 2.5,
      }}
    >
      <Stack spacing={1.75}>
        <Stack spacing={0.3}>
          <Typography sx={{ color: '#f8fafc', fontWeight: 950 }}>
            Comparativa por producto
          </Typography>

          <Typography sx={{ color: '#94a3b8', fontSize: '0.86rem' }}>
            Analiza referencias fabricadas y despliega una fila para consultar
            sus órdenes de fabricación o comprobar si la desviación se concentra
            en una línea concreta.
          </Typography>
        </Stack>

        {rows.length === 0 ? (
          <Alert severity="info" variant="outlined">
            No hay productos con actividad cerrada que coincidan con la
            búsqueda.
          </Alert>
        ) : (
          <Stack spacing={1.25}>
            <Box
              sx={{
                overflowX: 'auto',
                borderRadius: 2.5,
                border: '1px solid rgba(148,163,184,0.12)',
              }}
            >
              <Box sx={{ minWidth: 1360 }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns:
                    'minmax(205px, 1.45fr) minmax(160px, 1fr) minmax(145px, 1fr) minmax(175px, 1.15fr) minmax(115px, 0.75fr) minmax(125px, 0.82fr) minmax(140px, 0.9fr) minmax(125px, 0.82fr)',
                  gap: 1.1,
                  px: 1.5,
                  py: 0.7,
                  bgcolor: 'rgba(2,6,23,0.36)',
                  borderBottom: '1px solid rgba(148,163,184,0.12)',
                }}
              >
                {headers.map((header) => (
                  <Tooltip
                    key={header.field}
                    title={header.tooltip ?? ''}
                    arrow
                  >
                    <span>
                      <Button
                        size="small"
                        variant="text"
                        disabled={header.disabled}
                        onClick={() => onSortChange(header.field)}
                        sx={{
                          minWidth: 0,
                          p: 0,
                          color: '#93c5fd',
                          fontSize: '0.68rem',
                          fontWeight: 900,
                          letterSpacing: '0.03em',
                          textAlign: 'left',
                          justifyContent: 'flex-start',
                          textTransform: 'none',
                        }}
                      >
                        {header.label} {getSortIndicator(header.field)}
                      </Button>
                    </span>
                  </Tooltip>
                ))}
              </Box>

              {visibleRows.map((row, index) => {
                const productionDeviationPct =
                  row.production_deviation_pct;

                const setupAverage =
                  row.closed_orders_count > 0
                    ? row.setup_duration_minutes /
                      row.closed_orders_count
                    : null;

                const coveragePct =
                  row.closed_orders_count > 0
                    ? (row.estimated_orders_count /
                        row.closed_orders_count) *
                      100
                    : null;

                const isExpanded =
                  expandedProductKey === row.product_key;

                return (
                  <Box
                    key={row.product_key}
                    sx={{
                      borderTop:
                        index === 0
                          ? 'none'
                          : '1px solid rgba(148,163,184,0.10)',
                      bgcolor:
                        productionDeviationPct !== null &&
                        productionDeviationPct > 10
                          ? 'rgba(127,29,29,0.10)'
                          : 'transparent',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns:
                          'minmax(205px, 1.45fr) minmax(160px, 1fr) minmax(145px, 1fr) minmax(175px, 1.15fr) minmax(115px, 0.75fr) minmax(125px, 0.82fr) minmax(140px, 0.9fr) minmax(125px, 0.82fr)',
                        gap: 1.1,
                        alignItems: 'center',
                        px: 1.5,
                        py: 1.35,
                      }}
                    >
                      <Stack spacing={0.35} sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            color: '#f8fafc',
                            fontWeight: 900,
                            fontSize: '0.92rem',
                            wordBreak: 'break-word',
                          }}
                        >
                          {row.product_label}
                        </Typography>

                        <Typography
                          sx={{
                            color: '#64748b',
                            fontSize: '0.72rem',
                            wordBreak: 'break-word',
                          }}
                        >
                          Referencia: {row.product_ref}
                        </Typography>

                        <Button
                          size="small"
                          variant="text"
                          onClick={() =>
                            onToggleProduct(row.product_key)
                          }
                          sx={{
                            alignSelf: 'flex-start',
                            minWidth: 0,
                            p: 0,
                            textTransform: 'none',
                            fontSize: '0.72rem',
                            fontWeight: 850,
                          }}
                        >
                          {isExpanded ? 'Ocultar detalle' : 'Ver detalle'}
                        </Button>
                      </Stack>

                      <Stack spacing={0.2}>
                        <Typography
                          sx={{ color: '#f8fafc', fontWeight: 900 }}
                        >
                          {row.manufacturing_orders_count} OF ·{' '}
                          {row.closed_orders_count} tramo
                          {row.closed_orders_count === 1 ? '' : 's'}
                        </Typography>

                        <Typography
                          sx={{ color: '#64748b', fontSize: '0.72rem' }}
                        >
                          {row.reports_count} parte
                          {row.reports_count === 1 ? '' : 's'} ·{' '}
                          {row.line_count} línea
                          {row.line_count === 1 ? '' : 's'}
                        </Typography>

                        {row.orders_without_number_count > 0 ? (
                          <Typography
                            sx={{ color: '#fbbf24', fontSize: '0.7rem' }}
                          >
                            {row.orders_without_number_count} tramo
                            {row.orders_without_number_count === 1
                              ? ''
                              : 's'}{' '}
                            sin Nº OF
                          </Typography>
                        ) : null}
                      </Stack>

                      <Typography
                        sx={{
                          color: '#e2e8f0',
                          fontWeight: 850,
                          fontSize: '0.86rem',
                        }}
                      >
                        {formatPerformanceProductQuantities(
                          row.actual_quantities,
                        )}
                      </Typography>

                      <Stack spacing={0.2}>
                        <Typography
                          sx={{
                            color: '#f8fafc',
                            fontWeight: 900,
                            fontSize: '0.88rem',
                          }}
                        >
                          {formatEstimatedMinutes(
                            row.production_duration_minutes,
                          )}{' '}
                          <Typography
                            component="span"
                            sx={{
                              color: '#64748b',
                              fontSize: '0.74rem',
                            }}
                          >
                            /{' '}
                            {formatEstimatedMinutes(
                              row.estimated_production_duration_minutes,
                            )}
                          </Typography>
                        </Typography>
                      </Stack>

                      <Stack spacing={0.2}>
                        <Typography
                          sx={{
                            color:
                              productionDeviationPct === null
                                ? '#94a3b8'
                                : productionDeviationPct <= 0
                                  ? '#86efac'
                                  : '#fca5a5',
                            fontWeight: 950,
                          }}
                        >
                          {formatPct(productionDeviationPct)}
                        </Typography>

                        <Typography
                          sx={{ color: '#64748b', fontSize: '0.72rem' }}
                        >
                          {productionDeviationPct === null
                            ? 'sin estándar'
                            : formatSignedMinutes(
                                row.production_deviation_minutes,
                              )}
                        </Typography>
                      </Stack>

                      <Stack spacing={0.2}>
                        <Typography
                          sx={{ color: '#fbbf24', fontWeight: 900 }}
                        >
                          {formatEstimatedMinutes(setupAverage)}
                        </Typography>

                        <Typography
                          sx={{ color: '#64748b', fontSize: '0.72rem' }}
                        >
                          total{' '}
                          {formatEstimatedMinutes(
                            row.setup_duration_minutes,
                          )}
                        </Typography>
                      </Stack>

                      <Stack spacing={0.2}>
                        <Typography
                          sx={{
                            color:
                              row.attributed_stop_duration_minutes > 0
                                ? '#fca5a5'
                                : '#86efac',
                            fontWeight: 900,
                          }}
                        >
                          {formatEstimatedMinutes(
                            row.attributed_stop_duration_minutes,
                          )}
                        </Typography>

                        <Typography
                          sx={{ color: '#64748b', fontSize: '0.72rem' }}
                        >
                          {row.attributed_stops_count} parada
                          {row.attributed_stops_count === 1 ? '' : 's'}
                        </Typography>
                      </Stack>

                      <Stack spacing={0.2}>
                        <Typography
                          sx={{
                            color:
                              coveragePct !== null && coveragePct >= 90
                                ? '#86efac'
                                : '#fbbf24',
                            fontWeight: 900,
                          }}
                        >
                          {formatPct(coveragePct)}
                        </Typography>

                        <Typography
                          sx={{ color: '#64748b', fontSize: '0.72rem' }}
                        >
                          {row.estimated_orders_count}/
                          {row.closed_orders_count} con estándar
                        </Typography>
                      </Stack>
                    </Box>

                    <Collapse in={isExpanded}>
                      <Stack
                        spacing={1}
                        sx={{
                          mx: 1.5,
                          mb: 1.25,
                          p: 1,
                          borderRadius: 2,
                          border:
                            '1px solid rgba(56,189,248,0.14)',
                          bgcolor: 'rgba(8,47,73,0.10)',
                        }}
                      >
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1}
                          justifyContent="space-between"
                          alignItems={{ xs: 'flex-start', sm: 'center' }}
                        >
                          <Typography
                            sx={{
                              color: '#7dd3fc',
                              fontSize: '0.74rem',
                              fontWeight: 900,
                              textTransform: 'uppercase',
                              letterSpacing: '0.04em',
                            }}
                          >
                            Detalle del producto
                          </Typography>

                          <Stack
                            direction="row"
                            spacing={0.5}
                            sx={{
                              borderRadius: 2,
                              border:
                                '1px solid rgba(148,163,184,0.14)',
                              bgcolor: 'rgba(2,6,23,0.24)',
                              p: 0.35,
                            }}
                          >
                            <Button
                              size="small"
                              variant={
                                detailMode === 'orders'
                                  ? 'contained'
                                  : 'text'
                              }
                              onClick={() => setDetailMode('orders')}
                              sx={{
                                minWidth: 0,
                                px: 1,
                                borderRadius: 1.5,
                                textTransform: 'none',
                              }}
                            >
                              Órdenes de fabricación
                            </Button>

                            <Button
                              size="small"
                              variant={
                                detailMode === 'lines'
                                  ? 'contained'
                                  : 'text'
                              }
                              onClick={() => setDetailMode('lines')}
                              sx={{
                                minWidth: 0,
                                px: 1,
                                borderRadius: 1.5,
                                textTransform: 'none',
                              }}
                            >
                              Líneas
                            </Button>
                          </Stack>
                        </Stack>

                        {detailMode === 'orders' ? (
                          row.by_order.map((order, orderIndex) => {
                            const orderSetupAverage =
                              order.closed_orders_count > 0
                                ? order.setup_duration_minutes /
                                  order.closed_orders_count
                                : null;

                            return (
                              <Box
                                key={`${
                                  order.manufacturing_order_number ||
                                  'missing'
                                }-${orderIndex}`}
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns:
                                    'minmax(170px, 1.25fr) minmax(150px, 1fr) minmax(130px, 0.9fr) minmax(180px, 1.2fr) minmax(110px, 0.75fr) minmax(125px, 0.82fr) minmax(140px, 0.9fr)',
                                  gap: 1,
                                  alignItems: 'center',
                                  px: 1,
                                  py: 0.8,
                                  borderRadius: 1.5,
                                  bgcolor: 'rgba(15,23,42,0.34)',
                                }}
                              >
                                <Stack spacing={0.15}>
                                  <Typography
                                    sx={{
                                      color: order.has_number
                                        ? '#f8fafc'
                                        : '#fbbf24',
                                      fontWeight: 850,
                                      fontSize: '0.82rem',
                                    }}
                                  >
                                    {order.manufacturing_order_number ||
                                      'Sin Nº OF'}
                                  </Typography>

                                  <Typography
                                    sx={{
                                      color: '#64748b',
                                      fontSize: '0.7rem',
                                    }}
                                  >
                                    {order.line_labels.join(' · ') ||
                                      'Línea sin nombre'}
                                  </Typography>
                                </Stack>

                                <Typography
                                  sx={{
                                    color: '#cbd5e1',
                                    fontSize: '0.8rem',
                                  }}
                                >
                                  {order.closed_orders_count} tramo
                                  {order.closed_orders_count === 1
                                    ? ''
                                    : 's'}{' '}
                                  · {order.line_count} línea
                                  {order.line_count === 1 ? '' : 's'}
                                </Typography>

                                <Typography
                                  sx={{
                                    color: '#e2e8f0',
                                    fontSize: '0.8rem',
                                    fontWeight: 800,
                                  }}
                                >
                                  {formatPerformanceProductQuantities(
                                    order.actual_quantities,
                                  )}
                                </Typography>

                                <Typography
                                  sx={{
                                    color: '#e2e8f0',
                                    fontSize: '0.8rem',
                                    fontWeight: 800,
                                  }}
                                >
                                  {formatEstimatedMinutes(
                                    order.production_duration_minutes,
                                  )}{' '}
                                  /{' '}
                                  {formatEstimatedMinutes(
                                    order.estimated_production_duration_minutes,
                                  )}
                                </Typography>

                                <Typography
                                  sx={{
                                    color:
                                      order.production_deviation_pct !==
                                        null &&
                                      order.production_deviation_pct <= 0
                                        ? '#86efac'
                                        : '#fca5a5',
                                    fontSize: '0.8rem',
                                    fontWeight: 900,
                                  }}
                                >
                                  {formatPct(
                                    order.production_deviation_pct,
                                  )}
                                </Typography>

                                <Typography
                                  sx={{
                                    color: '#fbbf24',
                                    fontSize: '0.8rem',
                                    fontWeight: 850,
                                  }}
                                >
                                  Prep.{' '}
                                  {formatEstimatedMinutes(
                                    orderSetupAverage,
                                  )}
                                </Typography>

                                <Typography
                                  sx={{
                                    color:
                                      order.attributed_stop_duration_minutes >
                                      0
                                        ? '#fca5a5'
                                        : '#86efac',
                                    fontSize: '0.8rem',
                                    fontWeight: 850,
                                  }}
                                >
                                  Paradas{' '}
                                  {formatEstimatedMinutes(
                                    order.attributed_stop_duration_minutes,
                                  )}
                                </Typography>
                              </Box>
                            );
                          })
                        ) : (
                          row.by_line.map((line, lineIndex) => {
                            const lineSetupAverage =
                              line.closed_orders_count > 0
                                ? line.setup_duration_minutes /
                                  line.closed_orders_count
                                : null;

                            return (
                              <Box
                                key={`${
                                  line.production_line_config_id ||
                                  line.line_or_center
                                }-${lineIndex}`}
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns:
                                    'minmax(170px, 1.25fr) minmax(130px, 0.9fr) minmax(180px, 1.2fr) minmax(115px, 0.78fr) minmax(125px, 0.82fr) minmax(140px, 0.9fr)',
                                  gap: 1,
                                  alignItems: 'center',
                                  px: 1,
                                  py: 0.8,
                                  borderRadius: 1.5,
                                  bgcolor: 'rgba(15,23,42,0.34)',
                                }}
                              >
                                <Typography
                                  sx={{
                                    color: '#f8fafc',
                                    fontWeight: 850,
                                    fontSize: '0.82rem',
                                  }}
                                >
                                  {line.line_or_center}
                                </Typography>

                                <Typography
                                  sx={{
                                    color: '#cbd5e1',
                                    fontSize: '0.8rem',
                                  }}
                                >
                                  {line.closed_orders_count} tramo
                                  {line.closed_orders_count === 1 ? '' : 's'}
                                </Typography>

                                <Typography
                                  sx={{
                                    color: '#e2e8f0',
                                    fontSize: '0.8rem',
                                    fontWeight: 800,
                                  }}
                                >
                                  {formatEstimatedMinutes(
                                    line.production_duration_minutes,
                                  )}{' '}
                                  /{' '}
                                  {formatEstimatedMinutes(
                                    line.estimated_production_duration_minutes,
                                  )}
                                </Typography>

                                <Typography
                                  sx={{
                                    color:
                                      line.production_deviation_pct !== null &&
                                      line.production_deviation_pct <= 0
                                        ? '#86efac'
                                        : '#fca5a5',
                                    fontSize: '0.8rem',
                                    fontWeight: 900,
                                  }}
                                >
                                  {formatPct(
                                    line.production_deviation_pct,
                                  )}
                                </Typography>

                                <Typography
                                  sx={{
                                    color: '#fbbf24',
                                    fontSize: '0.8rem',
                                    fontWeight: 850,
                                  }}
                                >
                                  Prep.{' '}
                                  {formatEstimatedMinutes(
                                    lineSetupAverage,
                                  )}
                                </Typography>

                                <Typography
                                  sx={{
                                    color:
                                      line.attributed_stop_duration_minutes >
                                      0
                                        ? '#fca5a5'
                                        : '#86efac',
                                    fontSize: '0.8rem',
                                    fontWeight: 850,
                                  }}
                                >
                                  Paradas{' '}
                                  {formatEstimatedMinutes(
                                    line.attributed_stop_duration_minutes,
                                  )}
                                </Typography>
                              </Box>
                            );
                          })
                        )}
                      </Stack>
                    </Collapse>
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.25}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', md: 'center' }}
            sx={{
              borderRadius: 2.5,
              border: '1px solid rgba(148,163,184,0.12)',
              bgcolor: 'rgba(2,6,23,0.24)',
              px: 1.5,
              py: 1,
            }}
          >
            <Typography
              sx={{
                color: '#94a3b8',
                fontSize: '0.82rem',
              }}
            >
              Mostrando{' '}
              <Typography
                component="span"
                sx={{ color: '#e2e8f0', fontWeight: 900 }}
              >
                {firstVisibleRow}–{lastVisibleRow}
              </Typography>{' '}
              de{' '}
              <Typography
                component="span"
                sx={{ color: '#e2e8f0', fontWeight: 900 }}
              >
                {rows.length}
              </Typography>{' '}
              producto{rows.length === 1 ? '' : 's'}
            </Typography>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'stretch', sm: 'center' }}
            >
              <TextField
                select
                size="small"
                label="Filas por página"
                value={rowsPerPage}
                onChange={(event) =>
                  setRowsPerPage(Number(event.target.value))
                }
                sx={{ minWidth: 145 }}
              >
                {[10, 25, 50, 100].map((value) => (
                  <MenuItem key={value} value={value}>
                    {value}
                  </MenuItem>
                ))}
              </TextField>

              <Typography
                sx={{
                  color: '#94a3b8',
                  fontSize: '0.82rem',
                  minWidth: 92,
                  textAlign: { xs: 'left', sm: 'center' },
                }}
              >
                Página {page + 1} de {totalPages}
              </Typography>

              <Stack direction="row" spacing={0.75}>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={page === 0}
                  onClick={() =>
                    setPage((current) => Math.max(0, current - 1))
                  }
                  sx={{ textTransform: 'none' }}
                >
                  Anterior
                </Button>

                <Button
                  size="small"
                  variant="outlined"
                  disabled={page >= totalPages - 1}
                  onClick={() =>
                    setPage((current) =>
                      Math.min(totalPages - 1, current + 1),
                    )
                  }
                  sx={{ textTransform: 'none' }}
                >
                  Siguiente
                </Button>
              </Stack>
            </Stack>
          </Stack>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}

export default function IndustrialCostProductionReportsControlView({
  permissions,
}: IndustrialCostProductionReportsControlViewProps) {
  const [controlTab, setControlTab] = useState<ControlTab>('current');
  const [exportDateFrom, setExportDateFrom] = useState<Date | null>(null);
  const [exportDateTo, setExportDateTo] = useState<Date | null>(null);
  const [exportShift, setExportShift] = useState<string>('all');
  const [exportLineId, setExportLineId] = useState<string>('all');
  const [exportingKind, setExportingKind] =
    useState<ProductionReportExportKind | null>(null);
  const effectivePermissions: IndustrialCostControlPermission[] = permissions ?? [
    'factory_control:view',
    'production_history:view',
    'production_performance:view',
  ];

  const canViewCurrentTab = effectivePermissions.includes('factory_control:view');
  const canViewHistoryTab = effectivePermissions.includes('production_history:view');

  const canViewPerformanceTab = effectivePermissions.includes('production_performance:view');
  const canViewWeeklyTab = canViewCurrentTab || canViewHistoryTab || canViewPerformanceTab;
  const canViewIntelligenceTab = effectivePermissions.includes('production_planning:manage');
  const canViewExportTab = canViewHistoryTab;


  const firstAvailableControlTab: ControlTab | null = canViewCurrentTab
    ? 'current'
    : canViewHistoryTab
      ? 'history'
      : canViewPerformanceTab
        ? 'performance'
        : null;

  const isSelectedControlTabAllowed =
    (controlTab === 'current' && canViewCurrentTab) ||
    (controlTab === 'weekly' && canViewWeeklyTab) ||
    (controlTab === 'history' && canViewHistoryTab) ||
    (controlTab === 'performance' && canViewPerformanceTab) ||
    (controlTab === 'intelligence' && canViewIntelligenceTab) ||
    (controlTab === 'export' && canViewExportTab);

  const visibleControlTab = isSelectedControlTabAllowed ? controlTab : firstAvailableControlTab;
  const [historyMode, setHistoryMode] = useState<HistoryMode>('review');
  const [reviewStatusFilter, setReviewStatusFilter] =
    useState<ReviewBoardFilterMode>('unvalidated');
  const [reviewDateFrom, setReviewDateFrom] = useState('');
  const [reviewDateTo, setReviewDateTo] = useState('');
  const [reviewLineId, setReviewLineId] = useState('all');
  const [reviewOperatorSearchText, setReviewOperatorSearchText] = useState('');
  const [
    debouncedReviewOperatorSearchText,
    setDebouncedReviewOperatorSearchText,
  ] = useState('');
  const [reportDate, setReportDate] = useState(getTodayIsoDate());
  const [shift, setShift] = useState<'all' | 'morning' | 'afternoon' | 'night'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'draft' | 'validated' | 'exported'>('all');
  const [performancePeriodPreset, setPerformancePeriodPreset] =
    useState<PerformancePeriodPreset>('today');
  const initialPerformanceRange = getPerformancePeriodRange('today');
  const [performanceDateFrom, setPerformanceDateFrom] = useState(
    initialPerformanceRange.dateFrom,
  );
  const [performanceDateTo, setPerformanceDateTo] = useState(
    initialPerformanceRange.dateTo,
  );
  const [performanceView, setPerformanceView] =
    useState<PerformanceView>('summary');
  const [performanceOperatorSearchText, setPerformanceOperatorSearchText] =
    useState('');
  const [performanceProductSearchText, setPerformanceProductSearchText] =
    useState('');
  const [performanceProductSort, setPerformanceProductSort] =
    useState<ProductPerformanceSort>('production');
  const [
    performanceProductSortDirection,
    setPerformanceProductSortDirection,
  ] = useState<ProductPerformanceSortDirection>('desc');
  const [
    expandedPerformanceProductKey,
    setExpandedPerformanceProductKey,
  ] = useState<string | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<LineGroup | null>(null);
  const [plantNoticesDialogOpen, setPlantNoticesDialogOpen] =
    useState(false);
  const [selectedReviewItem, setSelectedReviewItem] =
    useState<ProductionReportReviewBoardItem | null>(null);
  const [timelineValidationIssue, setTimelineValidationIssue] =
    useState<TimelineValidationIssue | null>(null);
  const [expandedStopReasonLabel, setExpandedStopReasonLabel] = useState<string | null>(null);

  const [aiAnalysis, setAiAnalysis] = useState<string | null>(null);
  const [aiAnalysisLoading, setAiAnalysisLoading] = useState(false);
  const [aiAnalysisError, setAiAnalysisError] = useState<string | null>(null);

  const handleAnalyzePerformance = async () => {
    if (!factorySummaryQuery.data) return;

    setAiAnalysisLoading(true);
    setAiAnalysisError(null);
    setAiAnalysis(null);

    const periodLabels: Record<string, string> = {
      today: 'Hoy',
      week: 'Semana actual',
      month: 'Mes actual',
      year: 'Año actual',
      custom: `${performanceDateFrom} — ${performanceDateTo}`,
    };

    try {
      const result = await analyzeIndustrialCostProductionPerformance({
        period_label: periodLabels[performancePeriodPreset] ?? `${performanceDateFrom} — ${performanceDateTo}`,
        current: factorySummaryQuery.data.totals,
        previous: previousFactorySummaryQuery.data?.totals ?? null,
        by_line: factorySummaryQuery.data.by_line ?? [],
        by_shift: factorySummaryQuery.data.by_shift ?? [],
        by_operator: factorySummaryQuery.data.by_operator ?? [],
        top_stop_reasons: factorySummaryQuery.data.top_stop_reasons ?? [],
      });
      setAiAnalysis(result.analysis);
    } catch {
      setAiAnalysisError('No se pudo generar el análisis. Comprueba la configuración del servicio de IA.');
    } finally {
      setAiAnalysisLoading(false);
    }
  };

  const handlePerformancePeriodPresetChange = (nextPreset: PerformancePeriodPreset) => {
    setPerformancePeriodPreset(nextPreset);

    if (nextPreset === 'custom') return;

    const nextRange = getPerformancePeriodRange(nextPreset);
    setPerformanceDateFrom(nextRange.dateFrom);
    setPerformanceDateTo(nextRange.dateTo);
  };

  const [clockTick, setClockTick] = useState(0);
  const { enqueueSnackbar } = useSnackbar();

  const handleProductionReportExport = async (
    kind: ProductionReportExportKind,
  ) => {
    if (!exportDateFrom || !exportDateTo || exportingKind) return;

    if (exportDateFrom > exportDateTo) {
      enqueueSnackbar(
        'La fecha inicial no puede ser posterior a la fecha final.',
        { variant: 'warning' },
      );
      return;
    }

    const params = {
      date_from: format(exportDateFrom, 'yyyy-MM-dd'),
      date_to: format(exportDateTo, 'yyyy-MM-dd'),
      shift: exportShift === 'all' ? null : exportShift,
      production_line_config_id:
        exportLineId === 'all' ? null : exportLineId,
    };

    setExportingKind(kind);

    try {
      if (kind === 'lines') {
        await exportIndustrialCostProductionReportLines(params);
      } else {
        await exportIndustrialCostProductionReportStops(params);
      }
    } catch {
      enqueueSnackbar(
        kind === 'lines'
          ? 'No se pudo generar la exportación de órdenes.'
          : 'No se pudo generar la exportación de paradas.',
        { variant: 'error' },
      );
    } finally {
      setExportingKind(null);
    }
  };

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setClockTick((current) => current + 1);
    }, 30000);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (visibleControlTab && visibleControlTab !== controlTab) {
      setControlTab(visibleControlTab);
    }
  }, [controlTab, visibleControlTab]);

  useEffect(() => {
    const normalizedOperatorSearchText = reviewOperatorSearchText.trim();

    if (!normalizedOperatorSearchText) {
      setDebouncedReviewOperatorSearchText('');
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setDebouncedReviewOperatorSearchText(normalizedOperatorSearchText);
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [reviewOperatorSearchText]);

  const productsQuery = useQuery({
    queryKey: ['industrial-cost-products'],
    queryFn: getIndustrialCostProducts,
  });

  const productionLinesQuery = useQuery({
    queryKey: ['industrial-cost-production-lines-config'],
    queryFn: listIndustrialCostProductionLinesConfig,
  });

  const operationSettingsQuery = useQuery({
    queryKey: ['industrial-cost-production-operation-settings-config'],
    queryFn: getIndustrialCostProductionOperationSettingsConfig,
  });

  const activePlantNoticesQuery = useQuery({
    queryKey: ['industrial-cost-plant-notices', 'active'],
    queryFn: () =>
      listIndustrialCostPlantNotices({
        status_filter: 'active',
      }),
    enabled: canViewCurrentTab,
    refetchInterval: canViewCurrentTab ? 30000 : false,
    refetchIntervalInBackground: false,
  });

  const productById = useMemo(() => {
    const map = new Map<string, Product>();

    (productsQuery.data ?? []).forEach((product) => {
      map.set(product.id, product);
    });

    return map;
  }, [productsQuery.data]);

  const previousPerformanceRange = useMemo(
    () => getPreviousPeriodRange(performanceDateFrom, performanceDateTo),
    [performanceDateFrom, performanceDateTo],
  );

  const factoryControlQuery = useQuery({
    queryKey: [
      'industrial-cost-production-reports-factory-control',
      reportDate,
      shift,
      statusFilter,
    ],
    queryFn: () =>
      getIndustrialCostProductionReportFactoryControl({
        report_date: reportDate || null,
        shift: shift === 'all' ? null : shift,
        status_filter: statusFilter === 'all' ? null : statusFilter,
      }),
    enabled:
      visibleControlTab === 'current' ||
      (visibleControlTab === 'history' && historyMode === 'by_date'),
    refetchInterval: visibleControlTab === 'current' ? 30000 : false,
    refetchIntervalInBackground: false,
  });

  const reviewBoardQuery = useQuery({
    queryKey: [
      'industrial-cost-production-reports-review-board',
      reviewStatusFilter,
      shift,
      reviewDateFrom,
      reviewDateTo,
      reviewLineId,
      debouncedReviewOperatorSearchText,
    ],
    queryFn: () =>
      getIndustrialCostProductionReportReviewBoard({
        review_status: reviewStatusFilter,
        shift: shift === 'all' ? null : shift,
        date_from: reviewDateFrom || null,
        date_to: reviewDateTo || null,
        production_line_config_id:
          reviewLineId === 'all' ? null : reviewLineId,
        operator_number_search: debouncedReviewOperatorSearchText || null,
        limit: 200,
      }),
    enabled: visibleControlTab === 'history' && historyMode === 'review',
  });

  const reviewBoardSummaryQuery = useQuery({
    queryKey: [
      'industrial-cost-production-reports-review-board-summary',
      shift,
      reviewDateFrom,
      reviewDateTo,
      reviewLineId,
      debouncedReviewOperatorSearchText,
    ],
    queryFn: () =>
      getIndustrialCostProductionReportReviewBoardSummary({
        shift: shift === 'all' ? null : shift,
        date_from: reviewDateFrom || null,
        date_to: reviewDateTo || null,
        production_line_config_id:
          reviewLineId === 'all' ? null : reviewLineId,
        operator_number_search: debouncedReviewOperatorSearchText || null,
      }),
    enabled: visibleControlTab === 'history' && historyMode === 'review',
  });

  const factorySummaryQuery = useQuery({
    queryKey: [
      'industrial-cost-production-reports-factory-summary',
      performanceDateFrom,
      performanceDateTo,
    ],
    queryFn: () =>
      getIndustrialCostProductionReportFactorySummary({
        date_from: performanceDateFrom,
        date_to: performanceDateTo,
      }),
    enabled: visibleControlTab === 'performance',
  });

  const previousFactorySummaryQuery = useQuery({
    queryKey: [
      'industrial-cost-production-reports-factory-summary-previous',
      previousPerformanceRange.dateFrom,
      previousPerformanceRange.dateTo,
    ],
    queryFn: () =>
      getIndustrialCostProductionReportFactorySummary({
        date_from: previousPerformanceRange.dateFrom,
        date_to: previousPerformanceRange.dateTo,
      }),
    enabled: controlTab === 'performance',
  });

  const performanceProductsQuery = useQuery({
    queryKey: [
      'industrial-cost-production-performance-products',
      performanceDateFrom,
      performanceDateTo,
    ],
    queryFn: () =>
      getIndustrialCostProductionPerformanceProducts({
        date_from: performanceDateFrom,
        date_to: performanceDateTo,
      }),
    enabled:
      visibleControlTab === 'performance' &&
      performanceView === 'products',
  });

  const productPerformanceTableData = useMemo(() => {
    const normalizedSearch =
      performanceProductSearchText.trim().toLowerCase();

    const rows = [
      ...(performanceProductsQuery.data?.items ?? []),
    ].filter((item) => {
      if (!normalizedSearch) return true;

      return (
        item.product_label.toLowerCase().includes(normalizedSearch) ||
        item.product_ref.toLowerCase().includes(normalizedSearch)
      );
    });

    const visibleUnits = new Set<string>();

    rows.forEach((item) => {
      item.actual_quantities.forEach((quantity) => {
        visibleUnits.add(quantity.unit);
      });
    });

    const quantitySortEnabled = visibleUnits.size === 1;
    const comparableQuantityUnit =
      quantitySortEnabled
        ? Array.from(visibleUnits)[0]
        : null;

    const getSetupAverage = (
      item: ProductionReportPerformanceProductItem,
    ) =>
      item.closed_orders_count > 0
        ? item.setup_duration_minutes / item.closed_orders_count
        : 0;

    const getCoverage = (
      item: ProductionReportPerformanceProductItem,
    ) =>
      item.closed_orders_count > 0
        ? item.estimated_orders_count / item.closed_orders_count
        : 0;

    const getComparableQuantity = (
      item: ProductionReportPerformanceProductItem,
    ) => {
      if (!comparableQuantityUnit) return 0;

      return (
        item.actual_quantities.find(
          (quantity) => quantity.unit === comparableQuantityUnit,
        )?.value ?? 0
      );
    };

    const directionMultiplier =
      performanceProductSortDirection === 'asc' ? 1 : -1;

    const compareNumbers = (a: number, b: number) =>
      (a - b) * directionMultiplier;

    const compareNullableNumbers = (
      a: number | null,
      b: number | null,
    ) => {
      if (a === null && b === null) return 0;
      if (a === null) return 1;
      if (b === null) return -1;

      return compareNumbers(a, b);
    };

    rows.sort((a, b) => {
      if (performanceProductSort === 'reference') {
        return (
          a.product_label.localeCompare(b.product_label) *
          directionMultiplier
        );
      }

      if (performanceProductSort === 'activity') {
        return (
          compareNumbers(
            a.manufacturing_orders_count,
            b.manufacturing_orders_count,
          ) ||
          compareNumbers(
            a.closed_orders_count,
            b.closed_orders_count,
          )
        );
      }

      if (
        performanceProductSort === 'quantity' &&
        quantitySortEnabled
      ) {
        return compareNumbers(
          getComparableQuantity(a),
          getComparableQuantity(b),
        );
      }

      if (performanceProductSort === 'deviation') {
        return compareNullableNumbers(
          a.production_deviation_pct,
          b.production_deviation_pct,
        );
      }

      if (performanceProductSort === 'setup') {
        return compareNumbers(
          getSetupAverage(a),
          getSetupAverage(b),
        );
      }

      if (performanceProductSort === 'stops') {
        return compareNumbers(
          a.attributed_stop_duration_minutes,
          b.attributed_stop_duration_minutes,
        );
      }

      if (performanceProductSort === 'coverage') {
        return compareNumbers(
          getCoverage(a),
          getCoverage(b),
        );
      }

      return compareNumbers(
        a.production_duration_minutes,
        b.production_duration_minutes,
      );
    });

    return {
      rows,
      quantitySortEnabled,
    };
  }, [
    performanceProductSearchText,
    performanceProductSort,
    performanceProductSortDirection,
    performanceProductsQuery.data?.items,
  ]);

  useEffect(() => {
    if (
      performanceProductSort === 'quantity' &&
      !productPerformanceTableData.quantitySortEnabled
    ) {
      setPerformanceProductSort('production');
      setPerformanceProductSortDirection('desc');
    }
  }, [
    performanceProductSort,
    productPerformanceTableData.quantitySortEnabled,
  ]);


  const items = factoryControlQuery.data ?? [];
  const configuredLines = productionLinesQuery.data ?? [];
  const lineGroups = useMemo(
    () => buildLineGroups(items, configuredLines),
    [items, configuredLines],
  );
  const processLineGroups = useMemo(
    () => buildProcessLineGroups(lineGroups),
    [lineGroups],
  );
  const processPerformanceGroups = useMemo(
    () =>
      buildProcessPerformanceGroups(
        factorySummaryQuery.data?.by_line ?? [],
        configuredLines,
      ),
    [factorySummaryQuery.data?.by_line, configuredLines],
  );

  const shiftPerformanceRows = useMemo<PerformanceBreakdownRow[]>(() => {
    const shiftOrder: Record<string, number> = {
      morning: 1,
      afternoon: 2,
      night: 3,
    };

    return [...(factorySummaryQuery.data?.by_shift ?? [])]
      .sort(
        (a, b) =>
          (shiftOrder[a.shift] ?? 99) - (shiftOrder[b.shift] ?? 99) ||
          a.shift_label.localeCompare(b.shift_label),
      )
      .map((row) => ({
        ...row,
        key: `shift:${row.shift}`,
        label: row.shift_label || getShiftLabel(row.shift),
        helper: `${row.reports_count} parte${
          row.reports_count === 1 ? '' : 's'
        } validado${row.reports_count === 1 ? '' : 's'} · ${
          row.closed_orders_count
        } tramo${row.closed_orders_count === 1 ? '' : 's'} cerrado${
          row.closed_orders_count === 1 ? '' : 's'
        }`,
      }));
  }, [factorySummaryQuery.data?.by_shift]);

  const operatorPerformanceRows = useMemo<PerformanceBreakdownRow[]>(() => {
    const normalizedSearch =
      performanceOperatorSearchText.trim().toLowerCase();

    return [...(factorySummaryQuery.data?.by_operator ?? [])]
      .filter((row) =>
        normalizedSearch
          ? row.operator_number.toLowerCase().includes(normalizedSearch)
          : true,
      )
      .sort(
        (a, b) =>
          (b.total_occupied_duration_minutes ?? 0) -
            (a.total_occupied_duration_minutes ?? 0) ||
          a.operator_number.localeCompare(b.operator_number),
      )
      .map((row) => ({
        ...row,
        key: `operator:${row.operator_number}`,
        label: `Operario ${row.operator_number}`,
        helper: `${row.reports_count} parte${
          row.reports_count === 1 ? '' : 's'
        } · ${row.closed_orders_count} tramo${
          row.closed_orders_count === 1 ? '' : 's'
        } cerrado${row.closed_orders_count === 1 ? '' : 's'}`,
      }));
  }, [
    factorySummaryQuery.data?.by_operator,
    performanceOperatorSearchText,
  ]);

  const rawReviewBoardItems = reviewBoardQuery.data ?? [];

  const [reviewBoardSortField, setReviewBoardSortField] = useState<
    'report_date' | 'shift' | 'line_or_center'
  >('report_date');
  const [reviewBoardSortDir, setReviewBoardSortDir] = useState<'asc' | 'desc'>('desc');

  const handleReviewBoardSort = (field: 'report_date' | 'shift' | 'line_or_center') => {
    if (reviewBoardSortField === field) {
      setReviewBoardSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setReviewBoardSortField(field);
      setReviewBoardSortDir('asc');
    }
  };

  const reviewBoardItems = useMemo(() => {
    let filtered = rawReviewBoardItems;
    if (reviewStatusFilter === 'unvalidated') {
      filtered = rawReviewBoardItems.filter(
        (item) =>
          item.review_status === 'pending_administrative_action' ||
          item.review_status === 'pending_revalidation',
      );
    }
    return [...filtered].sort((a, b) => {
      const valA = a[reviewBoardSortField] ?? '';
      const valB = b[reviewBoardSortField] ?? '';
      const cmp = String(valA).localeCompare(String(valB));
      return reviewBoardSortDir === 'asc' ? cmp : -cmp;
    });
  }, [rawReviewBoardItems, reviewStatusFilter, reviewBoardSortField, reviewBoardSortDir]);

  const reviewBoardSummary = reviewBoardSummaryQuery.data ?? {
    total: 0,
    all: 0,
    unvalidated: 0,
    pending_administrative_action: 0,
    pending_revalidation: 0,
    validated: 0,
    exported: 0,
    requires_attention: 0,
  };

  const historyReviewSummary = useMemo(() => {
    const reportIds = new Set<string>();
    const draftReportIds = new Set<string>();

    items.forEach((item) => {
      if (!item.report_id) return;

      reportIds.add(item.report_id);

      if (item.report_status === 'draft') {
        draftReportIds.add(item.report_id);
      }
    });

    const groupsWithReport = lineGroups.filter(
      (group) => !group.has_configured_line_without_report,
    );

    const groupsWithWarnings = groupsWithReport.filter(
      (group) => group.warnings.length > 0,
    );

    const groupsWithOpenEvents = groupsWithReport.filter(
      (group) =>
        group.totals.open_stops_count > 0 ||
        group.totals.production_lines_count > group.totals.closed_lines_count,
    );

    const readyToReviewGroups = groupsWithReport.filter((group) => {
      const currentItem = getCurrentActivityItem(group);
      const hasOpenEvents =
        group.totals.open_stops_count > 0 ||
        group.totals.production_lines_count > group.totals.closed_lines_count;

      return (
        currentItem.report_status === 'draft' &&
        group.warnings.length === 0 &&
        !hasOpenEvents
      );
    });

    return {
      reportsCount: reportIds.size,
      draftReportsCount: draftReportIds.size,
      groupsWithWarningsCount: groupsWithWarnings.length,
      groupsWithOpenEventsCount: groupsWithOpenEvents.length,
      readyToReviewGroupsCount: readyToReviewGroups.length,
    };
  }, [items, lineGroups]);

  const selectedLineConfigId = selectedGroup?.configuredLine?.id ?? null;

  const selectedLineDetailsQuery = useQuery({
    queryKey: ['industrial-cost-production-reports-line-detail', selectedLineConfigId],
    enabled: Boolean(selectedLineConfigId),
    queryFn: () =>
      getIndustrialCostLineRecentHistory({
        production_line_config_id: selectedLineConfigId!,
        limit: 3,
      }),
  });

  const selectedReviewDetailQuery = useQuery({
    queryKey: [
      'industrial-cost-production-reports-review-detail',
      selectedReviewItem?.report_id ?? null,
    ],
    enabled: Boolean(selectedReviewItem?.report_id),
    queryFn: () =>
      getIndustrialCostProductionReportDetail(selectedReviewItem!.report_id),
  });

  useEffect(() => {
    setTimelineValidationIssue(null);
  }, [selectedReviewItem?.report_id]);

  const validateReviewReportMutation = useMutation({
    mutationFn: async () => {
      if (!selectedReviewItem?.report_id) {
        throw new Error('No hay parte seleccionado.');
      }

      return validateIndustrialCostProductionReport(selectedReviewItem.report_id);
    },
    onSuccess: async () => {
      enqueueSnackbar(
        selectedReviewItem?.review_status === 'pending_revalidation'
          ? 'Parte revalidado correctamente.'
          : 'Parte validado correctamente.',
        { variant: 'success' },
      );
      setTimelineValidationIssue(null);
      await Promise.all([
        reviewBoardQuery.refetch(),
        reviewBoardSummaryQuery.refetch(),
        selectedReviewDetailQuery.refetch(),
      ]);
    },
    onError: (error: unknown) => {
      const issue = getTimelineValidationIssue(error);

      if (issue) {
        setTimelineValidationIssue(issue);
        return;
      }

      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    },
  });

  return (
    <Stack spacing={3}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: '1px solid rgba(148,163,184,0.18)',
          bgcolor: 'rgba(15,23,42,0.72)',
          px: { xs: 2.5, md: 3 },
          py: { xs: 2.5, md: 3 },
        }}
      >
        <Stack spacing={2.25}>
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', lg: 'flex-start' }}
            spacing={2}
          >
            <Stack spacing={1}>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <FactoryRoundedIcon sx={{ color: '#38bdf8' }} />
                <Typography sx={{ color: '#f8fafc', fontSize: '1.55rem', fontWeight: 950 }}>
                  Control de fábrica
                </Typography>
              </Stack>

              <Typography sx={{ color: '#94a3b8', maxWidth: 820 }}>
                Puesto de mando para ver la situación actual de planta y analizar el rendimiento de producción.
              </Typography>
            </Stack>

            <Stack
              spacing={1.25}
              alignItems={{ xs: 'stretch', lg: 'flex-end' }}
            >
              <Button
                variant="outlined"
                startIcon={
                  factoryControlQuery.isFetching ? (
                    <CircularProgress size={16} thickness={5} />
                  ) : (
                    <RefreshRoundedIcon />
                  )
                }
                onClick={() => factoryControlQuery.refetch()}
                disabled={factoryControlQuery.isFetching}
              >
                {factoryControlQuery.isFetching
                  ? 'Actualizando'
                  : 'Actualizar'}
              </Button>

              {canViewCurrentTab ? (
                <Button
                  variant="outlined"
                  startIcon={<NotificationsActiveRoundedIcon />}
                  onClick={() => setPlantNoticesDialogOpen(true)}
                >
                  Avisos de fábrica ·{' '}
                  {activePlantNoticesQuery.data?.length ?? 0} activos
                </Button>
              ) : null}

            </Stack>
          </Stack>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            sx={{
              borderRadius: 3,
              bgcolor: 'rgba(2,6,23,0.28)',
              p: 0.75,
            }}
          >
            {canViewCurrentTab && (
              <Button
                variant={visibleControlTab === 'current' ? 'contained' : 'text'}
                onClick={() => setControlTab('current')}
                sx={{
                  borderRadius: 2,
                  color: visibleControlTab === 'current' ? '#020617' : '#cbd5e1',
                  bgcolor: visibleControlTab === 'current' ? '#38bdf8' : 'transparent',
                  '&:hover': {
                    bgcolor: visibleControlTab === 'current' ? '#7dd3fc' : 'rgba(148,163,184,0.08)',
                  },
                }}
              >
                Estado actual
              </Button>
            )}

            {canViewWeeklyTab && (
              <Button
                variant={visibleControlTab === 'weekly' ? 'contained' : 'text'}
                onClick={() => setControlTab('weekly')}
                sx={{
                  borderRadius: 2,
                  color: visibleControlTab === 'weekly' ? '#020617' : '#cbd5e1',
                  bgcolor: visibleControlTab === 'weekly' ? '#38bdf8' : 'transparent',
                  '&:hover': {
                    bgcolor: visibleControlTab === 'weekly' ? '#7dd3fc' : 'rgba(148,163,184,0.08)',
                  },
                }}
              >
                Actividad semanal
              </Button>
            )}

            {canViewHistoryTab && (
              <Button
                variant={visibleControlTab === 'history' ? 'contained' : 'text'}
                onClick={() => setControlTab('history')}
                sx={{
                  borderRadius: 2,
                  color: visibleControlTab === 'history' ? '#020617' : '#cbd5e1',
                  bgcolor: visibleControlTab === 'history' ? '#38bdf8' : 'transparent',
                  '&:hover': {
                    bgcolor: visibleControlTab === 'history' ? '#7dd3fc' : 'rgba(148,163,184,0.08)',
                  },
                }}
              >
                Histórico
              </Button>
            )}

            {canViewIntelligenceTab && (
              <Button
                variant={visibleControlTab === 'intelligence' ? 'contained' : 'text'}
                onClick={() => setControlTab('intelligence')}
                sx={{
                  borderRadius: 2,
                  color: visibleControlTab === 'intelligence' ? '#020617' : '#cbd5e1',
                  bgcolor: visibleControlTab === 'intelligence' ? '#38bdf8' : 'transparent',
                  '&:hover': {
                    bgcolor: visibleControlTab === 'intelligence'
                      ? '#7dd3fc'
                      : 'rgba(148,163,184,0.08)',
                  },
                }}
              >
                Inteligencia
              </Button>
            )}

            {canViewPerformanceTab && (
              <Button
                variant={visibleControlTab === 'performance' ? 'contained' : 'text'}

                onClick={() => setControlTab('performance')}
                sx={{
                  borderRadius: 2,
                  color: visibleControlTab === 'performance' ? '#020617' : '#cbd5e1',
                  bgcolor: visibleControlTab === 'performance' ? '#38bdf8' : 'transparent',
                  '&:hover': {
                    bgcolor: visibleControlTab === 'performance' ? '#7dd3fc' : 'rgba(148,163,184,0.08)',
                  },
                }}
              >
                Rendimiento
              </Button>
            )}
            {canViewExportTab && (
              <Button
                variant={visibleControlTab === 'export' ? 'contained' : 'text'}
                onClick={() => setControlTab('export')}
                sx={{
                  borderRadius: 2,
                  color: visibleControlTab === 'export' ? '#020617' : '#cbd5e1',
                  bgcolor: visibleControlTab === 'export' ? '#38bdf8' : 'transparent',
                  '&:hover': {
                    bgcolor:
                      visibleControlTab === 'export'
                        ? '#7dd3fc'
                        : 'rgba(148,163,184,0.08)',
                  },
                }}
              >
                Exportar
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      {visibleControlTab === null ? (
        <Alert severity="warning" variant="outlined">
          No tienes permisos para ver las pestañas de Control de fábrica.
        </Alert>
      ) : visibleControlTab === 'current' ? (
        <Stack spacing={2}>

          {(factoryControlQuery.isError ||
            productionLinesQuery.isError ||
            operationSettingsQuery.isError) && (
            <Alert severity="error">No se pudo cargar el control de fábrica.</Alert>
          )}

          {!factoryControlQuery.isFetching &&
          !productionLinesQuery.isFetching &&
          lineGroups.length === 0 ? (
            <Alert severity="info" variant="outlined">
              No hay líneas configuradas ni actividad de fábrica para el estado actual.
            </Alert>
          ) : (
            <Stack spacing={2}>
              {processLineGroups.map((processGroup) => (
                <Paper
                  key={processGroup.key}
                  elevation={0}
                  sx={{
                    borderRadius: 4,
                    border: `1px solid ${processGroup.tone.border}`,
                    bgcolor: 'rgba(15,23,42,0.34)',
                    p: { xs: 1.25, md: 1.5 },
                    boxShadow: '0 16px 36px rgba(2,6,23,0.14)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      right: 0,
                      height: 3,
                      bgcolor: processGroup.tone.accent,
                      opacity: 0.95,
                    },
                  }}
                >
                  <Stack spacing={1.15} sx={{ position: 'relative' }}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      justifyContent="space-between"
                      alignItems={{ xs: 'stretch', sm: 'center' }}
                      spacing={0.8}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        flexWrap="wrap"
                        useFlexGap
                      >
                        <Typography
                          sx={{
                            color: processGroup.tone.accent,
                            fontWeight: 950,
                            fontSize: '0.82rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                          }}
                        >
                          {processGroup.label}
                        </Typography>

                        <Chip
                          size="small"
                          label={`${processGroup.totals.total} ${
                            processGroup.totals.total === 1 ? 'línea' : 'líneas'
                          }`}
                          variant="outlined"
                          sx={{
                            height: 22,
                            color: '#cbd5e1',
                            borderColor: 'rgba(148,163,184,0.22)',
                            bgcolor: 'rgba(15,23,42,0.34)',
                            fontSize: '0.7rem',
                            fontWeight: 800,
                          }}
                        />

                        <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                          Produciendo {processGroup.totals.producing} · Paradas{' '}
                          {processGroup.totals.stopped} · Sin actividad{' '}
                          {processGroup.totals.idle +
                            processGroup.totals.validated +
                            processGroup.totals.exported}
                        </Typography>
                      </Stack>

                      {processGroup.totals.nc > 0 ? (
                        <Chip
                          size="small"
                          color="error"
                          variant="outlined"
                          label={`NC ${processGroup.totals.nc}`}
                          sx={{ fontWeight: 850 }}
                        />
                      ) : null}
                    </Stack>

                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          md: 'repeat(2, minmax(0, 1fr))',
                          xl: 'repeat(4, minmax(0, 1fr))',
                        },
                        gap: 1.25,
                      }}
                    >
                      {processGroup.groups.map((group) => (
                        <FactoryLineCard
                          key={group.key}
                          group={group}
                          clockTick={clockTick}
                          operationSettings={operationSettingsQuery.data}
                          onOpenDetail={setSelectedGroup}
                        />
                      ))}
                    </Box>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </Stack>
      ) : visibleControlTab === 'history' ? (
        <Stack spacing={3}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              border: '1px solid rgba(148,163,184,0.16)',
              bgcolor: 'rgba(15,23,42,0.58)',
              p: 2,
            }}
          >
            <Stack spacing={2}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                spacing={1.5}
              >
                <Stack spacing={0.5}>
                  <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                    Histórico de producción
                  </Typography>
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                    Revisa partes pendientes de supervisión o consulta la producción registrada por fecha.
                  </Typography>
                </Stack>

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  sx={{
                    width: { xs: '100%', md: 'auto' },
                    borderRadius: 3,
                    bgcolor: 'rgba(2,6,23,0.28)',
                    p: 0.75,
                  }}
                >
                  {[
                    { value: 'review', label: 'Revisión' },
                    { value: 'by_date', label: 'Por fecha' },
                  ].map((item) => {
                    const selected = historyMode === item.value;

                    return (
                      <Button
                        key={item.value}
                        variant={selected ? 'contained' : 'text'}
                        onClick={() => setHistoryMode(item.value as HistoryMode)}
                        sx={{
                          borderRadius: 2,
                          fontWeight: 850,
                          textTransform: 'none',
                          color: selected ? '#020617' : '#cbd5e1',
                          bgcolor: selected ? '#38bdf8' : 'transparent',
                          '&:hover': {
                            bgcolor: selected ? '#7dd3fc' : 'rgba(148,163,184,0.08)',
                          },
                        }}
                      >
                        {item.label}
                      </Button>
                    );
                  })}
                </Stack>
              </Stack>

              {historyMode === 'review' ? (
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1.25}
                  alignItems={{ xs: 'stretch', md: 'center' }}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <TextField
                    select
                    size="small"
                    label="Estado de revisión"
                    value={reviewStatusFilter}
                    onChange={(event) =>
                      setReviewStatusFilter(event.target.value as ReviewBoardFilterMode)
                    }
                    sx={{ minWidth: 220 }}
                  >
                    <MenuItem value="unvalidated">Sin validar</MenuItem>
                    <MenuItem value="all">Todos</MenuItem>
                    <MenuItem value="pending_revalidation">Pendientes de revalidación</MenuItem>
                    <MenuItem value="validated">Validados</MenuItem>
                    <MenuItem value="exported">Exportados</MenuItem>
                  </TextField>

                  <TextField
                    select
                    size="small"
                    label="Turno"
                    value={shift}
                    onChange={(event) =>
                      setShift(event.target.value as 'all' | 'morning' | 'afternoon' | 'night')
                    }
                    sx={{ minWidth: 140 }}
                  >
                    <MenuItem value="all">Todos</MenuItem>
                    <MenuItem value="morning">Mañana</MenuItem>
                    <MenuItem value="afternoon">Tarde</MenuItem>
                    <MenuItem value="night">Noche</MenuItem>
                  </TextField>

                  <LocalizationProvider
                    dateAdapter={AdapterDateFns}
                    adapterLocale={es}
                  >
                    <DatePicker
                      label="Desde"
                      value={reviewDateFrom ? parseISO(reviewDateFrom) : null}
                      onChange={(value) => {
                        const nextDateFrom = value
                          ? format(value, 'yyyy-MM-dd')
                          : '';

                        setReviewDateFrom(nextDateFrom);

                        if (
                          nextDateFrom &&
                          reviewDateTo &&
                          nextDateFrom > reviewDateTo
                        ) {
                          setReviewDateTo(nextDateFrom);
                        }
                      }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          sx: {
                            minWidth: 160,
                            '& .MuiInputBase-input': { color: '#f8fafc' },
                            '& .MuiInputLabel-root': { color: '#94a3b8' },
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(148,163,184,0.3)',
                            },
                            '& .MuiSvgIcon-root': { color: '#94a3b8' },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(148,163,184,0.6)',
                            },
                          },
                        },
                      }}
                    />

                    <DatePicker
                      label="Hasta"
                      value={reviewDateTo ? parseISO(reviewDateTo) : null}
                      minDate={
                        reviewDateFrom
                          ? parseISO(reviewDateFrom)
                          : undefined
                      }
                      onChange={(value) =>
                        setReviewDateTo(
                          value ? format(value, 'yyyy-MM-dd') : '',
                        )
                      }
                      slotProps={{
                        textField: {
                          size: 'small',
                          sx: {
                            minWidth: 160,
                            '& .MuiInputBase-input': { color: '#f8fafc' },
                            '& .MuiInputLabel-root': { color: '#94a3b8' },
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(148,163,184,0.3)',
                            },
                            '& .MuiSvgIcon-root': { color: '#94a3b8' },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(148,163,184,0.6)',
                            },
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>

                  <TextField
                    select
                    size="small"
                    label="Línea / puesto"
                    value={reviewLineId}
                    onChange={(event) => setReviewLineId(event.target.value)}
                    sx={{ minWidth: 190 }}
                  >
                    <MenuItem value="all">Todas las líneas</MenuItem>
                    {configuredLines.map((line) => (
                      <MenuItem key={line.id} value={line.id}>
                        {line.code} · {line.name}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    size="small"
                    label="Buscar operario"
                    value={reviewOperatorSearchText}
                    onChange={(event) =>
                      setReviewOperatorSearchText(event.target.value)
                    }
                    inputProps={{ maxLength: 100 }}
                    sx={{ minWidth: 170 }}
                  />

                  <Button
                    variant="outlined"
                    startIcon={<RefreshRoundedIcon />}
                    onClick={() =>
                      Promise.all([
                        reviewBoardQuery.refetch(),
                        reviewBoardSummaryQuery.refetch(),
                      ])
                    }
                    disabled={
                      reviewBoardQuery.isFetching ||
                      reviewBoardSummaryQuery.isFetching
                    }
                  >
                    Actualizar bandeja
                  </Button>
                </Stack>
              ) : (
            <Stack spacing={2}>
              <Stack spacing={0.5}>
                <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                  Histórico de producción
                </Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                  Consulta qué partes, órdenes y paradas se registraron para una fecha, turno o estado concreto.
                </Typography>
              </Stack>

              <Stack
                direction={{ xs: 'column', xl: 'row' }}
                spacing={1.25}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', xl: 'center' }}
              >
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1.25}
                alignItems={{ xs: 'stretch', md: 'center' }}
                flexWrap="wrap"
                useFlexGap
              >
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  adapterLocale={es}
                >
                  <DatePicker
                    label="Fecha"
                    value={reportDate ? parseISO(reportDate) : null}
                    onChange={(value) =>
                      setReportDate(
                        value ? format(value, 'yyyy-MM-dd') : '',
                      )
                    }
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: {
                          minWidth: 160,
                          '& .MuiInputBase-input': { color: '#f8fafc' },
                          '& .MuiInputLabel-root': { color: '#94a3b8' },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(148,163,184,0.3)',
                          },
                          '& .MuiSvgIcon-root': { color: '#94a3b8' },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(148,163,184,0.6)',
                          },
                        },
                      },
                    }}
                  />
                </LocalizationProvider>

                <TextField
                  select
                  size="small"
                  label="Turno"
                  value={shift}
                  onChange={(event) =>
                    setShift(
                      event.target.value as
                        | 'all'
                        | 'morning'
                        | 'afternoon'
                        | 'night',
                    )
                  }
                  sx={{ minWidth: 140 }}
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="morning">Mañana</MenuItem>
                  <MenuItem value="afternoon">Tarde</MenuItem>
                  <MenuItem value="night">Noche</MenuItem>
                </TextField>

                <TextField
                  select
                  size="small"
                  label="Estado parte"
                  value={statusFilter}
                  onChange={(event) =>
                    setStatusFilter(
                      event.target.value as
                        | 'all'
                        | 'draft'
                        | 'validated'
                        | 'exported',
                    )
                  }
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="draft">Borrador</MenuItem>
                  <MenuItem value="validated">Validado</MenuItem>
                  <MenuItem value="exported">Exportado</MenuItem>
                </TextField>

                <Button
                  variant="outlined"
                  startIcon={<RefreshRoundedIcon />}
                  onClick={() => factoryControlQuery.refetch()}
                  disabled={factoryControlQuery.isFetching}
                >
                  Aplicar filtros
                </Button>
              </Stack>

                <Stack
                  direction="row"
                  spacing={0.5}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{
                    alignSelf: { xs: 'flex-start', xl: 'center' },
                    borderRadius: 2.5,
                    border: '1px solid rgba(148,163,184,0.14)',
                    bgcolor: 'rgba(2,6,23,0.28)',
                    p: 0.5,
                  }}
                >
                  {(
                    [
                      { key: 'summary', label: 'Resumen' },
                      { key: 'shifts', label: 'Turnos' },
                      { key: 'operators', label: 'Operarios' },
                      { key: 'products', label: 'Productos' },
                    ] as { key: PerformanceView; label: string }[]
                  ).map((item) => (
                    <Button
                      key={item.key}
                      size="small"
                      variant={
                        performanceView === item.key
                          ? 'contained'
                          : 'text'
                      }
                      onClick={() => setPerformanceView(item.key)}
                      sx={{
                        minWidth: 0,
                        borderRadius: 2,
                        px: 1.25,
                        textTransform: 'none',
                        fontWeight: 850,
                      }}
                    >
                      {item.label}
                    </Button>
                  ))}
                </Stack>
              </Stack>
            </Stack>
          )}
            </Stack>
          </Paper>

          {historyMode === 'review' ? (
            <Stack spacing={2}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(251,191,36,0.24)',
                  bgcolor: 'rgba(120,53,15,0.14)',
                  px: 2,
                  py: 1.75,
                }}
              >
                <Stack
                  direction={{ xs: 'column', lg: 'row' }}
                  spacing={1.5}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', lg: 'center' }}
                >
                  <Stack spacing={0.35}>
                    <Typography sx={{ color: '#94a3b8', fontSize: '0.76rem', fontWeight: 850 }}>
                      Partes sin validar
                    </Typography>
                    <Typography sx={{ color: '#f8fafc', fontSize: '1.9rem', fontWeight: 950 }}>
                      {reviewBoardSummary.unvalidated}
                    </Typography>
                    <Typography sx={{ color: '#94a3b8', fontSize: '0.82rem' }}>
                      {reviewBoardSummaryQuery.isFetching
                        ? 'Actualizando resumen...'
                        : 'Partes pendientes de cierre administrativo'}
                    </Typography>
                  </Stack>

                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    useFlexGap
                    flexWrap="wrap"
                  >
                    <Chip
                      label={`Pendientes de validación: ${reviewBoardSummary.pending_administrative_action}`}
                      variant="outlined"
                      sx={{
                        color: '#fbbf24',
                        borderColor: 'rgba(251,191,36,0.28)',
                        bgcolor: 'rgba(120,53,15,0.18)',
                        fontWeight: 850,
                      }}
                    />
                    <Chip
                      label={`Pendientes de revalidación: ${reviewBoardSummary.pending_revalidation}`}
                      variant="outlined"
                      sx={{
                        color: '#fca5a5',
                        borderColor: 'rgba(248,113,113,0.28)',
                        bgcolor: 'rgba(127,29,29,0.18)',
                        fontWeight: 850,
                      }}
                    />
                  </Stack>
                </Stack>
              </Paper>

              {reviewBoardQuery.isError && (
                <Alert severity="error">No se pudo cargar la bandeja de revisión.</Alert>
              )}

              {!reviewBoardQuery.isFetching && reviewBoardItems.length === 0 ? (
                <Alert severity="info" variant="outlined">
                  No hay partes para el estado de revisión seleccionado.
                </Alert>
              ) : (
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 4,
                    border: '1px solid rgba(148,163,184,0.16)',
                    bgcolor: 'rgba(15,23,42,0.58)',
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ overflowX: 'auto' }}>
                    <Box sx={{ minWidth: 980 }}>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '120px 130px 1.4fr 1fr 1.1fr 1.4fr 120px',
                          gap: 1,
                          px: 2,
                          py: 1.25,
                          borderBottom: '1px solid rgba(148,163,184,0.16)',
                          bgcolor: 'rgba(15,23,42,0.78)',
                        }}
                      >
                        {(
                          [
                            { label: 'Fecha', field: 'report_date' },
                            { label: 'Turno', field: 'shift' },
                            { label: 'Línea', field: 'line_or_center' },
                            { label: 'Operario', field: null },
                            { label: 'Estado', field: null },
                            { label: 'Motivo', field: null },
                            { label: 'Acción', field: null },
                          ] as {
                            label: string;
                            field: 'report_date' | 'shift' | 'line_or_center' | null;
                          }[]
                        ).map(({ label, field }) =>
                          field ? (
                            <Box
                              key={label}
                              onClick={() => handleReviewBoardSort(field)}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                cursor: 'pointer',
                                userSelect: 'none',
                                '&:hover .sort-label': { color: '#e2e8f0' },
                              }}
                            >
                              <Typography
                                className="sort-label"
                                sx={{
                                  color:
                                    reviewBoardSortField === field ? '#f8fafc' : '#94a3b8',
                                  fontSize: '0.74rem',
                                  fontWeight: 900,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.04em',
                                  transition: 'color 0.15s',
                                }}
                              >
                                {label}
                              </Typography>
                              <Typography
                                sx={{
                                  color:
                                    reviewBoardSortField === field ? '#94a3b8' : 'transparent',
                                  fontSize: '0.7rem',
                                  lineHeight: 1,
                                }}
                              >
                                {reviewBoardSortDir === 'asc' ? '▲' : '▼'}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography
                              key={label}
                              sx={{
                                color: '#94a3b8',
                                fontSize: '0.74rem',
                                fontWeight: 900,
                                textTransform: 'uppercase',
                                letterSpacing: '0.04em',
                              }}
                            >
                              {label}
                            </Typography>
                          ),
                        )}
                      </Box>

                      {reviewBoardItems.map((item: ProductionReportReviewBoardItem) => {
                        const tone = getReviewStatusTone(item.review_status);

                        return (
                          <Box
                            key={item.report_id}
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: '120px 130px 1.4fr 1fr 1.1fr 1.4fr 120px',
                              gap: 1,
                              alignItems: 'center',
                              px: 2,
                              py: 1.35,
                              borderBottom: '1px solid rgba(148,163,184,0.10)',
                              '&:hover': {
                                bgcolor: 'rgba(15,23,42,0.72)',
                              },
                            }}
                          >
                            <Typography sx={{ color: '#e2e8f0', fontWeight: 850 }}>
                              {item.report_date}
                            </Typography>

                            <Typography sx={{ color: '#cbd5e1' }}>
                              {getShiftLabel(item.shift)}
                            </Typography>

                            <Stack spacing={0.25}>
                              <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                                {item.line_or_center || 'Sin línea'}
                              </Typography>
                              <Typography sx={{ color: '#64748b', fontSize: '0.78rem' }}>
                                {item.totals.production_lines_count} órdenes · {item.totals.stops_count} paradas
                              </Typography>
                            </Stack>

                            <Typography sx={{ color: '#cbd5e1' }}>
                              {item.operator_number || '—'}
                            </Typography>

                            <Stack spacing={0.5} alignItems="flex-start">
                              <Chip
                                size="small"
                                label={
                                  item.review_status === 'pending_administrative_action' ||
                                  item.review_status === 'pending_revalidation'
                                    ? 'Sin validar'
                                    : getReviewStatusLabel(item.review_status)
                                }
                                variant="outlined"
                                sx={{
                                  color: tone.color,
                                  borderColor: tone.border,
                                  bgcolor: tone.bg,
                                  fontWeight: 850,
                                }}
                              />
                              <Typography sx={{ color: '#64748b', fontSize: '0.76rem' }}>
                                {item.review_status === 'pending_revalidation'
                                  ? 'Reabierto tras una validación previa'
                                  : item.review_status === 'pending_administrative_action'
                                    ? 'Pendiente de validación inicial'
                                    : `Parte: ${getReportStatusLabel(item.report_status)}`}
                              </Typography>
                            </Stack>

                            <Typography sx={{ color: '#cbd5e1', fontSize: '0.84rem' }}>
                              {item.review_reasons.length > 0
                                ? item.review_reasons.join(' · ')
                                : 'Sin observaciones administrativas'}
                            </Typography>

                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setSelectedReviewItem(item)}
                              sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 850,
                              }}
                            >
                              Revisar
                            </Button>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                </Paper>
              )}
            </Stack>
          ) : (
            <Stack spacing={2}>
              <Paper
                elevation={0}
              sx={{
                borderRadius: 4,
                border: '1px solid rgba(56,189,248,0.22)',
                bgcolor: 'rgba(8,47,73,0.22)',
                p: 2,
              }}
            >
            <Stack spacing={2}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                spacing={1.5}
              >
                <Stack spacing={0.35}>
                  <Typography sx={{ color: '#f8fafc', fontWeight: 950 }}>
                    Centro de revisión del día
                  </Typography>
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                    Vista rápida para detectar qué partes necesitan supervisión antes de validarse.
                  </Typography>
                </Stack>

                <Chip
                  label={`${historyReviewSummary.reportsCount} partes en el filtro`}
                  variant="outlined"
                  sx={{
                    width: 'fit-content',
                    color: '#bae6fd',
                    borderColor: 'rgba(56,189,248,0.35)',
                    fontWeight: 850,
                  }}
                />
              </Stack>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, minmax(0, 1fr))',
                    lg: 'repeat(4, minmax(0, 1fr))',
                  },
                  gap: 1.25,
                }}
              >
                {[
                  {
                    label: 'Sin validar',
                    value: historyReviewSummary.draftReportsCount,
                    helper: 'Partes pendientes de revisión',
                    tone: 'warning',
                  },
                  {
                    label: 'Con incidencias',
                    value: historyReviewSummary.groupsWithWarningsCount,
                    helper: 'Líneas con avisos o anomalías',
                    tone: 'error',
                  },
                  {
                    label: 'Eventos abiertos',
                    value: historyReviewSummary.groupsWithOpenEventsCount,
                    helper: 'Órdenes o paradas sin cierre',
                    tone: 'error',
                  },
                  {
                    label: 'Listos para revisar',
                    value: historyReviewSummary.readyToReviewGroupsCount,
                    helper: 'Sin incidencias visibles',
                    tone: 'success',
                  },
                ].map((item) => (
                  <Paper
                    key={item.label}
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border:
                        item.tone === 'error'
                          ? '1px solid rgba(248,113,113,0.28)'
                          : item.tone === 'warning'
                            ? '1px solid rgba(251,191,36,0.28)'
                            : '1px solid rgba(34,197,94,0.24)',
                      bgcolor:
                        item.tone === 'error'
                          ? 'rgba(127,29,29,0.16)'
                          : item.tone === 'warning'
                            ? 'rgba(120,53,15,0.16)'
                            : 'rgba(20,83,45,0.16)',
                      px: 1.75,
                      py: 1.5,
                    }}
                  >
                    <Typography sx={{ color: '#94a3b8', fontSize: '0.76rem', fontWeight: 850 }}>
                      {item.label}
                    </Typography>
                    <Typography sx={{ color: '#f8fafc', fontSize: '1.7rem', fontWeight: 950 }}>
                      {item.value}
                    </Typography>
                    <Typography sx={{ color: '#94a3b8', fontSize: '0.78rem', mt: 0.25 }}>
                      {item.helper}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            </Stack>
          </Paper>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                lg: 'repeat(4, minmax(0, 1fr))',
              },
              gap: 1.5,
            }}
          >
            {[
              {
                label: 'Partes',
                value: items.filter((item) => item.report_id).length,
                helper: 'Registros encontrados',
              },
              {
                label: 'Órdenes',
                value: lineGroups.reduce(
                  (sum, group) => sum + group.totals.production_lines_count,
                  0,
                ),
                helper: 'Órdenes del filtro',
              },
              {
                label: 'Paradas',
                value: lineGroups.reduce((sum, group) => sum + group.totals.stops_count, 0),
                helper: 'Paradas registradas',
              },
              {
                label: 'Líneas con parte',
                value: lineGroups.filter((group) => !group.has_configured_line_without_report)
                  .length,
                helper: `${lineGroups.length} líneas visibles`,
              },
            ].map((item) => (
              <Paper
                key={item.label}
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(148,163,184,0.16)',
                  bgcolor: 'rgba(15,23,42,0.58)',
                  px: 2,
                  py: 1.75,
                }}
              >
                <Typography sx={{ color: '#94a3b8', fontSize: '0.78rem', fontWeight: 800 }}>
                  {item.label}
                </Typography>
                <Typography sx={{ color: '#f8fafc', fontSize: '1.55rem', fontWeight: 950 }}>
                  {item.value}
                </Typography>
                <Typography sx={{ color: '#64748b', fontSize: '0.78rem', mt: 0.25 }}>
                  {item.helper}
                </Typography>
              </Paper>
            ))}
          </Box>

          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              border: '1px solid rgba(148,163,184,0.16)',
              bgcolor: 'rgba(15,23,42,0.58)',
              p: 2,
            }}
          >
            <Stack spacing={2}>
              <Stack spacing={0.4}>
                <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                  Partes y actividad del filtro
                </Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                  Consulta rápida por línea para revisar partes, órdenes, paradas y calidad del día o turno seleccionado.
                </Typography>
              </Stack>

              {!factoryControlQuery.isFetching && lineGroups.length === 0 ? (
                <Alert severity="info" variant="outlined">
                  No hay partes ni actividad para los filtros seleccionados.
                </Alert>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Box
                    sx={{
                      minWidth: 980,
                      borderRadius: 3,
                      border: '1px solid rgba(148,163,184,0.12)',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1.2fr 0.8fr 1fr 0.8fr 0.75fr 0.75fr 0.9fr 0.8fr',
                        gap: 1,
                        px: 1.5,
                        py: 1,
                        bgcolor: 'rgba(2,6,23,0.48)',
                      }}
                    >
                      {[
                        'Línea',
                        'Estado',
                        'Turno / parte',
                        'Operario',
                        'Órdenes',
                        'Paradas',
                        'Calidad',
                        'Detalle',
                      ].map((label) => (
                        <Typography
                          key={label}
                          sx={{
                            color: '#94a3b8',
                            fontSize: '0.72rem',
                            fontWeight: 900,
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                          }}
                        >
                          {label}
                        </Typography>
                      ))}
                    </Box>

                    {lineGroups.map((group) => {
                      const currentItem = getCurrentActivityItem(group);

                      return (
                        <Box
                          key={group.key}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns:
                              '1.2fr 0.8fr 1fr 0.8fr 0.75fr 0.75fr 0.9fr 0.8fr',
                            gap: 1,
                            px: 1.5,
                            py: 1.15,
                            borderTop: '1px solid rgba(148,163,184,0.10)',
                            bgcolor: group.has_configured_line_without_report
                              ? 'rgba(15,23,42,0.18)'
                              : 'rgba(15,23,42,0.28)',
                            alignItems: 'center',
                          }}
                        >
                          <Stack spacing={0.12}>
                            <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
                              {group.label}
                            </Typography>
                            <Typography sx={{ color: '#64748b', fontSize: '0.74rem' }}>
                              {group.configuredLine?.code || currentItem.line_or_center || '—'}
                            </Typography>
                          </Stack>

                          <Chip
                            size="small"
                            label={group.status_label}
                            color={getStatusChipColor(group.current_status)}
                            variant={group.has_configured_line_without_report ? 'outlined' : 'filled'}
                            sx={{ width: 'fit-content', fontWeight: 850 }}
                          />

                          <Stack spacing={0.12}>
                            <Typography sx={{ color: '#e2e8f0', fontWeight: 800 }}>
                              {group.has_configured_line_without_report
                                ? 'Sin parte'
                                : getShiftLabel(currentItem.shift)}
                            </Typography>
                            <Typography sx={{ color: '#64748b', fontSize: '0.74rem' }}>
                              {currentItem.report_status || '—'}
                            </Typography>
                          </Stack>

                          <Typography sx={{ color: '#e2e8f0', fontWeight: 800 }}>
                            {group.has_configured_line_without_report
                              ? '—'
                              : currentItem.operator_number || '—'}
                          </Typography>

                          <Stack spacing={0.1}>
                            <Typography sx={{ color: '#e2e8f0', fontWeight: 850 }}>
                              {group.totals.production_lines_count}
                            </Typography>
                            <Typography sx={{ color: '#64748b', fontSize: '0.72rem' }}>
                              {group.totals.closed_lines_count} cerradas
                            </Typography>
                          </Stack>

                          <Typography sx={{ color: '#e2e8f0', fontWeight: 850 }}>
                            {group.totals.stops_count}
                          </Typography>

                          <Stack spacing={0.1}>
                            <Typography sx={{ color: '#e2e8f0', fontWeight: 850 }}>
                              C {group.totals.quality_c_count} · NC {group.totals.quality_nc_count}
                            </Typography>
                            <Typography sx={{ color: '#64748b', fontSize: '0.72rem' }}>
                              Saltados {group.totals.quality_na_count}
                            </Typography>
                          </Stack>

                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<VisibilityRoundedIcon />}
                            disabled={group.has_configured_line_without_report}
                            onClick={() => setSelectedGroup(group)}
                          >
                            Ver
                          </Button>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Stack>
          </Paper>
            </Stack>
          )}
        </Stack>
      ) : visibleControlTab === 'weekly' ? (
        <IndustrialCostWeeklyActivityView configuredLines={configuredLines} />
      ) : visibleControlTab === 'intelligence' ? (
        <IndustrialCostProductionIntelligenceView configuredLines={configuredLines} />
      ) : visibleControlTab === 'performance' ? (

        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
          <Stack spacing={3}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              border: '1px solid rgba(148,163,184,0.16)',
              bgcolor: 'rgba(15,23,42,0.58)',
              p: 2,
            }}
          >
            <Stack spacing={2}>
              <Stack spacing={0.5}>
                <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                  Rendimiento de producción
                </Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                  KPIs agregados calculados sobre órdenes cerradas del periodo seleccionado.
                  Comparativa contra {previousPerformanceRange.dateFrom} —{' '}
                  {previousPerformanceRange.dateTo}.
                </Typography>
              </Stack>

              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1.25}
                alignItems={{ xs: 'stretch', md: 'center' }}
                flexWrap="wrap"
                useFlexGap
              >
                <TextField
                  select
                  size="small"
                  label="Periodo"
                  value={performancePeriodPreset}
                  onChange={(event) =>
                    handlePerformancePeriodPresetChange(
                      event.target.value as PerformancePeriodPreset,
                    )
                  }
                  sx={{ minWidth: 170 }}
                >
                  <MenuItem value="today">Hoy</MenuItem>
                  <MenuItem value="week">Semana actual</MenuItem>
                  <MenuItem value="month">Mes actual</MenuItem>
                  <MenuItem value="year">Año actual</MenuItem>
                  <MenuItem value="custom">Personalizado</MenuItem>
                </TextField>

                <DatePicker
                  label="Desde"
                  value={performanceDateFrom ? parseISO(performanceDateFrom) : null}
                  onChange={(v) => {
                    setPerformancePeriodPreset('custom');
                    setPerformanceDateFrom(v ? format(v, 'yyyy-MM-dd') : '');
                  }}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: {
                        minWidth: 160,
                        '& .MuiInputBase-input': { color: '#f8fafc' },
                        '& .MuiInputLabel-root': { color: '#94a3b8' },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(148,163,184,0.3)' },
                        '& .MuiSvgIcon-root': { color: '#94a3b8' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(148,163,184,0.6)' },
                      },
                    },
                  }}
                />

                <DatePicker
                  label="Hasta"
                  value={performanceDateTo ? parseISO(performanceDateTo) : null}
                  onChange={(v) => {
                    setPerformancePeriodPreset('custom');
                    setPerformanceDateTo(v ? format(v, 'yyyy-MM-dd') : '');
                  }}
                  minDate={performanceDateFrom ? parseISO(performanceDateFrom) : undefined}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: {
                        minWidth: 160,
                        '& .MuiInputBase-input': { color: '#f8fafc' },
                        '& .MuiInputLabel-root': { color: '#94a3b8' },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(148,163,184,0.3)' },
                        '& .MuiSvgIcon-root': { color: '#94a3b8' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(148,163,184,0.6)' },
                      },
                    },
                  }}
                />

                <Box sx={{ flexGrow: 1 }} />

                <Stack
                  direction="row"
                  spacing={0.5}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{
                    borderRadius: 2.5,
                    border: '1px solid rgba(148,163,184,0.14)',
                    bgcolor: 'rgba(2,6,23,0.28)',
                    p: 0.5,
                  }}
                >
                  {(
                    [
                      { key: 'summary', label: 'Resumen' },
                      { key: 'shifts', label: 'Turnos' },
                      { key: 'operators', label: 'Operarios' },
                      { key: 'products', label: 'Productos' },
                    ] as { key: PerformanceView; label: string }[]
                  ).map((item) => (
                    <Button
                      key={item.key}
                      size="small"
                      variant={
                        performanceView === item.key
                          ? 'contained'
                          : 'text'
                      }
                      onClick={() => setPerformanceView(item.key)}
                      sx={{
                        minWidth: 0,
                        borderRadius: 2,
                        px: 1.25,
                        textTransform: 'none',
                        fontWeight: 850,
                      }}
                    >
                      {item.label}
                    </Button>
                  ))}
                </Stack>
              </Stack>
            </Stack>
          </Paper>

          {factorySummaryQuery.isError ? (
            <Alert severity="error" variant="outlined">
              No se pudo cargar el resumen de rendimiento.
            </Alert>
          ) : null}

          {previousFactorySummaryQuery.isError ? (
            <Alert severity="warning" variant="outlined">
              No se pudo cargar la comparativa con el periodo anterior. Los KPIs actuales siguen siendo válidos.
            </Alert>
          ) : null}

          {performanceView === 'summary' ? (
            <>
          {/* KPIs principales de rendimiento temporal */}
          {(() => {
            const totals = factorySummaryQuery.data?.totals;
            const prevTotals = previousFactorySummaryQuery.data?.totals;

            const closedOrders = totals?.closed_orders_count ?? 0;
            const periodDays = getDaysBetweenInclusive(performanceDateFrom, performanceDateTo);

            const productionReal = totals?.production_duration_minutes ?? 0;
            const productionEstimated = totals?.estimated_production_duration_minutes ?? 0;
            const productionDeviation = totals?.production_deviation_minutes ?? 0;
            const productionDeviationPct =
              productionEstimated > 0 ? (productionDeviation / productionEstimated) * 100 : null;
            const productionIsGood =
              productionDeviationPct !== null && productionDeviationPct <= 0;
            const productionIsNeutral =
              productionDeviationPct !== null &&
              productionDeviationPct > 0 &&
              productionDeviationPct <= 10;

            const setupReal = totals?.setup_duration_minutes ?? 0;
            const operativeNet = totals?.real_duration_minutes ?? setupReal + productionReal;
            const stopped = totals?.stop_duration_minutes ?? 0;
            const operativeAndStopped = operativeNet + stopped;
            const operativePct =
              operativeAndStopped > 0 ? Math.round((operativeNet / operativeAndStopped) * 100) : null;
            const stoppedPct =
              operativeAndStopped > 0 ? Math.round((stopped / operativeAndStopped) * 100) : null;

            const setupSharePct =
              operativeNet > 0 ? (setupReal / operativeNet) * 100 : null;
            const setupShareIsGood = setupSharePct !== null && setupSharePct <= 5;
            const setupShareIsNeutral =
              setupSharePct !== null && setupSharePct > 5 && setupSharePct <= 15;

            const stoppedSharePct =
              operativeAndStopped > 0 ? (stopped / operativeAndStopped) * 100 : null;
            const stoppedShareIsGood = stoppedSharePct !== null && stoppedSharePct <= 2;
            const stoppedShareIsNeutral =
              stoppedSharePct !== null && stoppedSharePct > 2 && stoppedSharePct <= 8;

            const totalOccupied =
              totals?.total_occupied_duration_minutes ??
              setupReal + productionReal + stopped;

            const setupPctForBar =
              operativeNet > 0 ? Math.round((setupReal / operativeNet) * 100) : 0;
            const productionPctForBar =
              operativeNet > 0 ? Math.max(0, 100 - setupPctForBar) : 0;

            const setupOccupiedPct =
              totalOccupied > 0 ? Math.round((setupReal / totalOccupied) * 100) : 0;
            const productionOccupiedPct =
              totalOccupied > 0 ? Math.round((productionReal / totalOccupied) * 100) : 0;
            const stoppedOccupiedPct =
              totalOccupied > 0 ? Math.max(0, 100 - setupOccupiedPct - productionOccupiedPct) : 0;

            const averageSetupPerOrder =
              closedOrders > 0 ? setupReal / closedOrders : null;
            const stopsPerDay =
              periodDays > 0 ? (totals?.stops_count ?? 0) / periodDays : null;
            const averageStopDuration = totals?.average_stop_duration_minutes ?? null;
            const estimationCoveragePct =
              closedOrders > 0
                ? ((totals?.estimated_orders_count ?? 0) / closedOrders) * 100
                : null;
            const estimationCoverageIsGood =
              estimationCoveragePct !== null && estimationCoveragePct >= 95;
            const estimationCoverageIsNeutral =
              estimationCoveragePct !== null &&
              estimationCoveragePct >= 80 &&
              estimationCoveragePct < 95;

            return (
              <Stack spacing={1.5}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, minmax(0, 1fr))',
                      lg: 'repeat(4, minmax(0, 1fr))',
                    },
                    gap: 1.5,
                  }}
                >
                  <Tooltip
                    arrow
                    placement="top"
                    title={
                      <Stack spacing={0.75}>
                        <Typography sx={{ fontWeight: 900 }}>Qué mide</Typography>
                        <Typography sx={{ fontSize: '0.78rem' }}>
                          Compara solo tiempo de fabricación real neta contra fabricación teórica.
                        </Typography>
                        <Typography sx={{ fontWeight: 900 }}>Incluye / excluye</Typography>
                        <Typography sx={{ fontSize: '0.78rem' }}>
                          Excluye preparación y paradas internas.
                        </Typography>
                        <Typography sx={{ fontWeight: 900 }}>Fórmula</Typography>
                        <Typography sx={{ fontSize: '0.78rem' }}>
                          Producción real neta - producción teórica.
                        </Typography>
                      </Stack>
                    }
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        borderRadius: 3,
                        border: productionDeviationPct === null
                          ? '1px solid rgba(148,163,184,0.16)'
                          : productionIsGood
                            ? '1px solid rgba(34,197,94,0.28)'
                            : productionIsNeutral
                              ? '1px solid rgba(251,191,36,0.30)'
                              : '1px solid rgba(239,68,68,0.30)',
                        bgcolor: productionDeviationPct === null
                          ? 'rgba(15,23,42,0.58)'
                          : productionIsGood
                            ? 'rgba(20,83,45,0.18)'
                            : productionIsNeutral
                              ? 'rgba(120,53,15,0.16)'
                              : 'rgba(127,29,29,0.18)',
                        px: 2,
                        py: 1.75,
                        ...TITLE_ONLY_TOOLTIP_CARD_SX,
                      }}
                    >
                      <Typography sx={{ color: '#93c5fd', fontSize: '0.72rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        Producción real vs teórica
                      </Typography>

                      <Stack direction="row" spacing={1.5} alignItems="baseline" sx={{ mt: 0.5 }}>
                        <Typography sx={{ color: '#f8fafc', fontSize: '1.75rem', fontWeight: 950, lineHeight: 1.1 }}>
                          {formatEstimatedMinutes(productionReal)}
                        </Typography>
                        <Typography sx={{ color: '#64748b', fontSize: '0.9rem', fontWeight: 800 }}>
                          / {formatEstimatedMinutes(productionEstimated)}
                        </Typography>
                      </Stack>

                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.55 }}>
                        <Chip
                          size="small"
                          label={
                            productionDeviationPct !== null
                              ? `${productionDeviationPct > 0 ? '+' : ''}${formatPct(productionDeviationPct)}`
                              : '—'
                          }
                          sx={{
                            bgcolor: productionDeviationPct === null
                              ? 'rgba(148,163,184,0.1)'
                              : productionIsGood
                                ? 'rgba(34,197,94,0.15)'
                                : productionIsNeutral
                                  ? 'rgba(251,191,36,0.16)'
                                  : 'rgba(239,68,68,0.16)',
                            color: productionDeviationPct === null
                              ? '#94a3b8'
                              : productionIsGood
                                ? '#86efac'
                                : productionIsNeutral
                                  ? '#fde68a'
                                  : '#fca5a5',
                            border: productionDeviationPct === null
                              ? '1px solid rgba(148,163,184,0.15)'
                              : productionIsGood
                                ? '1px solid rgba(34,197,94,0.25)'
                                : productionIsNeutral
                                  ? '1px solid rgba(251,191,36,0.25)'
                                  : '1px solid rgba(239,68,68,0.25)',
                            fontWeight: 900,
                            fontSize: '0.72rem',
                            height: 20,
                          }}
                        />
                        <Typography sx={{ color: '#94a3b8', fontSize: '0.74rem' }}>
                          {formatSignedMinutes(productionDeviation)} vs fabricación teórica
                        </Typography>
                      </Stack>

                      <Typography sx={{ color: '#64748b', fontSize: '0.72rem', mt: 0.55 }}>
                        Sin preparación · sin paradas
                      </Typography>
                    </Paper>
                  </Tooltip>

                  <Tooltip
                    arrow
                    placement="top"
                    title={
                      <Stack spacing={0.75}>
                        <Typography sx={{ fontWeight: 900 }}>Qué mide</Typography>
                        <Typography sx={{ fontSize: '0.78rem' }}>
                          Cuánto tiempo operativo se consume preparando frente a fabricar.
                        </Typography>
                        <Typography sx={{ fontWeight: 900 }}>Incluye / excluye</Typography>
                        <Typography sx={{ fontSize: '0.78rem' }}>
                          Incluye preparación real y fabricación real. Excluye paradas.
                        </Typography>
                        <Typography sx={{ fontWeight: 900 }}>Fórmula</Typography>
                        <Typography sx={{ fontSize: '0.78rem' }}>
                          Preparación real / (preparación real + producción real).
                        </Typography>
                      </Stack>
                    }
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        borderRadius: 3,
                        border: setupSharePct === null
                          ? '1px solid rgba(148,163,184,0.16)'
                          : setupShareIsGood
                            ? '1px solid rgba(34,197,94,0.24)'
                            : setupShareIsNeutral
                              ? '1px solid rgba(251,191,36,0.30)'
                              : '1px solid rgba(249,115,22,0.34)',
                        bgcolor: 'rgba(15,23,42,0.58)',
                        px: 2,
                        py: 1.75,
                        ...TITLE_ONLY_TOOLTIP_CARD_SX,
                      }}
                    >
                      <Typography sx={{ color: '#fbbf24', fontSize: '0.72rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        Preparación / producción
                      </Typography>

                      <Stack direction="row" spacing={1.5} alignItems="baseline" sx={{ mt: 0.5 }}>
                        <Typography sx={{ color: '#f8fafc', fontSize: '1.75rem', fontWeight: 950, lineHeight: 1.1 }}>
                          {formatEstimatedMinutes(setupReal)}
                        </Typography>
                        <Typography sx={{ color: '#38bdf8', fontSize: '0.9rem', fontWeight: 800 }}>
                          / {formatEstimatedMinutes(productionReal)}
                        </Typography>
                      </Stack>

                      <Box sx={{ mt: 0.8, mb: 0.5 }}>
                        <Box sx={{ height: 7, borderRadius: 1, bgcolor: 'rgba(148,163,184,0.12)', overflow: 'hidden', display: 'flex' }}>
                          <Box sx={{ height: '100%', width: `${setupPctForBar}%`, bgcolor: '#fbbf24' }} />
                          <Box sx={{ height: '100%', width: `${productionPctForBar}%`, bgcolor: '#38bdf8' }} />
                        </Box>
                        <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.35 }}>
                          <Typography sx={{ color: '#fbbf24', fontSize: '0.68rem', fontWeight: 800 }}>
                            Prep. {formatPct(setupSharePct)}
                          </Typography>
                          <Typography sx={{ color: '#38bdf8', fontSize: '0.68rem', fontWeight: 800 }}>
                            Producción {formatPct(setupSharePct === null ? null : 100 - setupSharePct)}
                          </Typography>
                        </Stack>
                      </Box>

                      <Typography sx={{ color: '#64748b', fontSize: '0.72rem', mt: 0.25 }}>
                        Tiempo operativo neto: {formatEstimatedMinutes(operativeNet)}
                      </Typography>
                    </Paper>
                  </Tooltip>

                  <Tooltip
                    arrow
                    placement="top"
                    title={
                      <Stack spacing={0.75}>
                        <Typography sx={{ fontWeight: 900 }}>Qué mide</Typography>
                        <Typography sx={{ fontSize: '0.78rem' }}>
                          Relación entre tiempo operativo de fábrica y tiempo parado.
                        </Typography>
                        <Typography sx={{ fontWeight: 900 }}>Incluye / excluye</Typography>
                        <Typography sx={{ fontSize: '0.78rem' }}>
                          Operativo incluye preparación + fabricación. Parada queda separada.
                        </Typography>
                        <Typography sx={{ fontWeight: 900 }}>Fórmula</Typography>
                        <Typography sx={{ fontSize: '0.78rem' }}>
                          (preparación + producción) vs paradas.
                        </Typography>
                      </Stack>
                    }
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        borderRadius: 3,
                        border: stoppedSharePct === null
                          ? '1px solid rgba(148,163,184,0.16)'
                          : stoppedShareIsGood
                            ? '1px solid rgba(34,197,94,0.24)'
                            : stoppedShareIsNeutral
                              ? '1px solid rgba(251,191,36,0.30)'
                              : '1px solid rgba(239,68,68,0.32)',
                        bgcolor: 'rgba(15,23,42,0.58)',
                        px: 2,
                        py: 1.75,
                        ...TITLE_ONLY_TOOLTIP_CARD_SX,
                      }}
                    >
                      <Typography sx={{ color: '#34d399', fontSize: '0.72rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        Operativo vs parado
                      </Typography>

                      <Stack direction="row" spacing={1.5} alignItems="baseline" sx={{ mt: 0.5 }}>
                        <Typography sx={{ color: '#f8fafc', fontSize: '1.75rem', fontWeight: 950, lineHeight: 1.1 }}>
                          {formatEstimatedMinutes(operativeNet)}
                        </Typography>
                        <Typography sx={{ color: '#f87171', fontSize: '0.9rem', fontWeight: 800 }}>
                          / {formatEstimatedMinutes(stopped)}
                        </Typography>
                      </Stack>

                      {operativeAndStopped > 0 ? (
                        <Box sx={{ mt: 0.8, mb: 0.5 }}>
                          <Box sx={{ height: 7, borderRadius: 1, bgcolor: 'rgba(148,163,184,0.12)', overflow: 'hidden', display: 'flex' }}>
                            <Box sx={{ height: '100%', width: `${operativePct}%`, bgcolor: '#22c55e' }} />
                            <Box sx={{ height: '100%', width: `${stoppedPct}%`, bgcolor: '#ef4444' }} />
                          </Box>
                          <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.35 }}>
                            <Typography sx={{ color: '#86efac', fontSize: '0.68rem', fontWeight: 800 }}>
                              {operativePct}% operativo
                            </Typography>
                            <Typography sx={{ color: '#fca5a5', fontSize: '0.68rem', fontWeight: 800 }}>
                              {stoppedPct}% parado
                            </Typography>
                          </Stack>
                        </Box>
                      ) : null}

                      <Typography sx={{ color: '#64748b', fontSize: '0.72rem', mt: 0.25 }}>
                        Operativo = preparación + fabricación
                      </Typography>
                    </Paper>
                  </Tooltip>

                  <Tooltip
                    arrow
                    placement="top"
                    title={
                      <Stack spacing={0.75}>
                        <Typography sx={{ fontWeight: 900 }}>Qué mide</Typography>
                        <Typography sx={{ fontSize: '0.78rem' }}>
                          Capacidad total consumida por las órdenes cerradas.
                        </Typography>
                        <Typography sx={{ fontWeight: 900 }}>Incluye / excluye</Typography>
                        <Typography sx={{ fontSize: '0.78rem' }}>
                          Incluye preparación, fabricación y paradas internas.
                        </Typography>
                        <Typography sx={{ fontWeight: 900 }}>Fórmula</Typography>
                        <Typography sx={{ fontSize: '0.78rem' }}>
                          Fin de orden - inicio de orden.
                        </Typography>
                      </Stack>
                    }
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        borderRadius: 3,
                        border: '1px solid rgba(56,189,248,0.22)',
                        bgcolor: 'rgba(8,47,73,0.16)',
                        px: 2,
                        py: 1.75,
                        ...TITLE_ONLY_TOOLTIP_CARD_SX,
                      }}
                    >
                      <Typography sx={{ color: '#38bdf8', fontSize: '0.72rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        Total ocupado
                      </Typography>

                      <Typography sx={{ color: '#f8fafc', fontSize: '1.75rem', fontWeight: 950, lineHeight: 1.1, mt: 0.5 }}>
                        {formatEstimatedMinutes(totalOccupied)}
                      </Typography>

                      <Box sx={{ mt: 0.8, mb: 0.5 }}>
                        <Box sx={{ height: 7, borderRadius: 1, bgcolor: 'rgba(148,163,184,0.12)', overflow: 'hidden', display: 'flex' }}>
                          <Box sx={{ height: '100%', width: `${setupOccupiedPct}%`, bgcolor: '#fbbf24' }} />
                          <Box sx={{ height: '100%', width: `${productionOccupiedPct}%`, bgcolor: '#38bdf8' }} />
                          <Box sx={{ height: '100%', width: `${stoppedOccupiedPct}%`, bgcolor: '#ef4444' }} />
                        </Box>
                      </Box>

                      <Typography sx={{ color: '#94a3b8', fontSize: '0.72rem', mt: 0.55 }}>
                        Prep. {formatEstimatedMinutes(setupReal)} · Prod. {formatEstimatedMinutes(productionReal)} · Paradas {formatEstimatedMinutes(stopped)}
                      </Typography>

                      <Typography sx={{ color: '#64748b', fontSize: '0.72rem', mt: 0.35 }}>
                        {formatComparisonValue(
                          totals?.total_occupied_duration_minutes,
                          prevTotals?.total_occupied_duration_minutes,
                          formatEstimatedMinutes,
                        )}
                      </Typography>
                    </Paper>
                  </Tooltip>
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, minmax(0, 1fr))',
                      lg: 'repeat(4, minmax(0, 1fr))',
                    },
                    gap: 1.5,
                  }}
                >
                  <Tooltip
                    arrow
                    placement="top"
                    title={
                      <Stack spacing={0.75}>
                        <Typography sx={{ fontWeight: 900 }}>Fórmula</Typography>
                        <Typography sx={{ fontSize: '0.78rem' }}>
                          Preparación real neta / órdenes cerradas.
                        </Typography>
                      </Stack>
                    }
                  >
                    <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid rgba(251,191,36,0.20)', bgcolor: 'rgba(15,23,42,0.42)', px: 1.75, py: 1.35, ...TITLE_ONLY_TOOLTIP_CARD_SX }}>
                      <Typography sx={{ color: '#94a3b8', fontSize: '0.7rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        Preparación media
                      </Typography>
                      <Typography sx={{ color: '#f8fafc', fontSize: '1.28rem', fontWeight: 950, mt: 0.35 }}>
                        {formatEstimatedMinutes(averageSetupPerOrder)}
                      </Typography>
                      <Typography sx={{ color: '#64748b', fontSize: '0.74rem' }}>
                        total prep. {formatEstimatedMinutes(setupReal)}
                      </Typography>
                    </Paper>
                  </Tooltip>

                  <Tooltip
                    arrow
                    placement="top"
                    title={
                      <Stack spacing={0.75}>
                        <Typography sx={{ fontWeight: 900 }}>Fórmula</Typography>
                        <Typography sx={{ fontSize: '0.78rem' }}>
                          Número de paradas / días del periodo seleccionado.
                        </Typography>
                      </Stack>
                    }
                  >
                    <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid rgba(248,113,113,0.20)', bgcolor: 'rgba(15,23,42,0.42)', px: 1.75, py: 1.35, ...TITLE_ONLY_TOOLTIP_CARD_SX }}>
                      <Typography sx={{ color: '#94a3b8', fontSize: '0.7rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        Paradas / día
                      </Typography>
                      <Typography sx={{ color: '#f8fafc', fontSize: '1.28rem', fontWeight: 950, mt: 0.35 }}>
                        {formatDecimal(stopsPerDay)}
                      </Typography>
                      <Typography sx={{ color: '#64748b', fontSize: '0.74rem' }}>
                        {totals?.stops_count ?? 0} paradas en {periodDays} día{periodDays !== 1 ? 's' : ''}
                      </Typography>
                    </Paper>
                  </Tooltip>

                  <Tooltip
                    arrow
                    placement="top"
                    title={
                      <Stack spacing={0.75}>
                        <Typography sx={{ fontWeight: 900 }}>Fórmula</Typography>
                        <Typography sx={{ fontSize: '0.78rem' }}>
                          Duración total de paradas / número de paradas.
                        </Typography>
                      </Stack>
                    }
                  >
                    <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid rgba(248,113,113,0.20)', bgcolor: 'rgba(15,23,42,0.42)', px: 1.75, py: 1.35, ...TITLE_ONLY_TOOLTIP_CARD_SX }}>
                      <Typography sx={{ color: '#94a3b8', fontSize: '0.7rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        Tiempo medio parada
                      </Typography>
                      <Typography sx={{ color: '#f8fafc', fontSize: '1.28rem', fontWeight: 950, mt: 0.35 }}>
                        {formatEstimatedMinutes(averageStopDuration)}
                      </Typography>
                      <Typography sx={{ color: '#64748b', fontSize: '0.74rem' }}>
                        total paradas {formatEstimatedMinutes(stopped)}
                      </Typography>
                    </Paper>
                  </Tooltip>

                  <Tooltip
                    arrow
                    placement="top"
                    title={
                      <Stack spacing={0.75}>
                        <Typography sx={{ fontWeight: 900 }}>Qué mide</Typography>
                        <Typography sx={{ fontSize: '0.78rem' }}>
                          Calidad analítica del periodo: cuántas órdenes cerradas tienen estándar para comparar.
                        </Typography>
                        <Typography sx={{ fontWeight: 900 }}>Fórmula</Typography>
                        <Typography sx={{ fontSize: '0.78rem' }}>
                          Órdenes con estimación / órdenes cerradas.
                        </Typography>
                      </Stack>
                    }
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        borderRadius: 3,
                        border: estimationCoveragePct === null
                          ? '1px solid rgba(148,163,184,0.16)'
                          : estimationCoverageIsGood
                            ? '1px solid rgba(34,197,94,0.24)'
                            : estimationCoverageIsNeutral
                              ? '1px solid rgba(251,191,36,0.30)'
                              : '1px solid rgba(239,68,68,0.30)',
                        bgcolor: 'rgba(15,23,42,0.42)',
                        px: 1.75,
                        py: 1.35,
                        ...TITLE_ONLY_TOOLTIP_CARD_SX,
                      }}
                    >
                      <Typography sx={{ color: '#94a3b8', fontSize: '0.7rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        Cobertura estándar
                      </Typography>
                      <Typography
                        sx={{
                          color: estimationCoveragePct === null
                            ? '#f8fafc'
                            : estimationCoverageIsGood
                              ? '#86efac'
                              : estimationCoverageIsNeutral
                                ? '#fde68a'
                                : '#fca5a5',
                          fontSize: '1.28rem',
                          fontWeight: 950,
                          mt: 0.35,
                        }}
                      >
                        {formatPct(estimationCoveragePct)}
                      </Typography>
                      <Typography sx={{ color: '#64748b', fontSize: '0.74rem' }}>
                        {totals?.orders_without_estimation_count ?? 0} sin estimación · {closedOrders} cerradas
                      </Typography>
                    </Paper>
                  </Tooltip>
                </Box>
              </Stack>
            );
          })()}

          {/* Rendimiento por línea */}
          {factorySummaryQuery.data && !factorySummaryQuery.isFetching ? (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                border: '1px solid rgba(148,163,184,0.16)',
                bgcolor: 'rgba(15,23,42,0.58)',
                p: 2.5,
              }}
            >
              <Stack spacing={2}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  justifyContent="space-between"
                  spacing={1.5}
                >
                  <Stack spacing={0.35}>
                    <Typography sx={{ color: '#f8fafc', fontWeight: 950 }}>
                      Rendimiento por línea
                    </Typography>
                    <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                      Lectura por línea con la misma lógica de KPIs: fabricación, preparación,
                      operativo, paradas y ocupación.
                    </Typography>
                  </Stack>

                  <Chip
                    size="small"
                    label={`${factorySummaryQuery.data.by_line.length} línea${
                      factorySummaryQuery.data.by_line.length === 1 ? '' : 's'
                    } con actividad`}
                    variant="outlined"
                    sx={{
                      alignSelf: { xs: 'flex-start', md: 'center' },
                      color: '#bae6fd',
                      borderColor: 'rgba(56,189,248,0.26)',
                      bgcolor: 'rgba(8,47,73,0.18)',
                      fontWeight: 850,
                    }}
                  />
                </Stack>

                {(factorySummaryQuery.data?.by_line ?? []).length === 0 ? (
                  <Alert severity="info" variant="outlined">
                    No hay líneas con producción cerrada para el periodo seleccionado.
                  </Alert>
                ) : (
                  <Box sx={{ overflowX: 'auto' }}>
                    <Box
                      sx={{
                        minWidth: 1040,
                        borderRadius: 3,
                        border: '1px solid rgba(148,163,184,0.12)',
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '1.25fr 1.55fr 0.9fr 1.1fr 1.2fr 1fr',
                          gap: 1.25,
                          px: 1.5,
                          py: 1,
                          bgcolor: 'rgba(2,6,23,0.52)',
                        }}
                      >
                        {[
                          {
                            label: 'Línea',
                            tooltip:
                              'Línea o centro productivo. El volumen queda como contexto, no como KPI principal.',
                          },
                          {
                            label: 'Producción real / teórica',
                            tooltip:
                              'Compara solo fabricación neta contra fabricación teórica. No incluye preparación ni paradas.',
                          },
                          {
                            label: 'Desviación prod.',
                            tooltip:
                              'Producción real neta - producción teórica. Positivo significa que se tarda más que el estándar.',
                          },
                          {
                            label: 'Preparación media',
                            tooltip:
                              'Preparación real neta media por tramo cerrado. También muestra qué porcentaje representa sobre el tiempo operativo.',
                          },
                          {
                            label: 'Operativo vs parado',
                            tooltip:
                              'Operativo = preparación + fabricación. Se compara contra el tiempo parado de la línea.',
                          },
                          {
                            label: 'Paradas',
                            tooltip:
                              'Tiempo total parado, número de paradas y duración media de parada.',
                          },
                        ].map((item) => (
                          <Tooltip key={item.label} arrow placement="top" title={item.tooltip}>
                            <Typography
                              sx={{
                                color: '#94a3b8',
                                fontSize: '0.7rem',
                                fontWeight: 950,
                                textTransform: 'uppercase',
                                letterSpacing: '0.06em',
                                cursor: 'help',
                              }}
                            >
                              {item.label}
                            </Typography>
                          </Tooltip>
                        ))}
                      </Box>

                      {[...(factorySummaryQuery.data?.by_line ?? [])]
                        .sort((a, b) => {
                          const deviationA = Math.abs(a.production_deviation_minutes ?? 0);
                          const deviationB = Math.abs(b.production_deviation_minutes ?? 0);

                          return (
                            deviationB - deviationA ||
                            b.stop_duration_minutes - a.stop_duration_minutes ||
                            a.line_or_center.localeCompare(b.line_or_center)
                          );
                        })
                        .map((line) => {
                          const closedOrders = line.closed_orders_count ?? 0;
                          const productionReal = line.production_duration_minutes ?? 0;
                          const productionEstimated =
                            line.estimated_production_duration_minutes ?? 0;
                          const productionDeviation = line.production_deviation_minutes ?? 0;
                          const productionDeviationPct =
                            productionEstimated > 0
                              ? (productionDeviation / productionEstimated) * 100
                              : null;
                          const productionDeviationIsOver = productionDeviation > 0;
                          const productionDeviationIsGood = productionDeviation <= 0;

                          const productionProgressPct =
                            productionEstimated > 0
                              ? Math.min(
                                  100,
                                  Math.round((productionReal / productionEstimated) * 100),
                                )
                              : 0;
                          const productionOverrunPct =
                            productionEstimated > 0 && productionReal > productionEstimated
                              ? Math.min(
                                  100,
                                  Math.round(
                                    ((productionReal - productionEstimated) /
                                      productionEstimated) *
                                      100,
                                  ),
                                )
                              : 0;

                          const setupReal = line.setup_duration_minutes ?? 0;
                          const operativeNet = line.real_duration_minutes ?? setupReal + productionReal;
                          const stopped = line.stop_duration_minutes ?? 0;

                          const averageSetupPerOrder =
                            closedOrders > 0 ? setupReal / closedOrders : null;
                          const setupSharePct =
                            operativeNet > 0 ? (setupReal / operativeNet) * 100 : null;

                          const operativeAndStopped = operativeNet + stopped;
                          const operativePct =
                            operativeAndStopped > 0
                              ? Math.round((operativeNet / operativeAndStopped) * 100)
                              : null;
                          const stoppedPct =
                            operativeAndStopped > 0
                              ? Math.round((stopped / operativeAndStopped) * 100)
                              : null;

                          return (
                            <Box
                              key={line.production_line_config_id ?? line.line_or_center}
                              sx={{
                                display: 'grid',
                                gridTemplateColumns:
                                  '1.25fr 1.55fr 0.9fr 1.1fr 1.2fr 1fr',
                                gap: 1.25,
                                px: 1.5,
                                py: 1.35,
                                borderTop: '1px solid rgba(148,163,184,0.10)',
                                bgcolor: productionDeviationIsOver
                                  ? 'rgba(127,29,29,0.11)'
                                  : 'rgba(15,23,42,0.24)',
                                alignItems: 'center',
                                '& > *': {
                                  pointerEvents: 'none',
                                },
                              }}
                            >
                              <Tooltip
                                arrow
                                placement="top"
                                title={
                                  <Stack spacing={0.5}>
                                    <Typography sx={{ fontWeight: 900 }}>
                                      Contexto de línea
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.78rem' }}>
                                      {line.reports_count} partes · {closedOrders} tramos cerrados.
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.78rem' }}>
                                      El volumen no mide rendimiento, solo tamaño de muestra.
                                    </Typography>
                                  </Stack>
                                }
                              >
                                <Stack spacing={0.16} sx={{ cursor: 'help' }}>
                                  <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                                    {line.line_or_center || 'Línea sin nombre'}
                                  </Typography>
                                  <Typography sx={{ color: '#64748b', fontSize: '0.74rem' }}>
                                    {line.reports_count} partes · {closedOrders} tramos cerrados
                                  </Typography>
                                </Stack>
                              </Tooltip>

                              <Tooltip
                                arrow
                                placement="top"
                                title={
                                  <Stack spacing={0.65}>
                                    <Typography sx={{ fontWeight: 900 }}>
                                      Producción real vs teórica
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.78rem' }}>
                                      Solo fabricación neta. No incluye preparación ni paradas.
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.78rem' }}>
                                      Fórmula: producción real neta - producción teórica.
                                    </Typography>
                                  </Stack>
                                }
                              >
                                <Stack spacing={0.45} sx={{ cursor: 'help' }}>
                                  <Stack direction="row" spacing={0.75} alignItems="baseline">
                                    <Typography sx={{ color: '#e0f2fe', fontWeight: 950 }}>
                                      {formatEstimatedMinutes(productionReal)}
                                    </Typography>
                                    <Typography
                                      sx={{
                                        color: '#64748b',
                                        fontSize: '0.76rem',
                                        fontWeight: 800,
                                      }}
                                    >
                                      / {formatEstimatedMinutes(productionEstimated)}
                                    </Typography>
                                  </Stack>

                                  <Box
                                    sx={{
                                      height: 6,
                                      borderRadius: 1,
                                      bgcolor: 'rgba(148,163,184,0.12)',
                                      overflow: 'hidden',
                                      position: 'relative',
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        height: '100%',
                                        width: `${productionProgressPct}%`,
                                        bgcolor: productionDeviationIsOver ? '#ef4444' : '#38bdf8',
                                        borderRadius: 1,
                                      }}
                                    />

                                    {productionOverrunPct > 0 ? (
                                      <Box
                                        sx={{
                                          position: 'absolute',
                                          right: 0,
                                          top: 0,
                                          height: '100%',
                                          width: `${Math.max(8, productionOverrunPct)}%`,
                                          bgcolor: '#f97316',
                                          opacity: 0.95,
                                        }}
                                      />
                                    ) : null}
                                  </Box>

                                  {productionOverrunPct > 0 ? (
                                    <Typography sx={{ color: '#fca5a5', fontSize: '0.68rem', fontWeight: 850 }}>
                                      Exceso sobre estándar: +{formatPct(productionOverrunPct)}
                                    </Typography>
                                  ) : (
                                    <Typography sx={{ color: '#38bdf8', fontSize: '0.68rem', fontWeight: 850 }}>
                                      Dentro del estándar productivo
                                    </Typography>
                                  )}
                                </Stack>
                              </Tooltip>

                              <Tooltip
                                arrow
                                placement="top"
                                title={
                                  <Stack spacing={0.65}>
                                    <Typography sx={{ fontWeight: 900 }}>
                                      Desviación productiva
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.78rem' }}>
                                      Positivo: la línea tarda más que el estándar. Negativo: fabrica más rápido.
                                    </Typography>
                                  </Stack>
                                }
                              >
                                <Stack spacing={0.12} sx={{ cursor: 'help' }}>
                                  <Typography
                                    sx={{
                                      color: productionDeviationIsGood ? '#86efac' : '#fca5a5',
                                      fontWeight: 950,
                                    }}
                                  >
                                    {formatPct(productionDeviationPct)}
                                  </Typography>
                                  <Typography sx={{ color: '#64748b', fontSize: '0.72rem' }}>
                                    {formatSignedMinutes(productionDeviation)}
                                  </Typography>
                                </Stack>
                              </Tooltip>

                              <Tooltip
                                arrow
                                placement="top"
                                title={
                                  <Stack spacing={0.65}>
                                    <Typography sx={{ fontWeight: 900 }}>
                                      Preparación media
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.78rem' }}>
                                      Preparación real neta dividida entre tramos cerrados.
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.78rem' }}>
                                      Fórmula: preparación real / tramos cerrados.
                                    </Typography>
                                  </Stack>
                                }
                              >
                                <Stack spacing={0.12} sx={{ cursor: 'help' }}>
                                  <Typography sx={{ color: '#fbbf24', fontWeight: 950 }}>
                                    {formatEstimatedMinutes(averageSetupPerOrder)}
                                  </Typography>
                                  <Typography sx={{ color: '#64748b', fontSize: '0.72rem' }}>
                                    {formatPct(setupSharePct)} del operativo
                                  </Typography>
                                  <Typography sx={{ color: '#475569', fontSize: '0.68rem' }}>
                                    total {formatEstimatedMinutes(setupReal)}
                                  </Typography>
                                </Stack>
                              </Tooltip>

                              <Tooltip
                                arrow
                                placement="top"
                                title={
                                  <Stack spacing={0.65}>
                                    <Typography sx={{ fontWeight: 900 }}>
                                      Operativo vs parado
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.78rem' }}>
                                      Operativo incluye preparación + fabricación. Parada queda separada.
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.78rem' }}>
                                      Fórmula: operativo / (operativo + parado).
                                    </Typography>
                                  </Stack>
                                }
                              >
                                <Stack spacing={0.45} sx={{ cursor: 'help' }}>
                                  <Stack direction="row" spacing={0.75} alignItems="baseline">
                                    <Typography sx={{ color: '#e2e8f0', fontWeight: 950 }}>
                                      {formatEstimatedMinutes(operativeNet)}
                                    </Typography>
                                    <Typography sx={{ color: '#fca5a5', fontSize: '0.76rem', fontWeight: 850 }}>
                                      / {formatEstimatedMinutes(stopped)}
                                    </Typography>
                                  </Stack>

                                  <Box
                                    sx={{
                                      height: 5,
                                      borderRadius: 1,
                                      bgcolor: 'rgba(148,163,184,0.12)',
                                      overflow: 'hidden',
                                      display: 'flex',
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        height: '100%',
                                        width: `${operativePct ?? 0}%`,
                                        bgcolor: '#22c55e',
                                      }}
                                    />
                                    <Box
                                      sx={{
                                        height: '100%',
                                        width: `${stoppedPct ?? 0}%`,
                                        bgcolor: '#ef4444',
                                      }}
                                    />
                                  </Box>

                                  <Typography sx={{ color: '#64748b', fontSize: '0.72rem' }}>
                                    {operativePct ?? 0}% operativo · {stoppedPct ?? 0}% parado
                                  </Typography>
                                </Stack>
                              </Tooltip>

                              <Tooltip
                                arrow
                                placement="top"
                                title={
                                  <Stack spacing={0.65}>
                                    <Typography sx={{ fontWeight: 900 }}>
                                      Paradas
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.78rem' }}>
                                      Tiempo total parado, número de paradas y duración media.
                                    </Typography>
                                  </Stack>
                                }
                              >
                                <Stack spacing={0.12} sx={{ cursor: 'help' }}>
                                  <Typography
                                    sx={{
                                      color: stopped > 0 ? '#fca5a5' : '#e2e8f0',
                                      fontWeight: 950,
                                    }}
                                  >
                                    {formatEstimatedMinutes(stopped)}
                                  </Typography>
                                  <Typography sx={{ color: '#64748b', fontSize: '0.72rem' }}>
                                    {line.stops_count} paradas
                                  </Typography>
                                  <Typography sx={{ color: '#475569', fontSize: '0.68rem' }}>
                                    media {formatEstimatedMinutes(line.average_stop_duration_minutes)}
                                  </Typography>
                                </Stack>
                              </Tooltip>
                            </Box>
                          );
                        })}
                    </Box>
                  </Box>
                )}
              </Stack>
            </Paper>
          ) : null}

          {factorySummaryQuery.data && !factorySummaryQuery.isFetching && processPerformanceGroups.length > 0 ? (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                border: '1px solid rgba(56,189,248,0.18)',
                bgcolor: 'rgba(15,23,42,0.58)',
                p: 2.5,
              }}
            >
              <Stack spacing={2}>
                <Stack spacing={0.35}>
                  <Typography sx={{ color: '#f8fafc', fontWeight: 950 }}>
                    Rendimiento por proceso
                  </Typography>
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                    Comparativa agregada por proceso con la misma lectura que los KPIs principales.
                    Permite ver qué área concentra desviación productiva, preparación y paradas.
                  </Typography>
                </Stack>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      lg: processPerformanceGroups.length > 1 ? 'repeat(2, minmax(0, 1fr))' : '1fr',
                    },
                    gap: 1.5,
                  }}
                >
                  {processPerformanceGroups.map((processGroup) => {
                    const closedOrders = processGroup.totals.closed_orders_count ?? 0;

                    const productionReal = processGroup.totals.production_duration_minutes ?? 0;
                    const productionEstimated =
                      processGroup.totals.estimated_production_duration_minutes ?? 0;
                    const productionDeviation =
                      processGroup.totals.production_deviation_minutes ?? 0;
                    const productionDeviationPct =
                      productionEstimated > 0
                        ? (productionDeviation / productionEstimated) * 100
                        : null;
                    const productionDeviationIsOver = productionDeviation > 0;

                    const productionProgressPct =
                      productionEstimated > 0
                        ? Math.min(100, Math.round((productionReal / productionEstimated) * 100))
                        : 0;
                    const productionOverrunPct =
                      productionEstimated > 0 && productionReal > productionEstimated
                        ? Math.min(
                            100,
                            Math.round(
                              ((productionReal - productionEstimated) / productionEstimated) * 100,
                            ),
                          )
                        : 0;

                    const setupReal = processGroup.totals.setup_duration_minutes ?? 0;
                    const operativeNet =
                      processGroup.totals.real_duration_minutes ?? setupReal + productionReal;
                    const stopped = processGroup.totals.stop_duration_minutes ?? 0;
                    const operativeAndStopped = operativeNet + stopped;

                    const averageSetupPerOrder =
                      closedOrders > 0 ? setupReal / closedOrders : null;
                    const setupSharePct =
                      operativeNet > 0 ? (setupReal / operativeNet) * 100 : null;

                    const operativePct =
                      operativeAndStopped > 0
                        ? Math.round((operativeNet / operativeAndStopped) * 100)
                        : null;
                    const stoppedPct =
                      operativeAndStopped > 0
                        ? Math.round((stopped / operativeAndStopped) * 100)
                        : null;

                    const maxLineProductionDuration = Math.max(
                      ...processGroup.lines.map((line) => line.production_duration_minutes ?? 0),
                      1,
                    );

                    return (
                      <Paper
                        key={processGroup.key}
                        elevation={0}
                        sx={{
                          borderRadius: 3.5,
                          border: `1px solid ${processGroup.tone.border}`,
                          bgcolor: processGroup.tone.bg,
                          p: 1.85,
                          position: 'relative',
                          overflow: 'hidden',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: 4,
                            bgcolor: processGroup.tone.accent,
                          },
                        }}
                      >
                        <Stack spacing={1.65} sx={{ position: 'relative' }}>
                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            justifyContent="space-between"
                            spacing={1.25}
                          >
                            <Stack spacing={0.35}>
                              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                <Typography
                                  sx={{
                                    color: processGroup.tone.accent,
                                    fontWeight: 950,
                                    fontSize: '0.86rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.08em',
                                  }}
                                >
                                  {processGroup.label}
                                </Typography>

                                <Chip
                                  size="small"
                                  label={`${processGroup.lines.length} ${
                                    processGroup.lines.length === 1 ? 'línea' : 'líneas'
                                  }`}
                                  variant="outlined"
                                  sx={{
                                    height: 22,
                                    color: '#cbd5e1',
                                    borderColor: 'rgba(148,163,184,0.22)',
                                    bgcolor: 'rgba(15,23,42,0.32)',
                                    fontSize: '0.7rem',
                                    fontWeight: 800,
                                  }}
                                />

                                {processGroup.totals.quality_nc_count > 0 ? (
                                  <Chip
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                    label={`NC ${processGroup.totals.quality_nc_count}`}
                                    sx={{ height: 22, fontWeight: 850 }}
                                  />
                                ) : null}
                              </Stack>

                              <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                                {closedOrders} tramos cerrados · {processGroup.totals.reports_count}{' '}
                                partes · {formatEstimatedMinutes(productionReal)} de fabricación neta
                              </Typography>
                            </Stack>

                            <Tooltip
                              arrow
                              placement="top"
                              title="Cobertura de estándar: tramos cerrados que tienen datos suficientes para comparar producción real contra producción teórica."
                            >
                              <Chip
                                size="small"
                                label={`Estándar ${formatPct(
                                  closedOrders > 0
                                    ? ((processGroup.totals.estimated_orders_count ?? 0) / closedOrders) * 100
                                    : null,
                                )}`}
                                variant="outlined"
                                sx={{
                                  alignSelf: { xs: 'flex-start', sm: 'center' },
                                  color:
                                    closedOrders > 0 &&
                                    ((processGroup.totals.estimated_orders_count ?? 0) / closedOrders) >= 0.95
                                      ? '#86efac'
                                      : '#fde68a',
                                  borderColor: 'rgba(148,163,184,0.22)',
                                  bgcolor: 'rgba(15,23,42,0.30)',
                                  fontWeight: 850,
                                  cursor: 'help',
                                }}
                              />
                            </Tooltip>
                          </Stack>

                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: {
                                xs: '1fr',
                                sm: 'repeat(2, minmax(0, 1fr))',
                                xl: 'repeat(4, minmax(0, 1fr))',
                              },
                              gap: 1,
                            }}
                          >
                            <Tooltip
                              arrow
                              placement="top"
                              title={
                                <Stack spacing={0.65}>
                                  <Typography sx={{ fontWeight: 900 }}>
                                    Producción real vs teórica
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.78rem' }}>
                                    Solo fabricación neta. No incluye preparación ni paradas.
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.78rem' }}>
                                    Fórmula: producción real neta - producción teórica.
                                  </Typography>
                                </Stack>
                              }
                            >
                              <Paper
                                elevation={0}
                                sx={{
                                  borderRadius: 2.5,
                                  border: productionDeviationIsOver
                                    ? '1px solid rgba(239,68,68,0.30)'
                                    : '1px solid rgba(56,189,248,0.22)',
                                  bgcolor: productionDeviationIsOver
                                    ? 'rgba(127,29,29,0.16)'
                                    : 'rgba(15,23,42,0.32)',
                                  p: 1.1,
                                  ...TITLE_ONLY_TOOLTIP_CARD_SX,
                                }}
                              >
                                <Typography sx={{ color: '#93c5fd', fontSize: '0.7rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                  Producción real / teórica
                                </Typography>

                                <Stack direction="row" spacing={0.75} alignItems="baseline" sx={{ mt: 0.35 }}>
                                  <Typography sx={{ color: '#f8fafc', fontWeight: 950, fontSize: '1.03rem' }}>
                                    {formatEstimatedMinutes(productionReal)}
                                  </Typography>
                                  <Typography sx={{ color: '#64748b', fontSize: '0.74rem', fontWeight: 800 }}>
                                    / {formatEstimatedMinutes(productionEstimated)}
                                  </Typography>
                                </Stack>

                                <Box
                                  sx={{
                                    height: 6,
                                    borderRadius: 1,
                                    bgcolor: 'rgba(148,163,184,0.12)',
                                    overflow: 'hidden',
                                    position: 'relative',
                                    mt: 0.65,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      height: '100%',
                                      width: `${productionProgressPct}%`,
                                      bgcolor: productionDeviationIsOver ? '#ef4444' : processGroup.tone.accent,
                                      borderRadius: 1,
                                    }}
                                  />

                                  {productionOverrunPct > 0 ? (
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        right: 0,
                                        top: 0,
                                        height: '100%',
                                        width: `${Math.max(8, productionOverrunPct)}%`,
                                        bgcolor: '#f97316',
                                      }}
                                    />
                                  ) : null}
                                </Box>

                                <Typography
                                  sx={{
                                    color: productionDeviationIsOver ? '#fca5a5' : '#86efac',
                                    fontSize: '0.72rem',
                                    fontWeight: 850,
                                    mt: 0.55,
                                  }}
                                >
                                  {formatPct(productionDeviationPct)} · {formatSignedMinutes(productionDeviation)}
                                </Typography>
                              </Paper>
                            </Tooltip>

                            <Tooltip
                              arrow
                              placement="top"
                              title={
                                <Stack spacing={0.65}>
                                  <Typography sx={{ fontWeight: 900 }}>
                                    Preparación media
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.78rem' }}>
                                    Preparación real neta media por tramo cerrado del proceso.
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.78rem' }}>
                                    Fórmula: preparación real / tramos cerrados.
                                  </Typography>
                                </Stack>
                              }
                            >
                              <Paper
                                elevation={0}
                                sx={{
                                  borderRadius: 2.5,
                                  border: '1px solid rgba(251,191,36,0.22)',
                                  bgcolor: 'rgba(15,23,42,0.32)',
                                  p: 1.1,
                                  ...TITLE_ONLY_TOOLTIP_CARD_SX,
                                }}
                              >
                                <Typography sx={{ color: '#fbbf24', fontSize: '0.7rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                  Preparación media
                                </Typography>
                                <Typography sx={{ color: '#f8fafc', fontWeight: 950, fontSize: '1.03rem', mt: 0.35 }}>
                                  {formatEstimatedMinutes(averageSetupPerOrder)}
                                </Typography>
                                <Typography sx={{ color: '#64748b', fontSize: '0.72rem', mt: 0.25 }}>
                                  {formatPct(setupSharePct)} del operativo · total {formatEstimatedMinutes(setupReal)}
                                </Typography>
                              </Paper>
                            </Tooltip>

                            <Tooltip
                              arrow
                              placement="top"
                              title={
                                <Stack spacing={0.65}>
                                  <Typography sx={{ fontWeight: 900 }}>
                                    Operativo vs parado
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.78rem' }}>
                                    Operativo incluye preparación + fabricación. Parada queda separada.
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.78rem' }}>
                                    Fórmula: operativo / (operativo + parado).
                                  </Typography>
                                </Stack>
                              }
                            >
                              <Paper
                                elevation={0}
                                sx={{
                                  borderRadius: 2.5,
                                  border: stopped > 0
                                    ? '1px solid rgba(239,68,68,0.24)'
                                    : '1px solid rgba(34,197,94,0.20)',
                                  bgcolor: 'rgba(15,23,42,0.32)',
                                  p: 1.1,
                                  ...TITLE_ONLY_TOOLTIP_CARD_SX,
                                }}
                              >
                                <Typography sx={{ color: '#34d399', fontSize: '0.7rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                  Operativo vs parado
                                </Typography>

                                <Stack direction="row" spacing={0.75} alignItems="baseline" sx={{ mt: 0.35 }}>
                                  <Typography sx={{ color: '#f8fafc', fontWeight: 950, fontSize: '1.03rem' }}>
                                    {formatEstimatedMinutes(operativeNet)}
                                  </Typography>
                                  <Typography sx={{ color: stopped > 0 ? '#fca5a5' : '#64748b', fontSize: '0.74rem', fontWeight: 800 }}>
                                    / {formatEstimatedMinutes(stopped)}
                                  </Typography>
                                </Stack>

                                <Box
                                  sx={{
                                    height: 6,
                                    borderRadius: 1,
                                    bgcolor: 'rgba(148,163,184,0.12)',
                                    overflow: 'hidden',
                                    display: 'flex',
                                    mt: 0.65,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      height: '100%',
                                      width: `${operativePct ?? 0}%`,
                                      bgcolor: '#22c55e',
                                    }}
                                  />
                                  <Box
                                    sx={{
                                      height: '100%',
                                      width: `${stoppedPct ?? 0}%`,
                                      bgcolor: '#ef4444',
                                    }}
                                  />
                                </Box>

                                <Typography sx={{ color: '#64748b', fontSize: '0.72rem', mt: 0.55 }}>
                                  {operativePct ?? 0}% operativo · {stoppedPct ?? 0}% parado
                                </Typography>
                              </Paper>
                            </Tooltip>

                            <Tooltip
                              arrow
                              placement="top"
                              title={
                                <Stack spacing={0.65}>
                                  <Typography sx={{ fontWeight: 900 }}>
                                    Paradas
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.78rem' }}>
                                    Tiempo parado acumulado, número de paradas y duración media.
                                  </Typography>
                                </Stack>
                              }
                            >
                              <Paper
                                elevation={0}
                                sx={{
                                  borderRadius: 2.5,
                                  border: stopped > 0
                                    ? '1px solid rgba(239,68,68,0.28)'
                                    : '1px solid rgba(148,163,184,0.12)',
                                  bgcolor: stopped > 0 ? 'rgba(127,29,29,0.14)' : 'rgba(15,23,42,0.32)',
                                  p: 1.1,
                                  ...TITLE_ONLY_TOOLTIP_CARD_SX,
                                }}
                              >
                                <Typography sx={{ color: stopped > 0 ? '#fca5a5' : '#94a3b8', fontSize: '0.7rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                  Paradas
                                </Typography>
                                <Typography sx={{ color: stopped > 0 ? '#fecaca' : '#f8fafc', fontWeight: 950, fontSize: '1.03rem', mt: 0.35 }}>
                                  {formatEstimatedMinutes(stopped)}
                                </Typography>
                                <Typography sx={{ color: '#64748b', fontSize: '0.72rem', mt: 0.25 }}>
                                  {processGroup.totals.stops_count} paradas · media{' '}
                                  {formatEstimatedMinutes(processGroup.totals.average_stop_duration_minutes)}
                                </Typography>
                              </Paper>
                            </Tooltip>
                          </Box>

                          <Stack spacing={0.85}>
                            {processGroup.lines.map((line) => {
                              const lineProduction = line.production_duration_minutes ?? 0;
                              const lineProductionPct = Math.round(
                                (lineProduction / maxLineProductionDuration) * 100,
                              );
                              const lineDeviation = line.production_deviation_minutes ?? 0;
                              const lineDeviationPct =
                                (line.estimated_production_duration_minutes ?? 0) > 0
                                  ? (lineDeviation / (line.estimated_production_duration_minutes ?? 1)) * 100
                                  : null;
                              const lineIsOver = lineDeviation > 0;
                              const lineSetupAverage =
                                (line.closed_orders_count ?? 0) > 0
                                  ? (line.setup_duration_minutes ?? 0) / (line.closed_orders_count ?? 1)
                                  : null;

                              return (
                                <Stack
                                  key={line.production_line_config_id ?? line.line_or_center}
                                  spacing={0.35}
                                >
                                  <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    spacing={1}
                                    alignItems="baseline"
                                  >
                                    <Typography sx={{ color: '#e2e8f0', fontSize: '0.8rem', fontWeight: 850 }}>
                                      {line.line_or_center}
                                    </Typography>
                                    <Typography sx={{ color: '#94a3b8', fontSize: '0.74rem', textAlign: 'right' }}>
                                      Prod. {formatEstimatedMinutes(lineProduction)} · Desv.{' '}
                                      <Box
                                        component="span"
                                        sx={{ color: lineIsOver ? '#fca5a5' : '#86efac', fontWeight: 850 }}
                                      >
                                        {formatPct(lineDeviationPct)}
                                      </Box>{' '}
                                      · Prep. media {formatEstimatedMinutes(lineSetupAverage)}
                                    </Typography>
                                  </Stack>

                                  <Box
                                    sx={{
                                      height: 6,
                                      borderRadius: 1,
                                      bgcolor: 'rgba(148,163,184,0.10)',
                                      overflow: 'hidden',
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        height: '100%',
                                        width: `${lineProductionPct}%`,
                                        bgcolor: lineIsOver ? '#ef4444' : processGroup.tone.accent,
                                        borderRadius: 1,
                                      }}
                                    />
                                  </Box>
                                </Stack>
                              );
                            })}
                          </Stack>
                        </Stack>
                      </Paper>
                    );
                  })}
                </Box>
              </Stack>
            </Paper>
          ) : null}

          {factorySummaryQuery.data && !factorySummaryQuery.isFetching && (factorySummaryQuery.data.totals.closed_orders_count ?? 0) > 0 ? (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                border: '1px solid rgba(56,189,248,0.18)',
                bgcolor: 'rgba(15,23,42,0.58)',
                p: 2.5,
              }}
            >
              <Stack spacing={1.75}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  spacing={1.5}
                >
                  <Stack spacing={0.35}>
                    <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                      Análisis del periodo
                    </Typography>
                    <Typography sx={{ color: '#94a3b8', fontSize: '0.88rem' }}>
                      Interpretación inteligente de los datos. Identifica lo que más importa, no lo que ya ves.
                    </Typography>
                  </Stack>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleAnalyzePerformance}
                    disabled={aiAnalysisLoading}
                    sx={{
                      borderColor: 'rgba(56,189,248,0.4)',
                      color: '#38bdf8',
                      whiteSpace: 'nowrap',
                      '&:hover': {
                        borderColor: '#38bdf8',
                        bgcolor: 'rgba(56,189,248,0.06)',
                      },
                    }}
                  >
                    {aiAnalysisLoading ? 'Analizando...' : aiAnalysis ? 'Regenerar análisis' : 'Analizar periodo'}
                  </Button>
                </Stack>

                {aiAnalysisLoading ? (
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <CircularProgress size={16} sx={{ color: '#38bdf8' }} />
                    <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                      Analizando datos de producción...
                    </Typography>
                  </Stack>
                ) : aiAnalysisError ? (
                  <Alert severity="error" variant="outlined">
                    {aiAnalysisError}
                  </Alert>
                ) : aiAnalysis ? (
                  <Typography
                    sx={{
                      color: '#e2e8f0',
                      fontSize: '0.95rem',
                      lineHeight: 1.7,
                      fontStyle: 'normal',
                    }}
                  >
                    {aiAnalysis}
                  </Typography>
                ) : (
                  <Typography sx={{ color: '#475569', fontSize: '0.88rem' }}>
                    Pulsa "Analizar periodo" para obtener una interpretación inteligente de los datos actuales.
                  </Typography>
                )}
              </Stack>
            </Paper>
          ) : null}

          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              border: '1px solid rgba(148,163,184,0.16)',
              bgcolor: 'rgba(15,23,42,0.58)',
              p: 2.5,
            }}
          >
            <Stack spacing={1.75}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={1.25}
              >
                <Stack spacing={0.25}>
                  <Typography sx={{ color: '#f8fafc', fontWeight: 950 }}>
                    Principales motivos de parada
                  </Typography>
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.86rem' }}>
                    Motivos ordenados por tiempo acumulado. Despliega un motivo para ver su peso e intensidad media.
                  </Typography>
                </Stack>

                <Chip
                  size="small"
                  label={`${factorySummaryQuery.data?.top_stop_reasons?.length ?? 0} motivo${
                    (factorySummaryQuery.data?.top_stop_reasons?.length ?? 0) === 1 ? '' : 's'
                  }`}
                  variant="outlined"
                  sx={{
                    color: '#fecaca',
                    borderColor: 'rgba(248,113,113,0.24)',
                    bgcolor: 'rgba(127,29,29,0.12)',
                    fontWeight: 850,
                  }}
                />
              </Stack>

              {(() => {
                const reasons = factorySummaryQuery.data?.top_stop_reasons ?? [];
                const totalStopMinutes = reasons.reduce(
                  (sum, reason) => sum + (reason.stop_duration_minutes ?? 0),
                  0,
                );
                const maxMinutes = Math.max(
                  ...reasons.map((reason) => reason.stop_duration_minutes ?? 0),
                  1,
                );

                return (
                  <Stack spacing={0.9}>
                    {reasons.map((reason) => {
                      const duration = reason.stop_duration_minutes ?? 0;
                      const count = reason.stops_count ?? 0;
                      const relativePct = Math.round((duration / maxMinutes) * 100);
                      const sharePct =
                        totalStopMinutes > 0 ? (duration / totalStopMinutes) * 100 : null;
                      const averageDuration = count > 0 ? duration / count : null;
                      const isExpanded = expandedStopReasonLabel === reason.reason_label;

                      return (
                        <Paper
                          key={reason.reason_label}
                          elevation={0}
                          sx={{
                            borderRadius: 2.75,
                            border: isExpanded
                              ? '1px solid rgba(248,113,113,0.34)'
                              : '1px solid rgba(148,163,184,0.12)',
                            bgcolor: isExpanded
                              ? 'rgba(127,29,29,0.16)'
                              : 'rgba(15,23,42,0.30)',
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            role="button"
                            tabIndex={0}
                            onClick={() =>
                              setExpandedStopReasonLabel((current) =>
                                current === reason.reason_label ? null : reason.reason_label,
                              )
                            }
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                setExpandedStopReasonLabel((current) =>
                                  current === reason.reason_label ? null : reason.reason_label,
                                );
                              }
                            }}
                            sx={{
                              px: 1.5,
                              py: 1.15,
                              cursor: 'pointer',
                              outline: 'none',
                              '&:hover': {
                                bgcolor: 'rgba(248,113,113,0.06)',
                              },
                            }}
                          >
                            <Stack spacing={0.65}>
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                spacing={1.5}
                              >
                                <Stack spacing={0.15} sx={{ minWidth: 0 }}>
                                  <Typography
                                    sx={{
                                      color: '#f8fafc',
                                      fontSize: '0.9rem',
                                      fontWeight: 900,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {reason.reason_label}
                                  </Typography>
                                  <Typography sx={{ color: '#64748b', fontSize: '0.72rem' }}>
                                    {isExpanded ? 'Ocultar detalle' : 'Ver detalle agregado'}
                                  </Typography>
                                </Stack>

                                <Stack
                                  direction="row"
                                  spacing={1.5}
                                  alignItems="center"
                                  sx={{ flexShrink: 0 }}
                                >
                                  <Typography sx={{ color: '#94a3b8', fontSize: '0.78rem' }}>
                                    {count} parada{count !== 1 ? 's' : ''}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      color: '#fecaca',
                                      fontSize: '0.9rem',
                                      fontWeight: 950,
                                      minWidth: 78,
                                      textAlign: 'right',
                                    }}
                                  >
                                    {formatEstimatedMinutes(duration)}
                                  </Typography>
                                </Stack>
                              </Stack>

                              <Box
                                sx={{
                                  height: 6,
                                  borderRadius: 1,
                                  bgcolor: 'rgba(148,163,184,0.10)',
                                  overflow: 'hidden',
                                }}
                              >
                                <Box
                                  sx={{
                                    height: '100%',
                                    width: `${relativePct}%`,
                                    bgcolor: '#f87171',
                                    borderRadius: 1,
                                    transition: 'width 0.4s ease',
                                  }}
                                />
                              </Box>
                            </Stack>
                          </Box>

                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box
                              sx={{
                                borderTop: '1px solid rgba(148,163,184,0.10)',
                                px: 1.5,
                                py: 1.2,
                                bgcolor: 'rgba(2,6,23,0.22)',
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: {
                                    xs: '1fr',
                                    sm: 'repeat(3, minmax(0, 1fr))',
                                  },
                                  gap: 1,
                                }}
                              >
                                <Paper
                                  elevation={0}
                                  sx={{
                                    borderRadius: 2,
                                    border: '1px solid rgba(148,163,184,0.12)',
                                    bgcolor: 'rgba(15,23,42,0.34)',
                                    p: 1,
                                  }}
                                >
                                  <Typography sx={{ color: '#64748b', fontSize: '0.68rem', fontWeight: 900 }}>
                                    PESO SOBRE PARADAS
                                  </Typography>
                                  <Typography sx={{ color: '#f8fafc', fontWeight: 950, mt: 0.25 }}>
                                    {formatPct(sharePct)}
                                  </Typography>
                                  <Typography sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                                    del tiempo parado total
                                  </Typography>
                                </Paper>

                                <Paper
                                  elevation={0}
                                  sx={{
                                    borderRadius: 2,
                                    border: '1px solid rgba(148,163,184,0.12)',
                                    bgcolor: 'rgba(15,23,42,0.34)',
                                    p: 1,
                                  }}
                                >
                                  <Typography sx={{ color: '#64748b', fontSize: '0.68rem', fontWeight: 900 }}>
                                    DURACIÓN MEDIA
                                  </Typography>
                                  <Typography sx={{ color: '#f8fafc', fontWeight: 950, mt: 0.25 }}>
                                    {formatEstimatedMinutes(averageDuration)}
                                  </Typography>
                                  <Typography sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                                    por parada registrada
                                  </Typography>
                                </Paper>

                                <Paper
                                  elevation={0}
                                  sx={{
                                    borderRadius: 2,
                                    border: '1px solid rgba(148,163,184,0.12)',
                                    bgcolor: 'rgba(15,23,42,0.34)',
                                    p: 1,
                                  }}
                                >
                                  <Typography sx={{ color: '#64748b', fontSize: '0.68rem', fontWeight: 900 }}>
                                    LECTURA
                                  </Typography>
                                  <Typography sx={{ color: '#f8fafc', fontWeight: 900, mt: 0.25 }}>
                                    {count >= 5 && averageDuration !== null && averageDuration <= 15
                                      ? 'Recurrente corta'
                                      : averageDuration !== null && averageDuration >= 60
                                        ? 'Impacto largo'
                                        : 'Impacto moderado'}
                                  </Typography>
                                  <Typography sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                                    clasificación orientativa
                                  </Typography>
                                </Paper>
                              </Box>

                              <Typography sx={{ color: '#64748b', fontSize: '0.72rem', mt: 1 }}>
                                Detalle agregado del motivo. Para listar cada parada individual haría falta ampliar el summary con fecha, línea, inicio, fin y observaciones.
                              </Typography>
                            </Box>
                          </Collapse>
                        </Paper>
                      );
                    })}
                  </Stack>
                );
              })()}
            </Stack>
          </Paper>

          <Alert severity="info" variant="outlined">
            El rendimiento se calcula sobre órdenes cerradas entre {performanceDateFrom} y{' '}
            {performanceDateTo}. La comparativa usa el periodo anterior equivalente:{' '}
            {previousPerformanceRange.dateFrom} — {previousPerformanceRange.dateTo}.
          </Alert>
            </>
          ) : performanceView === 'shifts' ? (
            <Stack spacing={1.5}>
              <PerformanceShiftCapacityTable rows={shiftPerformanceRows} />

              <PerformanceBreakdownTable
                title="Comparativa por turno"
                description="Compara simultáneamente los turnos con actividad del periodo seleccionado. El contexto temporal es común para todas las filas."
                rows={shiftPerformanceRows}
                emptyMessage="No hay turnos con actividad cerrada en el periodo seleccionado."
              />
            </Stack>
          ) : performanceView === 'operators' ? (
            <Stack spacing={1.5}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(148,163,184,0.16)',
                  bgcolor: 'rgba(15,23,42,0.48)',
                  px: 2,
                  py: 1.5,
                }}
              >
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1.25}
                  justifyContent="space-between"
                  alignItems={{ xs: 'stretch', md: 'center' }}
                >
                  <Stack spacing={0.25}>
                    <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                      Rendimiento por operario
                    </Typography>
                    <Typography sx={{ color: '#94a3b8', fontSize: '0.84rem' }}>
                      Consulta agregados con su tamaño de muestra. La tabla sirve
                      para investigar contexto operativo, no para clasificar
                      automáticamente a las personas.
                    </Typography>
                  </Stack>

                  <TextField
                    size="small"
                    label="Buscar operario"
                    value={performanceOperatorSearchText}
                    onChange={(event) =>
                      setPerformanceOperatorSearchText(event.target.value)
                    }
                    sx={{ minWidth: 190 }}
                  />
                </Stack>
              </Paper>

              <PerformanceBreakdownTable
                title="Comparativa por operario"
                description="Orden inicial por tiempo ocupado analizado. Cada fila conserva partes, tramos cerrados y cobertura de estándar."
                rows={operatorPerformanceRows}
                emptyMessage="No hay operarios con actividad cerrada que coincidan con la búsqueda."
              />
            </Stack>
          ) : (
            <Stack spacing={1.5}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(148,163,184,0.16)',
                  bgcolor: 'rgba(15,23,42,0.48)',
                  px: 2,
                  py: 1.5,
                }}
              >
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1.25}
                  justifyContent="space-between"
                  alignItems={{ xs: 'stretch', md: 'center' }}
                >
                  <Stack spacing={0.25}>
                    <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                      Rendimiento por producto
                    </Typography>

                    <Typography sx={{ color: '#94a3b8', fontSize: '0.84rem' }}>
                      Consulta referencias fabricadas y analiza si las
                      desviaciones se concentran en productos o combinaciones
                      concretas de producto y línea.
                    </Typography>
                  </Stack>

                  <TextField
                    size="small"
                    label="Buscar producto o referencia"
                    value={performanceProductSearchText}
                    onChange={(event) =>
                      setPerformanceProductSearchText(
                        event.target.value,
                      )
                    }
                    sx={{ minWidth: 250 }}
                  />
                </Stack>
              </Paper>

              {performanceProductsQuery.isLoading ? (
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid rgba(148,163,184,0.16)',
                    bgcolor: 'rgba(15,23,42,0.48)',
                    px: 2,
                    py: 2,
                  }}
                >
                  <Typography sx={{ color: '#94a3b8' }}>
                    Cargando rendimiento por producto...
                  </Typography>
                </Paper>
              ) : performanceProductsQuery.isError ? (
                <Alert severity="error" variant="outlined">
                  No se pudo cargar el rendimiento por producto.
                </Alert>
              ) : (
                <ProductPerformanceTable
                  rows={productPerformanceTableData.rows}
                  expandedProductKey={expandedPerformanceProductKey}
                  sortField={performanceProductSort}
                  sortDirection={performanceProductSortDirection}
                  quantitySortEnabled={
                    productPerformanceTableData.quantitySortEnabled
                  }
                  onToggleProduct={(productKey) =>
                    setExpandedPerformanceProductKey((current) =>
                      current === productKey ? null : productKey,
                    )
                  }
                  onSortChange={(field) => {
                    if (field === performanceProductSort) {
                      setPerformanceProductSortDirection((current) =>
                        current === 'desc' ? 'asc' : 'desc',
                      );
                      return;
                    }

                    setPerformanceProductSort(field);
                    setPerformanceProductSortDirection(
                      field === 'reference' ? 'asc' : 'desc',
                    );
                  }}
                />
              )}
            </Stack>
          )}
          </Stack>
        </LocalizationProvider>
      ) : visibleControlTab === 'export' ? (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
          <Stack spacing={2}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid rgba(148,163,184,0.16)',
                bgcolor: 'rgba(15,23,42,0.48)',
                p: { xs: 2, md: 3 },
              }}
            >
              <Stack spacing={2.5}>
                <Stack spacing={0.5}>
                  <Typography
                    sx={{
                      color: '#f8fafc',
                      fontWeight: 900,
                      fontSize: '1.05rem',
                    }}
                  >
                    Exportar datos industriales
                  </Typography>

                  <Typography
                    sx={{
                      color: '#94a3b8',
                      fontSize: '0.88rem',
                    }}
                  >
                    Selecciona un rango y utiliza los mismos filtros para
                    descargar órdenes de fabricación o paradas de fábrica.
                  </Typography>
                </Stack>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      md: 'repeat(4, minmax(0, 1fr))',
                    },
                    gap: 1.5,
                  }}
                >
                  <DatePicker
                    label="Fecha desde"
                    value={exportDateFrom}
                    onChange={(value) => {
                      setExportDateFrom(value);

                      if (
                        exportDateTo &&
                        value &&
                        value > exportDateTo
                      ) {
                        setExportDateTo(null);
                      }
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        placeholder: 'dd/mm/aaaa',
                        sx: {
                          '& .MuiInputBase-input': {
                            color: '#f8fafc',
                          },
                          '& .MuiInputLabel-root': {
                            color: '#94a3b8',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(148,163,184,0.3)',
                          },
                          '& .MuiSvgIcon-root': {
                            color: '#94a3b8',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(148,163,184,0.6)',
                          },
                        },
                      },
                    }}
                  />

                  <DatePicker
                    label="Fecha hasta"
                    value={exportDateTo}
                    onChange={(value) => setExportDateTo(value)}
                    minDate={exportDateFrom ?? undefined}
                    disabled={!exportDateFrom}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        placeholder: 'dd/mm/aaaa',
                        helperText: !exportDateFrom
                          ? 'Selecciona primero la fecha de inicio'
                          : undefined,
                        sx: {
                          '& .MuiInputBase-input': {
                            color: '#f8fafc',
                          },
                          '& .MuiInputLabel-root': {
                            color: '#94a3b8',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(148,163,184,0.3)',
                          },
                          '& .MuiSvgIcon-root': {
                            color: '#94a3b8',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(148,163,184,0.6)',
                          },
                          '& .MuiFormHelperText-root': {
                            color: '#64748b',
                          },
                        },
                      },
                    }}
                  />

                  <TextField
                    select
                    label="Turno"
                    fullWidth
                    value={exportShift}
                    onChange={(event) =>
                      setExportShift(event.target.value)
                    }
                    sx={{
                      '& .MuiInputBase-input': {
                        color: '#f8fafc',
                      },
                      '& .MuiInputLabel-root': {
                        color: '#94a3b8',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(148,163,184,0.3)',
                      },
                      '& .MuiSvgIcon-root': {
                        color: '#94a3b8',
                      },
                    }}
                  >
                    <MenuItem value="all">Todos los turnos</MenuItem>
                    <MenuItem value="morning">Mañana</MenuItem>
                    <MenuItem value="afternoon">Tarde</MenuItem>
                    <MenuItem value="night">Noche</MenuItem>
                  </TextField>

                  <TextField
                    select
                    label="Línea / Puesto"
                    fullWidth
                    value={exportLineId}
                    onChange={(event) =>
                      setExportLineId(event.target.value)
                    }
                    sx={{
                      '& .MuiInputBase-input': {
                        color: '#f8fafc',
                      },
                      '& .MuiInputLabel-root': {
                        color: '#94a3b8',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(148,163,184,0.3)',
                      },
                      '& .MuiSvgIcon-root': {
                        color: '#94a3b8',
                      },
                    }}
                  >
                    <MenuItem value="all">Todas las líneas</MenuItem>

                    {configuredLines.map((line) => (
                      <MenuItem key={line.id} value={line.id}>
                        {line.code} · {line.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>

                {exportDateFrom && exportDateTo ? (
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 2,
                      border: '1px solid rgba(56,189,248,0.2)',
                      bgcolor: 'rgba(56,189,248,0.06)',
                      px: 2,
                      py: 1.25,
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#7dd3fc',
                        fontSize: '0.85rem',
                      }}
                    >
                      Filtros activos · del{' '}
                      <strong>
                        {format(exportDateFrom, 'dd/MM/yyyy')}
                      </strong>{' '}
                      al{' '}
                      <strong>
                        {format(exportDateTo, 'dd/MM/yyyy')}
                      </strong>

                      {exportShift !== 'all' && (
                        <>
                          {' '}
                          · Turno{' '}
                          <strong>
                            {exportShift === 'morning'
                              ? 'Mañana'
                              : exportShift === 'afternoon'
                                ? 'Tarde'
                                : 'Noche'}
                          </strong>
                        </>
                      )}

                      {exportLineId !== 'all' && (
                        <>
                          {' '}
                          ·{' '}
                          <strong>
                            {configuredLines.find(
                              (line) => line.id === exportLineId,
                            )?.name ?? exportLineId}
                          </strong>
                        </>
                      )}
                    </Typography>
                  </Paper>
                ) : (
                  <Alert severity="info" variant="outlined">
                    Selecciona el rango de fechas para habilitar las descargas.
                  </Alert>
                )}
              </Stack>
            </Paper>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'repeat(2, minmax(0, 1fr))',
                },
                gap: 1.5,
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(56,189,248,0.22)',
                  bgcolor: 'rgba(8,47,73,0.16)',
                  p: 2,
                }}
              >
                <Stack spacing={1.25} sx={{ height: '100%' }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                  >
                    <FactoryRoundedIcon sx={{ color: '#38bdf8' }} />

                    <Typography
                      sx={{
                        color: '#f8fafc',
                        fontWeight: 900,
                      }}
                    >
                      Órdenes de fabricación
                    </Typography>
                  </Stack>

                  <Typography
                    sx={{
                      color: '#94a3b8',
                      fontSize: '0.86rem',
                    }}
                  >
                    Listado de tramos fabricados con cantidades, preparación,
                    fabricación real, paradas internas y campos configurados.
                  </Typography>

                  <Button
                    variant="contained"
                    disabled={
                      !exportDateFrom ||
                      !exportDateTo ||
                      Boolean(exportingKind)
                    }
                    onClick={() =>
                      handleProductionReportExport('lines')
                    }
                    sx={{
                      mt: 'auto',
                      alignSelf: 'flex-start',
                      textTransform: 'none',
                      fontWeight: 700,
                    }}
                  >
                    {exportingKind === 'lines'
                      ? 'Generando órdenes...'
                      : 'Descargar órdenes'}
                  </Button>
                </Stack>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(248,113,113,0.22)',
                  bgcolor: 'rgba(127,29,29,0.12)',
                  p: 2,
                }}
              >
                <Stack spacing={1.25} sx={{ height: '100%' }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                  >
                    <ReportProblemRoundedIcon
                      sx={{ color: '#f87171' }}
                    />

                    <Typography
                      sx={{
                        color: '#f8fafc',
                        fontWeight: 900,
                      }}
                    >
                      Paradas de fábrica
                    </Typography>
                  </Stack>

                  <Typography
                    sx={{
                      color: '#94a3b8',
                      fontSize: '0.86rem',
                    }}
                  >
                    Listado de interrupciones del rango seleccionado con motivo,
                    ámbito, OF asociada, horas, duración y observaciones.
                  </Typography>

                  <Button
                    variant="contained"
                    color="error"
                    disabled={
                      !exportDateFrom ||
                      !exportDateTo ||
                      Boolean(exportingKind)
                    }
                    onClick={() =>
                      handleProductionReportExport('stops')
                    }
                    sx={{
                      mt: 'auto',
                      alignSelf: 'flex-start',
                      textTransform: 'none',
                      fontWeight: 700,
                    }}
                  >
                    {exportingKind === 'stops'
                      ? 'Generando paradas...'
                      : 'Descargar paradas'}
                  </Button>
                </Stack>
              </Paper>
            </Box>
          </Stack>
        </LocalizationProvider>
      ) : null}


      {canViewCurrentTab ? (
        <PlantNoticesDialog
          open={plantNoticesDialogOpen}
          activeNotices={activePlantNoticesQuery.data ?? []}
          activeLoading={activePlantNoticesQuery.isLoading}
          activeError={activePlantNoticesQuery.isError}
          configuredLines={configuredLines}
          onRefreshActive={() => activePlantNoticesQuery.refetch()}
          onClose={() => setPlantNoticesDialogOpen(false)}
        />
      ) : null}

      <ReviewBoardDialog
        item={selectedReviewItem}
        detail={selectedReviewDetailQuery.data}
        productById={productById}
        operationSettings={operationSettingsQuery.data}
        loading={selectedReviewDetailQuery.isFetching || productsQuery.isFetching}
        validationPending={validateReviewReportMutation.isPending}
        timelineValidationIssue={timelineValidationIssue}
        onValidate={() => validateReviewReportMutation.mutate()}
        onDetailRefresh={() => selectedReviewDetailQuery.refetch()}
        onReportDeleted={() => {
          reviewBoardQuery.refetch();
          reviewBoardSummaryQuery.refetch();
        }}
        onClose={() => {
          setSelectedReviewItem(null);
          setTimelineValidationIssue(null);
        }}
      />

      <LineDetailDialog
        group={selectedGroup}
        details={selectedLineDetailsQuery.data ?? []}
        productById={productById}
        loading={selectedLineDetailsQuery.isFetching || productsQuery.isFetching}
        onClose={() => setSelectedGroup(null)}
      />
    </Stack>
  );
}
